import { z } from 'zod';

// src/common/types/shared.types.ts
var RecurrenceInterval = /* @__PURE__ */ ((RecurrenceInterval2) => {
  RecurrenceInterval2["DAILY"] = "DAILY";
  RecurrenceInterval2["WEEKLY"] = "WEEKLY";
  RecurrenceInterval2["MONTHLY"] = "MONTHLY";
  RecurrenceInterval2["QUARTERLY"] = "QUARTERLY";
  RecurrenceInterval2["YEARLY"] = "YEARLY";
  return RecurrenceInterval2;
})(RecurrenceInterval || {});
var DayOfWeek = /* @__PURE__ */ ((DayOfWeek2) => {
  DayOfWeek2["SUNDAY"] = "SUNDAY";
  DayOfWeek2["MONDAY"] = "MONDAY";
  DayOfWeek2["TUESDAY"] = "TUESDAY";
  DayOfWeek2["WEDNESDAY"] = "WEDNESDAY";
  DayOfWeek2["THURSDAY"] = "THURSDAY";
  DayOfWeek2["FRIDAY"] = "FRIDAY";
  DayOfWeek2["SATURDAY"] = "SATURDAY";
  return DayOfWeek2;
})(DayOfWeek || {});
var WeekIndex = /* @__PURE__ */ ((WeekIndex2) => {
  WeekIndex2["FIRST"] = "FIRST";
  WeekIndex2["SECOND"] = "SECOND";
  WeekIndex2["THIRD"] = "THIRD";
  WeekIndex2["FOURTH"] = "FOURTH";
  WeekIndex2["LAST"] = "LAST";
  return WeekIndex2;
})(WeekIndex || {});
var RECURRENCE_INTERVAL_DISPLAY = {
  ["DAILY" /* DAILY */]: { label: "Daily" },
  ["WEEKLY" /* WEEKLY */]: { label: "Weekly" },
  ["MONTHLY" /* MONTHLY */]: { label: "Monthly" },
  ["QUARTERLY" /* QUARTERLY */]: { label: "Quarterly" },
  ["YEARLY" /* YEARLY */]: { label: "Yearly" }
};
var DAY_OF_WEEK_DISPLAY = {
  ["SUNDAY" /* SUNDAY */]: { label: "Sunday", short: "Sun" },
  ["MONDAY" /* MONDAY */]: { label: "Monday", short: "Mon" },
  ["TUESDAY" /* TUESDAY */]: { label: "Tuesday", short: "Tue" },
  ["WEDNESDAY" /* WEDNESDAY */]: { label: "Wednesday", short: "Wed" },
  ["THURSDAY" /* THURSDAY */]: { label: "Thursday", short: "Thu" },
  ["FRIDAY" /* FRIDAY */]: { label: "Friday", short: "Fri" },
  ["SATURDAY" /* SATURDAY */]: { label: "Saturday", short: "Sat" }
};
var WEEK_INDEX_DISPLAY = {
  ["FIRST" /* FIRST */]: { label: "First" },
  ["SECOND" /* SECOND */]: { label: "Second" },
  ["THIRD" /* THIRD */]: { label: "Third" },
  ["FOURTH" /* FOURTH */]: { label: "Fourth" },
  ["LAST" /* LAST */]: { label: "Last" }
};
var dateToISOString = z.preprocess(
  (val) => val instanceof Date ? val.toISOString() : val,
  z.string()
);
var nullableDateToISOString = z.preprocess(
  (val) => val instanceof Date ? val.toISOString() : val,
  z.string().nullable()
);
var optionalDateToISOString = z.preprocess(
  (val) => val instanceof Date ? val.toISOString() : val,
  z.string().optional()
);
var bigintToString = z.preprocess((val) => {
  if (typeof val === "bigint" || typeof val === "number") {
    return val.toString();
  }
  return val;
}, z.string());
var nullableBigintToString = z.preprocess((val) => {
  if (typeof val === "bigint" || typeof val === "number") {
    return val.toString();
  }
  return val;
}, z.string().nullable());
var optionalBigintToString = z.preprocess((val) => {
  if (typeof val === "bigint" || typeof val === "number") {
    return val.toString();
  }
  return val;
}, z.string().optional());
var numberCoerce = z.preprocess((val) => {
  if (val === null || val === void 0) return val;
  if (typeof val === "number") return val;
  if (val && typeof val === "object" && "toNumber" in val) {
    return val.toNumber();
  }
  if (typeof val === "string") {
    const num = Number(val);
    return isNaN(num) ? val : num;
  }
  return val;
}, z.number());
function coerceToNumber(val, invalidValue) {
  if (val === null || val === void 0 || val === "") return invalidValue;
  if (typeof val === "number") return val;
  if (val && typeof val === "object" && "toNumber" in val) {
    return val.toNumber();
  }
  if (typeof val === "string") {
    const num = Number(val);
    return isNaN(num) ? invalidValue : num;
  }
  return invalidValue;
}
var optionalNumberCoerce = z.preprocess(
  (val) => coerceToNumber(val, void 0),
  z.number().optional()
);
var nullableNumberCoerce = z.preprocess(
  (val) => coerceToNumber(val, null),
  z.number().nullable()
);
var STRIPE_HOSTNAME_PATTERN = /^([a-z0-9-]+\.)?stripe\.com$/i;
function isStripeHostedUrl(url) {
  try {
    const { protocol, hostname } = new URL(url);
    return protocol === "https:" && STRIPE_HOSTNAME_PATTERN.test(hostname);
  } catch {
    return false;
  }
}
var StripeUrlSchema = z.url().refine(isStripeHostedUrl, "Must be an HTTPS URL hosted on stripe.com");
function isStripeUrl(url) {
  return StripeUrlSchema.safeParse(url).success;
}

// src/turnstile/index.ts
var TURNSTILE_HEADER = "X-Turnstile-Token";
var TURNSTILE_VALIDATION_KEY = "turnstileToken";
var TURNSTILE_MAX_TOKEN_LENGTH = 2048;
var TurnstileAction = /* @__PURE__ */ ((TurnstileAction2) => {
  TurnstileAction2["GuestCheckout"] = "guest_checkout";
  TurnstileAction2["GuestDonation"] = "guest_donation";
  TurnstileAction2["GuestEventCheckout"] = "guest_event_checkout";
  TurnstileAction2["GuestEventRegistration"] = "guest_event_registration";
  TurnstileAction2["GuestEventCheckIn"] = "guest_event_check_in";
  TurnstileAction2["EmailSignup"] = "email_signup";
  return TurnstileAction2;
})(TurnstileAction || {});
var passwordSchema = z.string().min(8, "Password must be at least 8 characters long").regex(/[a-z]/, "Password must contain at least one lowercase letter").regex(/[A-Z]/, "Password must contain at least one uppercase letter").regex(/[0-9]/, "Password must contain at least one number");
var ChangePasswordDtoSchema = z.object({
  oldPassword: z.string().min(1, "Current password is required"),
  newPassword: passwordSchema
});
var ChangePasswordResponseDtoSchema = z.object({
  message: z.string()
});
var ChangeEmailDtoSchema = z.object({
  newEmail: z.string().min(1, "Email is required").email("Please enter a valid email address")
});
var ChangeEmailResponseDtoSchema = z.object({
  message: z.string(),
  codeDeliveryMedium: z.string()
});
var ConfirmEmailChangeDtoSchema = z.object({
  code: z.string().length(6, "Verification code must be 6 digits").regex(/^\d{6}$/, "Verification code must contain only numbers")
});
var ConfirmEmailChangeResponseDtoSchema = z.object({
  message: z.string(),
  email: z.string().email()
});
var MFASetupResponseDtoSchema = z.object({
  secretCode: z.string(),
  qrCodeUri: z.string()
});
var MFAVerifyDtoSchema = z.object({
  code: z.string().length(6, "Verification code must be 6 digits").regex(/^\d{6}$/, "Verification code must contain only numbers")
});
var MFAVerifyResponseDtoSchema = z.object({
  message: z.string()
});
var MfaPreferredMethodSchema = z.enum(["totp", "sms"]);
var MFAStatusResponseDtoSchema = z.object({
  totpEnabled: z.boolean(),
  smsEnabled: z.boolean(),
  phoneNumber: z.string().nullable(),
  phoneVerified: z.boolean(),
  preferredMethod: MfaPreferredMethodSchema.nullable()
});
var MFADisableResponseDtoSchema = z.object({
  message: z.string()
});
var UpdatePhoneNumberDtoSchema = z.object({
  phoneNumber: z.string().regex(
    /^\+[1-9]\d{1,14}$/,
    "Phone number must be in E.164 format (e.g., +12125551234)"
  ),
  smsConsent: z.boolean().refine((value) => value === true, {
    message: "You must consent to receive SMS messages"
  }),
  termsConsent: z.boolean().refine((value) => value === true, {
    message: "You must accept the Terms of Service and Privacy Policy"
  })
});
var UpdatePhoneNumberResponseDtoSchema = z.object({
  message: z.string()
});
var VerifyPhoneNumberDtoSchema = z.object({
  code: z.string().length(6, "Verification code must be 6 digits").regex(/^\d{6}$/, "Verification code must contain only numbers")
});
var VerifyPhoneNumberResponseDtoSchema = z.object({
  message: z.string()
});
var EnableSMSMFADtoSchema = z.object({
  preferred: z.boolean().optional().default(false)
});
var EnableSMSMFAResponseDtoSchema = z.object({
  message: z.string()
});
var SetPreferredMfaMethodDtoSchema = z.object({
  method: MfaPreferredMethodSchema
});
var SetPreferredMfaMethodResponseDtoSchema = z.object({
  message: z.string()
});
var GenerateRecoveryCodesResponseDtoSchema = z.object({
  codes: z.array(z.string())
});
var VerifyRecoveryCodeDtoSchema = z.object({
  email: z.string().email("Invalid email address"),
  code: z.string().length(8, "Recovery code must be 8 characters").regex(/^[A-Z0-9]{8}$/i, "Recovery code must be 8 alphanumeric characters")
});
var VerifyRecoveryCodeResponseDtoSchema = z.object({
  message: z.string()
});
var RemainingRecoveryCodesResponseDtoSchema = z.object({
  remaining: z.number().int().min(0),
  total: z.number().int().min(0)
});
var StepUpMethodSchema = z.enum(["totp", "sms", "email"]);
var StepUpInitiateResponseDtoSchema = z.object({
  method: StepUpMethodSchema
}).strict();
var StepUpVerifyDtoSchema = z.object({
  code: z.string().length(6, "Verification code must be 6 digits").regex(/^\d{6}$/, "Verification code must contain only numbers"),
  method: StepUpMethodSchema
}).strict();
var StepUpVerifyResponseDtoSchema = z.object({
  message: z.string()
}).strict();
var StepUpStatusResponseDtoSchema = z.object({
  verified: z.boolean(),
  expiresAt: z.string().datetime().optional()
}).strict();
var WebAuthnCredentialDtoSchema = z.object({
  credentialId: z.string(),
  friendlyCredentialName: z.string(),
  relyingPartyId: z.string(),
  createdAt: z.coerce.date(),
  authenticatorTransports: z.array(z.string()).optional()
});
var ListPasskeysResponseDtoSchema = z.object({
  credentials: z.array(WebAuthnCredentialDtoSchema),
  nextToken: z.string().optional()
});
var ConfirmReactivationDtoSchema = z.object({
  code: z.string().length(6, "Verification code must be 6 digits").regex(/^\d{6}$/, "Verification code must contain only numbers")
});
var RequestReactivationCodeResponseDtoSchema = z.object({
  message: z.string(),
  codeDeliveryMedium: z.string()
});
var ConfirmReactivationResponseDtoSchema = z.object({
  message: z.string()
});
var DeleteAccountResponseDtoSchema = z.object({
  message: z.string()
});
var AccountSecurityEventTypeSchema = z.enum([
  "LOGIN",
  "PASSWORD_CHANGED",
  "EMAIL_CHANGE_INITIATED",
  "EMAIL_CHANGED",
  "MFA_TOTP_ENABLED",
  "MFA_TOTP_DISABLED",
  "MFA_SMS_ENABLED",
  "MFA_SMS_DISABLED",
  "MFA_PREFERRED_METHOD_CHANGED",
  "RECOVERY_CODE_USED",
  "RECOVERY_CODES_GENERATED",
  "PHONE_UPDATED",
  "PHONE_VERIFIED",
  "ACCOUNT_DEACTIVATED",
  "ACCOUNT_REACTIVATED",
  "ACCOUNT_DELETION_SCHEDULED",
  "SESSIONS_REVOKED",
  "STEP_UP_VERIFIED",
  "SOCIAL_PROVIDER_LINKED",
  "SOCIAL_PROVIDER_UNLINKED"
]);
var AccountSecurityLogDtoSchema = z.object({
  uuid: z.string(),
  eventType: AccountSecurityEventTypeSchema,
  ipAddress: z.string().nullable(),
  browser: z.string().nullable(),
  os: z.string().nullable(),
  deviceType: z.string().nullable(),
  metadata: z.record(z.string(), z.any()).nullable(),
  createdAt: dateToISOString
});
var RecordLoginEventResponseDtoSchema = z.object({
  message: z.string()
});
var RevokeAllSessionsResponseDtoSchema = z.object({
  message: z.string()
});
var passwordSchema2 = z.string().min(8, "Password must be at least 8 characters long").regex(/[a-z]/, "Password must contain at least one lowercase letter").regex(/[A-Z]/, "Password must contain at least one uppercase letter").regex(/[0-9]/, "Password must contain at least one number");
var SetPasswordDtoSchema = z.object({
  newPassword: passwordSchema2
});
var SetPasswordResponseDtoSchema = z.object({
  message: z.string()
});
var SocialProviderSchema = z.enum(["Google"]);
var SocialProviderDtoSchema = z.object({
  provider: SocialProviderSchema,
  linked: z.boolean(),
  primary: z.boolean().optional(),
  email: z.string().email().optional(),
  linkedAt: z.string().datetime().optional()
});
var LinkedProvidersResponseDtoSchema = z.object({
  providers: z.array(SocialProviderDtoSchema),
  hasPassword: z.boolean()
});
var UnlinkSocialProviderDtoSchema = z.object({
  provider: SocialProviderSchema
});
var InitiateLinkResponseDtoSchema = z.object({
  authorizationUrl: z.string().url()
});
var PaginationMetaSchema = z.object({
  total: numberCoerce,
  page: numberCoerce,
  limit: numberCoerce,
  totalPages: numberCoerce,
  hasNext: z.boolean(),
  hasPrev: z.boolean()
});
var ApiErrorSchema = z.object({
  code: z.string(),
  statusCode: z.number(),
  message: z.string(),
  details: z.any().optional()
});
var ResponseMetaSchema = z.object({
  error: ApiErrorSchema.nullable().optional(),
  pagination: PaginationMetaSchema.optional(),
  timestamp: z.string().optional(),
  requestId: z.string().optional(),
  version: z.string().optional()
});
function createResponseSchema(dataSchema) {
  return z.object({
    data: dataSchema,
    meta: ResponseMetaSchema.optional()
  });
}
function createArrayResponseSchema(elementSchema) {
  const arraySchema = z.array(elementSchema);
  return z.object({
    data: arraySchema,
    meta: ResponseMetaSchema.optional()
  });
}

// src/permissions/permission.dto.ts
var Resource = /* @__PURE__ */ ((Resource2) => {
  Resource2["ALL"] = "ALL";
  Resource2["ADMINISTRATOR"] = "ADMINISTRATOR";
  Resource2["ANNOUNCEMENT"] = "ANNOUNCEMENT";
  Resource2["AUDIT_LOG"] = "AUDIT_LOG";
  Resource2["COMMUNICATION_TEMPLATES"] = "COMMUNICATION_TEMPLATES";
  Resource2["CONFIGURATION"] = "CONFIGURATION";
  Resource2["CONTENT"] = "CONTENT";
  Resource2["DEAD_LETTER_JOBS"] = "DEAD_LETTER_JOBS";
  Resource2["DONATION"] = "DONATION";
  Resource2["EVENT"] = "EVENT";
  Resource2["FEATURE_FLAGS"] = "FEATURE_FLAGS";
  Resource2["GOVERNANCE"] = "GOVERNANCE";
  Resource2["GROUP"] = "GROUP";
  Resource2["INVITATION"] = "INVITATION";
  Resource2["INVOICE"] = "INVOICE";
  Resource2["MEMBERSHIP"] = "MEMBERSHIP";
  Resource2["MEMBERSHIP_TIER"] = "MEMBERSHIP_TIER";
  Resource2["ORDER"] = "ORDER";
  Resource2["ORGANIZATION"] = "ORGANIZATION";
  Resource2["PAYMENT"] = "PAYMENT";
  Resource2["ROLE"] = "ROLE";
  Resource2["STORAGE"] = "STORAGE";
  Resource2["USER"] = "USER";
  Resource2["WAITLIST"] = "WAITLIST";
  return Resource2;
})(Resource || {});
var Action = /* @__PURE__ */ ((Action2) => {
  Action2["READ"] = "READ";
  Action2["CREATE"] = "CREATE";
  Action2["UPDATE"] = "UPDATE";
  Action2["DELETE"] = "DELETE";
  Action2["MANAGE"] = "MANAGE";
  Action2["SEND"] = "SEND";
  return Action2;
})(Action || {});
var PermissionDtoSchema = z.object({
  resource: z.enum(Resource),
  action: z.enum(Action),
  createdAt: dateToISOString
});
var UserPermissionDtoSchema = z.object({
  uuid: z.string(),
  resource: z.enum(Resource),
  action: z.enum(Action),
  granted: z.boolean(),
  role: z.enum(["MIMBER", "MIMBER_ORGANIZATION"]).optional()
});
var AggregatedPermissionDtoSchema = z.object({
  resource: z.enum(Resource),
  action: z.enum(Action)
});
var GrantUserPermissionDtoSchema = z.object({
  resource: z.enum(Resource),
  action: z.enum(Action)
});
var UserPermissionResponseDtoSchema = createResponseSchema(
  UserPermissionDtoSchema
);
var UserPermissionArrayResponseDtoSchema = createResponseSchema(
  UserPermissionDtoSchema.array()
);
var RESOURCE_LABELS = {
  ["ALL" /* ALL */]: "All",
  ["ADMINISTRATOR" /* ADMINISTRATOR */]: "Administrator",
  ["ANNOUNCEMENT" /* ANNOUNCEMENT */]: "Announcements",
  ["AUDIT_LOG" /* AUDIT_LOG */]: "Audit Log",
  ["COMMUNICATION_TEMPLATES" /* COMMUNICATION_TEMPLATES */]: "Communication Templates",
  ["CONFIGURATION" /* CONFIGURATION */]: "Configuration",
  ["CONTENT" /* CONTENT */]: "Content",
  ["DEAD_LETTER_JOBS" /* DEAD_LETTER_JOBS */]: "Dead Letter Jobs",
  ["DONATION" /* DONATION */]: "Donations",
  ["EVENT" /* EVENT */]: "Events",
  ["FEATURE_FLAGS" /* FEATURE_FLAGS */]: "Feature Flags",
  ["GOVERNANCE" /* GOVERNANCE */]: "Governance",
  ["GROUP" /* GROUP */]: "Groups",
  ["INVITATION" /* INVITATION */]: "Invitations",
  ["INVOICE" /* INVOICE */]: "Invoices",
  ["MEMBERSHIP" /* MEMBERSHIP */]: "Members",
  ["MEMBERSHIP_TIER" /* MEMBERSHIP_TIER */]: "Membership Tiers",
  ["ORDER" /* ORDER */]: "Orders",
  ["ORGANIZATION" /* ORGANIZATION */]: "Organization",
  ["PAYMENT" /* PAYMENT */]: "Payments",
  ["ROLE" /* ROLE */]: "Roles",
  ["STORAGE" /* STORAGE */]: "Storage",
  ["USER" /* USER */]: "User",
  ["WAITLIST" /* WAITLIST */]: "Waitlist"
};
var ADMIN_ONLY_RESOURCES = [
  "FEATURE_FLAGS" /* FEATURE_FLAGS */,
  "WAITLIST" /* WAITLIST */,
  "CONFIGURATION" /* CONFIGURATION */,
  "DEAD_LETTER_JOBS" /* DEAD_LETTER_JOBS */
];
var SYSTEM_RESOURCES = [
  "ALL" /* ALL */,
  "ADMINISTRATOR" /* ADMINISTRATOR */,
  "USER" /* USER */,
  "AUDIT_LOG" /* AUDIT_LOG */
];
var ASSIGNABLE_RESOURCES = [
  "ANNOUNCEMENT" /* ANNOUNCEMENT */,
  "COMMUNICATION_TEMPLATES" /* COMMUNICATION_TEMPLATES */,
  "CONTENT" /* CONTENT */,
  "DONATION" /* DONATION */,
  "EVENT" /* EVENT */,
  "GOVERNANCE" /* GOVERNANCE */,
  "GROUP" /* GROUP */,
  "INVITATION" /* INVITATION */,
  "INVOICE" /* INVOICE */,
  "MEMBERSHIP" /* MEMBERSHIP */,
  "MEMBERSHIP_TIER" /* MEMBERSHIP_TIER */,
  "ORDER" /* ORDER */,
  "ORGANIZATION" /* ORGANIZATION */,
  "PAYMENT" /* PAYMENT */,
  "ROLE" /* ROLE */,
  "STORAGE" /* STORAGE */
];
var STANDARD_ACTIONS = [
  "READ" /* READ */,
  "CREATE" /* CREATE */,
  "UPDATE" /* UPDATE */,
  "DELETE" /* DELETE */,
  "MANAGE" /* MANAGE */
];
var SEND_RESOURCES = [
  "ANNOUNCEMENT" /* ANNOUNCEMENT */,
  "COMMUNICATION_TEMPLATES" /* COMMUNICATION_TEMPLATES */
];

// src/administrators/administrator.dto.ts
var AdministratorDtoSchema = z.object({
  uuid: z.string(),
  createdAt: dateToISOString,
  updatedAt: dateToISOString,
  identityId: z.string(),
  email: z.string(),
  lastLoginDate: nullableDateToISOString,
  avatar: z.any().optional(),
  // Storage item - keep flexible for now
  permissions: z.array(PermissionDtoSchema).optional()
});
var AdministratorProfileDtoSchema = z.object({
  uuid: z.string(),
  identityId: z.string(),
  email: z.string(),
  avatarUrl: z.string().nullable(),
  createdAt: dateToISOString,
  updatedAt: dateToISOString,
  permissions: z.array(PermissionDtoSchema)
});
var CreateAdministratorDtoSchema = z.object({
  email: z.string().min(1, "Email is required").email("Please enter a valid email address").max(255, "Email must be less than or equal to 255 characters"),
  identityId: z.string().min(1, "Identity ID is required").uuid("Identity ID must be a valid UUID")
});
var LoginAdministratorDtoSchema = z.object({});
var RegisterAdministratorDtoSchema = z.object({
  email: z.string().min(1, "Email is required").email("Please enter a valid email address").max(255, "Email must be less than or equal to 255 characters")
});
var UpdateAdministratorDtoSchema = z.object({
  email: z.string().min(1, "Email is required").email("Please enter a valid email address").max(255, "Email must be less than or equal to 255 characters").optional(),
  lastLoginDate: z.string().datetime().or(z.date()).optional(),
  permissions: z.array(PermissionDtoSchema.omit({ createdAt: true })).optional().refine(
    (permissions) => {
      if (!permissions) return true;
      const permissionKeys = permissions.map(
        (p) => `${p.resource}:${p.action}`
      );
      return permissionKeys.length === new Set(permissionKeys).size;
    },
    {
      message: "Duplicate permissions are not allowed"
    }
  )
});
var UploadAvatarDtoSchema = z.object({
  description: z.string().optional()
});
var UserObjectDtoSchema = z.object({
  uuid: z.string(),
  createdAt: dateToISOString,
  updatedAt: dateToISOString,
  identityId: z.string().nullable(),
  email: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  phoneNumber: z.string().nullable(),
  address: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  zipCode: z.string().nullable(),
  country: z.string().nullable(),
  timezone: z.string().nullable(),
  dateOfBirth: nullableDateToISOString,
  deactivated: z.boolean(),
  deactivatedAt: nullableDateToISOString,
  deleted: nullableDateToISOString,
  deletedScheduledAt: nullableDateToISOString,
  waitlistId: z.unknown().optional(),
  waitlist: z.boolean().optional()
});
var UserDtoSchema = UserObjectDtoSchema.transform(
  ({ waitlistId, waitlist, ...user }) => ({
    ...user,
    displayName: user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email,
    waitlist: waitlist ?? waitlistId != null
  })
);
var UserProfileDtoSchema = z.object({
  uuid: z.string(),
  email: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  phoneNumber: z.string().nullable(),
  address: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  zipCode: z.string().nullable(),
  country: z.string().nullable(),
  timezone: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  dateOfBirth: nullableDateToISOString,
  lastLoginDate: nullableDateToISOString,
  createdAt: dateToISOString,
  updatedAt: dateToISOString,
  permissions: z.array(UserPermissionDtoSchema)
});
var CreateUserDtoSchema = z.object({
  identityId: z.string().min(1).max(255, "Identity ID must be less than 255 characters").nullable().optional(),
  email: z.string().min(1, "Email is required").email("Please enter a valid email address").max(255, "Email must be less than or equal to 255 characters"),
  firstName: z.string().max(100, "First name must be less than 100 characters").optional(),
  lastName: z.string().max(100, "Last name must be less than 100 characters").optional(),
  phoneNumber: z.string().max(20, "Phone number must be less than 20 characters").optional(),
  address: z.string().max(255, "Address must be less than 255 characters").optional(),
  city: z.string().max(100, "City must be less than 100 characters").optional(),
  state: z.string().max(100, "State must be less than 100 characters").optional(),
  zipCode: z.string().max(20, "Zip code must be less than 20 characters").optional(),
  country: z.string().max(100, "Country must be less than 100 characters").optional(),
  timezone: z.string().max(100, "Timezone must be less than 100 characters").optional(),
  dateOfBirth: z.string().datetime("Please enter a valid date").or(z.date()).optional(),
  inviteCode: z.string().uuid("Must be a valid invite code").optional()
});
var RegisterUserDtoSchema = z.object({
  email: z.string().min(1, "Email is required").email("Please enter a valid email address").max(255, "Email must be less than or equal to 255 characters"),
  firstName: z.string().min(1, "First name is required").max(100, "First name must be less than or equal to 100 characters"),
  lastName: z.string().min(1, "Last name is required").max(100, "Last name must be less than or equal to 100 characters"),
  inviteCode: z.string().uuid("Must be a valid invite code").optional()
});
var SubmitInviteCodeDtoSchema = z.object({
  inviteCode: z.string().uuid("Must be a valid invite code")
});
var UpdateUserDtoSchema = z.object({
  identityId: z.string().min(1, "Identity ID is required").max(255, "Identity ID must be less than 255 characters").optional(),
  email: z.string().min(1, "Email is required").email("Please enter a valid email address").max(255, "Email must be less than or equal to 255 characters").optional(),
  firstName: z.string().max(100, "First name must be less than 100 characters").optional(),
  lastName: z.string().max(100, "Last name must be less than 100 characters").optional(),
  phoneNumber: z.string().max(20, "Phone number must be less than 20 characters").optional(),
  address: z.string().max(255, "Address must be less than 255 characters").optional(),
  city: z.string().max(100, "City must be less than 100 characters").optional(),
  state: z.string().max(100, "State must be less than 100 characters").optional(),
  zipCode: z.string().max(20, "Zip code must be less than 20 characters").optional(),
  country: z.string().max(100, "Country must be less than 100 characters").optional(),
  timezone: z.string().max(100, "Timezone must be less than 100 characters").optional(),
  dateOfBirth: z.string().datetime("Please enter a valid date").or(z.date()).optional()
});
var isValidTimezone = (value) => {
  try {
    Intl.DateTimeFormat(void 0, { timeZone: value });
    return true;
  } catch {
    return false;
  }
};
var UpdateProfileDtoSchema = z.object({
  firstName: z.string().max(100, "First name must be less than 100 characters").nullable().optional(),
  lastName: z.string().max(100, "Last name must be less than 100 characters").nullable().optional(),
  phoneNumber: z.string().max(20, "Phone number must be less than 20 characters").nullable().optional(),
  address: z.string().max(255, "Address must be less than 255 characters").nullable().optional(),
  city: z.string().max(100, "City must be less than 100 characters").nullable().optional(),
  state: z.string().max(100, "State must be less than 100 characters").nullable().optional(),
  zipCode: z.string().max(20, "Zip code must be less than 20 characters").nullable().optional(),
  country: z.string().max(100, "Country must be less than 100 characters").nullable().optional(),
  timezone: z.string().max(100, "Timezone must be less than 100 characters").refine(isValidTimezone, "Please select a valid timezone").nullable().optional(),
  dateOfBirth: z.union([z.string().datetime("Please enter a valid date"), z.date()]).nullable().optional()
});
var CreateOrganizationDtoSchema = z.object({
  name: z.string().min(1, "Name is required").max(255, "Name must be less than 255 characters"),
  description: z.string().max(1e3, "Description must be less than 1000 characters").optional(),
  website: z.string().url("Please enter a valid URL").max(255, "Website must be less than 255 characters").optional().or(z.literal("")),
  address: z.string().max(255, "Address must be less than 255 characters").optional(),
  city: z.string().max(100, "City must be less than 100 characters").optional(),
  state: z.string().max(100, "State must be less than 100 characters").optional(),
  country: z.string().max(100, "Country must be less than 100 characters").optional(),
  phoneNumber: z.string().max(20, "Phone number must be less than 20 characters").optional(),
  email: z.string().email("Please enter a valid email address").max(255, "Email must be less than or equal to 255 characters").optional().or(z.literal("")),
  ownerId: z.string().min(1, "Owner ID is required").max(255, "Owner ID must be less than 255 characters")
});
var UpdateOrganizationDtoSchema = z.object({
  name: z.string().min(1, "Name is required").max(255, "Name must be less than 255 characters").optional(),
  description: z.string().max(1e3, "Description must be less than 1000 characters").optional(),
  website: z.string().url("Please enter a valid URL").max(255, "Website must be less than 255 characters").optional().or(z.literal("")),
  address: z.string().max(255, "Address must be less than 255 characters").optional(),
  city: z.string().max(100, "City must be less than 100 characters").optional(),
  state: z.string().max(100, "State must be less than 100 characters").optional(),
  country: z.string().max(100, "Country must be less than 100 characters").optional(),
  phoneNumber: z.string().max(20, "Phone number must be less than 20 characters").optional(),
  email: z.string().email("Please enter a valid email address").max(255, "Email must be less than or equal to 255 characters").optional().or(z.literal(""))
});
var FileTypeEnum = z.enum(["FILE", "FOLDER"]);
var FileStatusEnum = z.enum(["ACTIVE", "DELETED", "ARCHIVED"]);
var StorageClassEnum = z.enum(["STANDARD", "STANDARD_IA", "GLACIER"]);
var StorageItemDtoSchema = z.object({
  id: bigintToString,
  uuid: z.string(),
  name: z.string(),
  type: FileTypeEnum,
  status: FileStatusEnum,
  // Storage information (for files)
  storageKey: z.string().nullable(),
  contentType: z.string().nullable(),
  size: nullableBigintToString,
  etag: z.string().nullable(),
  versionId: z.string().nullable(),
  storageClass: StorageClassEnum,
  // File metadata
  originalName: z.string().nullable(),
  description: z.string().nullable(),
  tags: z.array(z.string()),
  metadata: z.any(),
  // Hierarchy
  parentId: nullableBigintToString,
  path: z.string(),
  fullPath: z.string(),
  depth: z.number(),
  // Ownership
  ownerId: nullableBigintToString,
  administratorId: nullableBigintToString,
  organizationId: nullableBigintToString,
  // Access control
  isPublic: z.boolean(),
  // Timestamps
  createdAt: dateToISOString,
  updatedAt: dateToISOString,
  deletedAt: nullableDateToISOString,
  uploadedAt: nullableDateToISOString,
  lastAccessedAt: nullableDateToISOString,
  // Computed field (added by backend before serialization)
  storageUrl: z.string().nullable().optional()
});

// src/organizations/organization.dto.ts
var StripeAccountStatus = /* @__PURE__ */ ((StripeAccountStatus2) => {
  StripeAccountStatus2["PENDING"] = "PENDING";
  StripeAccountStatus2["ACTIVE"] = "ACTIVE";
  StripeAccountStatus2["RESTRICTED"] = "RESTRICTED";
  StripeAccountStatus2["REJECTED"] = "REJECTED";
  return StripeAccountStatus2;
})(StripeAccountStatus || {});
var ProfileStatus = /* @__PURE__ */ ((ProfileStatus2) => {
  ProfileStatus2["DRAFT"] = "DRAFT";
  ProfileStatus2["PUBLISHED"] = "PUBLISHED";
  return ProfileStatus2;
})(ProfileStatus || {});
var OrganizationDtoSchema = z.object({
  uuid: z.string(),
  createdAt: dateToISOString,
  updatedAt: dateToISOString,
  name: z.string(),
  legalName: z.string().nullable(),
  description: z.string().nullable(),
  website: z.string().nullable(),
  address: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  zipCode: z.string().nullable(),
  country: z.string().nullable(),
  phoneNumber: z.string().nullable(),
  email: z.string().nullable(),
  // Billing fields
  billingAddress: z.string().nullable(),
  billingCity: z.string().nullable(),
  billingState: z.string().nullable(),
  billingZipCode: z.string().nullable(),
  billingCountry: z.string().nullable(),
  billingContactName: z.string().nullable(),
  billingContactEmail: z.string().nullable(),
  // Profile fields
  slug: z.string().nullable(),
  aboutText: z.string().nullable(),
  industry: z.string().nullable(),
  foundedDate: nullableDateToISOString,
  taxId: z.string().nullable(),
  logoStorageItem: StorageItemDtoSchema.nullable().optional(),
  primaryColor: z.string().nullable(),
  secondaryColor: z.string().nullable(),
  accentColor: z.string().nullable(),
  socialMediaFacebook: z.string().nullable(),
  socialMediaTwitter: z.string().nullable(),
  socialMediaLinkedin: z.string().nullable(),
  socialMediaInstagram: z.string().nullable(),
  isPublic: z.boolean(),
  publicDescription: z.string().nullable(),
  publicContactEmail: z.string().nullable(),
  profileStatus: z.nativeEnum(ProfileStatus),
  profilePreviewToken: z.string().nullable(),
  deleted: nullableDateToISOString,
  deletedScheduledAt: nullableDateToISOString,
  // Stripe Connect fields
  stripeAccountId: z.string().nullable(),
  stripeCustomerId: z.string().nullable(),
  stripeAccountActive: z.boolean().nullable(),
  stripeAccountStatus: z.nativeEnum(StripeAccountStatus).nullable(),
  stripeOnboardingComplete: z.boolean(),
  stripeDetailsSubmitted: z.boolean(),
  stripeChargesEnabled: z.boolean(),
  stripePayoutsEnabled: z.boolean(),
  mimberFeePercentage: numberCoerce,
  // Decimal converted to number
  stripeFeePassthrough: z.boolean()
});
var MembershipTierDtoSchema = z.object({
  uuid: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  color: z.string().nullable(),
  level: z.number(),
  isDefault: z.boolean(),
  isActive: z.boolean(),
  price: nullableNumberCoerce.refine(
    (value) => value == null || Number.isInteger(value),
    "Price must be an integer number of cents"
  ).refine(
    (value) => value == null || value >= 0,
    "Price must be non-negative"
  ),
  intervalLength: z.number().nullable(),
  intervalType: z.enum(RecurrenceInterval).nullable(),
  metadata: z.any().nullable(),
  createdAt: dateToISOString,
  updatedAt: dateToISOString
});

// src/memberships/membership-status.enum.ts
var MembershipStatus = /* @__PURE__ */ ((MembershipStatus2) => {
  MembershipStatus2["INVITED"] = "INVITED";
  MembershipStatus2["ACTIVE"] = "ACTIVE";
  MembershipStatus2["INACTIVE"] = "INACTIVE";
  MembershipStatus2["PENDING"] = "PENDING";
  MembershipStatus2["SUSPENDED"] = "SUSPENDED";
  MembershipStatus2["LAPSED"] = "LAPSED";
  return MembershipStatus2;
})(MembershipStatus || {});
var MembershipSource = /* @__PURE__ */ ((MembershipSource2) => {
  MembershipSource2["INVITATION"] = "INVITATION";
  MembershipSource2["MANAGED"] = "MANAGED";
  MembershipSource2["SELF_SERVICE"] = "SELF_SERVICE";
  MembershipSource2["PUBLIC_EVENT"] = "PUBLIC_EVENT";
  return MembershipSource2;
})(MembershipSource || {});
var MembershipSourceSchema = z.nativeEnum(MembershipSource);
var MEMBERSHIP_SOURCE_DISPLAY = {
  ["INVITATION" /* INVITATION */]: { label: "Invited", color: "default" },
  ["MANAGED" /* MANAGED */]: { label: "Managed", color: "blue" },
  ["SELF_SERVICE" /* SELF_SERVICE */]: { label: "Self-Service", color: "green" },
  ["PUBLIC_EVENT" /* PUBLIC_EVENT */]: {
    label: "Public Event Guest",
    color: "orange"
  }
};

// src/memberships/membership.dto.ts
var MembershipStatusSchema = z.nativeEnum(MembershipStatus);
var BaseMembershipDtoSchema = z.object({
  uuid: z.string(),
  userId: bigintToString,
  organizationId: bigintToString,
  membershipTierId: nullableBigintToString,
  status: MembershipStatusSchema,
  source: MembershipSourceSchema,
  isGuest: z.boolean().default(false),
  isManaged: z.boolean().default(false),
  tierExpiresAt: nullableDateToISOString,
  tierAssignedAt: nullableDateToISOString,
  tierExpiredAt: nullableDateToISOString,
  isTierPermanent: z.boolean(),
  suspendedReason: z.string().nullable(),
  suspendedAt: nullableDateToISOString,
  lastDuesPaidAt: nullableDateToISOString,
  lastDuesPaidAmount: z.number().nullable(),
  joinedAt: dateToISOString,
  leftAt: nullableDateToISOString,
  createdAt: dateToISOString,
  updatedAt: dateToISOString
});
var MembershipDtoSchema = BaseMembershipDtoSchema.extend({
  organization: OrganizationDtoSchema,
  membershipTier: MembershipTierDtoSchema.nullable()
});

// src/organizations/current-organization.dto.ts
var CurrentOrganizationDtoSchema = z.object({
  organization: OrganizationDtoSchema,
  membership: MembershipDtoSchema,
  permissions: z.array(AggregatedPermissionDtoSchema)
});
var hexColorSchema = z.string().regex(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/, "Invalid hex color format").nullable();
var urlSchema = z.string().url("Invalid URL format").max(255, "URL must not exceed 255 characters").nullable();
var slugSchema = z.string().min(3, "Slug must be at least 3 characters").max(255, "Slug must not exceed 255 characters").regex(
  /^[a-z0-9]+(-[a-z0-9]+)*$/,
  "Slug can only contain lowercase letters, numbers, and hyphens"
);
var UpdateOrganizationProfileDtoSchema = z.object({
  // Basic information
  name: z.string().min(1, "Name is required").max(255).optional(),
  slug: slugSchema.optional(),
  legalName: z.string().max(255).nullable().optional(),
  description: z.string().max(1e3).nullable().optional(),
  aboutText: z.string().nullable().optional(),
  industry: z.string().max(255).nullable().optional(),
  foundedDate: z.coerce.date().nullable().optional(),
  taxId: z.string().max(50).nullable().optional(),
  // Contact information
  email: z.string().email("Invalid email format").max(255).nullable().optional(),
  phoneNumber: z.string().max(50).nullable().optional(),
  website: urlSchema.optional(),
  address: z.string().max(255).nullable().optional(),
  city: z.string().max(255).nullable().optional(),
  state: z.string().max(255).nullable().optional(),
  zipCode: z.string().max(20).nullable().optional(),
  country: z.string().max(255).nullable().optional(),
  // Billing information
  billingAddress: z.string().max(255).nullable().optional(),
  billingCity: z.string().max(255).nullable().optional(),
  billingState: z.string().max(255).nullable().optional(),
  billingZipCode: z.string().max(20).nullable().optional(),
  billingCountry: z.string().max(255).nullable().optional(),
  billingContactName: z.string().max(255).nullable().optional(),
  billingContactEmail: z.string().email("Invalid email format").max(255).nullable().optional(),
  // Brand colors (hex format validation)
  primaryColor: hexColorSchema.optional(),
  secondaryColor: hexColorSchema.optional(),
  accentColor: hexColorSchema.optional(),
  // Social media links
  socialMediaFacebook: urlSchema.optional(),
  socialMediaTwitter: urlSchema.optional(),
  socialMediaLinkedin: urlSchema.optional(),
  socialMediaInstagram: urlSchema.optional(),
  // Public profile settings
  isPublic: z.boolean().optional(),
  publicDescription: z.string().max(1e3).nullable().optional(),
  publicContactEmail: z.string().email("Invalid email format").max(255).nullable().optional()
});
var PublicStorageItemDtoSchema = z.object({
  uuid: z.string(),
  name: z.string(),
  contentType: z.string().nullable(),
  size: nullableBigintToString,
  isPublic: z.boolean(),
  storageUrl: z.string().nullable(),
  createdAt: dateToISOString,
  updatedAt: dateToISOString,
  uploadedAt: nullableDateToISOString
});

// src/organizations/organization-profile-settings.dto.ts
var OrganizationProfileSettingsDtoSchema = z.object({
  uuid: z.string(),
  name: z.string(),
  legalName: z.string().nullable(),
  slug: z.string().nullable(),
  description: z.string().nullable(),
  aboutText: z.string().nullable(),
  industry: z.string().nullable(),
  foundedDate: nullableDateToISOString,
  taxId: z.string().nullable(),
  phoneNumber: z.string().nullable(),
  address: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  zipCode: z.string().nullable(),
  country: z.string().nullable(),
  billingAddress: z.string().nullable(),
  billingCity: z.string().nullable(),
  billingState: z.string().nullable(),
  billingZipCode: z.string().nullable(),
  billingCountry: z.string().nullable(),
  billingContactName: z.string().nullable(),
  billingContactEmail: z.string().nullable(),
  logoStorageItem: PublicStorageItemDtoSchema.nullable().optional(),
  primaryColor: z.string().nullable(),
  secondaryColor: z.string().nullable(),
  accentColor: z.string().nullable(),
  socialMediaFacebook: z.string().nullable(),
  socialMediaTwitter: z.string().nullable(),
  socialMediaLinkedin: z.string().nullable(),
  socialMediaInstagram: z.string().nullable(),
  isPublic: z.boolean(),
  publicDescription: z.string().nullable(),
  publicContactEmail: z.string().nullable(),
  profileStatus: z.nativeEnum(ProfileStatus)
});
var PublicOrganizationDtoSchema = z.object({
  uuid: z.string(),
  name: z.string(),
  slug: z.string(),
  // Profile information
  aboutText: z.string().nullable(),
  industry: z.string().nullable(),
  foundedDate: nullableDateToISOString,
  // Branding
  logoStorageItem: PublicStorageItemDtoSchema.nullable().optional(),
  primaryColor: z.string().nullable(),
  secondaryColor: z.string().nullable(),
  accentColor: z.string().nullable(),
  // Public contact information
  website: z.string().nullable(),
  publicContactEmail: z.string().nullable(),
  publicDescription: z.string().nullable(),
  // Social media links
  socialMediaFacebook: z.string().nullable(),
  socialMediaTwitter: z.string().nullable(),
  socialMediaLinkedin: z.string().nullable(),
  socialMediaInstagram: z.string().nullable()
});
var PreviewTokenResponseSchema = z.object({
  previewToken: z.string().uuid("Preview token must be a valid UUID"),
  expiresAt: z.string().nullable()
  // ISO date string, null for no expiration in MVP
});
var GetPublicOrganizationProfileParamsSchema = z.object({
  slug: z.string().min(1, "Organization slug is required")
});
var GetPublicOrganizationProfileQuerySchema = z.object({
  previewToken: z.string().uuid().optional()
});
var OrganizationOwnershipDtoSchema = z.object({
  ownerId: bigintToString,
  ownerName: z.string().nullable(),
  ownerEmail: z.string(),
  createdById: bigintToString,
  createdByName: z.string().nullable(),
  createdAt: dateToISOString
});
var OrganizationOwnershipResponseDtoSchema = createResponseSchema(
  OrganizationOwnershipDtoSchema
);
var OrganizationApprovalActionType = /* @__PURE__ */ ((OrganizationApprovalActionType2) => {
  OrganizationApprovalActionType2["TRANSFER_OWNERSHIP"] = "TRANSFER_OWNERSHIP";
  OrganizationApprovalActionType2["DELETE_ORGANIZATION"] = "DELETE_ORGANIZATION";
  return OrganizationApprovalActionType2;
})(OrganizationApprovalActionType || {});
var OrganizationApprovalStatus = /* @__PURE__ */ ((OrganizationApprovalStatus2) => {
  OrganizationApprovalStatus2["PENDING"] = "PENDING";
  OrganizationApprovalStatus2["APPROVED"] = "APPROVED";
  OrganizationApprovalStatus2["REJECTED"] = "REJECTED";
  OrganizationApprovalStatus2["EXPIRED"] = "EXPIRED";
  OrganizationApprovalStatus2["CANCELED"] = "CANCELED";
  return OrganizationApprovalStatus2;
})(OrganizationApprovalStatus || {});
var OrganizationApprovalRequestDtoSchema = z.object({
  uuid: z.string(),
  actionType: z.enum(OrganizationApprovalActionType),
  status: z.enum(OrganizationApprovalStatus),
  expiresAt: dateToISOString,
  approvedAt: nullableDateToISOString,
  createdAt: dateToISOString
});
var OrganizationApprovalRequestResponseDtoSchema = createResponseSchema(OrganizationApprovalRequestDtoSchema.nullable());
var OrganizationApprovalRequestSummaryDtoSchema = z.object({
  uuid: z.string(),
  actionType: z.enum(OrganizationApprovalActionType),
  status: z.enum(OrganizationApprovalStatus),
  organizationName: z.string(),
  requestedByName: z.string(),
  expiresAt: dateToISOString,
  createdAt: dateToISOString
});
var OrganizationApprovalRequestSummaryResponseDtoSchema = createResponseSchema(OrganizationApprovalRequestSummaryDtoSchema);
var ConfirmOrganizationApprovalDtoSchema = z.object({
  token: z.string().min(1)
});
var EligibleOwnerDtoSchema = z.object({
  userId: z.uuid(),
  name: z.string(),
  email: z.string()
});
var EligibleOwnerArrayResponseDtoSchema = createArrayResponseSchema(
  EligibleOwnerDtoSchema
);
var InitiateTransferOwnershipDtoSchema = z.object({
  newOwnerId: z.uuid()
});
var FilterOperator = /* @__PURE__ */ ((FilterOperator2) => {
  FilterOperator2["EQUALS"] = "equals";
  FilterOperator2["NOT"] = "not";
  FilterOperator2["IN"] = "in";
  FilterOperator2["NOT_IN"] = "notIn";
  FilterOperator2["CONTAINS"] = "contains";
  FilterOperator2["STARTS_WITH"] = "startsWith";
  FilterOperator2["ENDS_WITH"] = "endsWith";
  FilterOperator2["GT"] = "gt";
  FilterOperator2["GTE"] = "gte";
  FilterOperator2["LT"] = "lt";
  FilterOperator2["LTE"] = "lte";
  FilterOperator2["BETWEEN"] = "between";
  FilterOperator2["NOT_BETWEEN"] = "notBetween";
  return FilterOperator2;
})(FilterOperator || {});
var FilterConditionSchema = z.object({
  field: z.string(),
  operator: z.enum(FilterOperator),
  value: z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.date(),
    z.array(z.union([z.string(), z.number(), z.date()]))
  ])
});
var SortDirection = /* @__PURE__ */ ((SortDirection2) => {
  SortDirection2["ASC"] = "asc";
  SortDirection2["DESC"] = "desc";
  return SortDirection2;
})(SortDirection || {});
var SortConditionSchema = z.object({
  field: z.string(),
  direction: z.enum(SortDirection)
});
var PaginationSchema = z.object({
  page: numberCoerce.optional().default(1).optional(),
  limit: numberCoerce.optional().default(10).optional(),
  cursor: z.string().optional()
});
var coerceFilterValue = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => coerceFilterValue(item));
  }
  if (value === "true") return true;
  if (value === "false") return false;
  return value;
};
var QuerySchema = z.object({
  filters: z.union([
    z.array(FilterConditionSchema),
    z.record(z.string(), z.any())
    // Object format: { field: { operator: value } }
  ]).transform((val) => {
    if (!val) return void 0;
    if (Array.isArray(val)) return val;
    const filters = [];
    for (const [field, operators] of Object.entries(val)) {
      if (typeof operators === "object" && operators !== null) {
        for (const [operator, value] of Object.entries(operators)) {
          filters.push({
            field,
            operator,
            value: coerceFilterValue(value)
          });
        }
      }
    }
    return filters;
  }).optional(),
  sort: z.union([
    z.array(SortConditionSchema),
    z.record(z.string(), z.enum(SortDirection))
  ]).transform((val) => {
    if (!val) return void 0;
    if (Array.isArray(val)) return val;
    const sorts = [];
    for (const [field, direction] of Object.entries(val)) {
      sorts.push({
        field,
        direction
      });
    }
    return sorts;
  }).optional(),
  pagination: PaginationSchema.optional(),
  search: z.string().optional()
});
var QueryParamsFilterSchema = z.record(
  z.string(),
  z.partialRecord(
    z.enum(FilterOperator),
    z.union([
      z.string(),
      z.number(),
      z.boolean(),
      z.array(z.union([z.string(), z.number()]))
    ])
  )
);
var QueryParamsDtoSchema = z.object({
  filters: QueryParamsFilterSchema.optional(),
  sort: z.record(z.string(), z.enum(SortDirection)).optional(),
  page: z.number().optional(),
  limit: z.number().optional(),
  search: z.string().optional()
});
var BaseQuerySchema = QuerySchema;
function createValidatedQuerySchema(allowedFields) {
  return QuerySchema.refine(
    (data) => {
      if (!data.filters) return true;
      return data.filters.every(
        (filter) => allowedFields.includes(filter.field)
      );
    },
    {
      message: `Invalid filter field.`,
      path: ["filters"]
    }
  ).refine(
    (data) => {
      if (!data.sort) return true;
      return data.sort.every((sort) => allowedFields.includes(sort.field));
    },
    {
      message: `Invalid sort field.`,
      path: ["sort"]
    }
  );
}

// src/members/query-members.dto.ts
var ALLOWED_MEMBER_FIELDS = [
  "id",
  "uuid",
  "userId",
  "organizationId",
  "joinedAt",
  "leftAt",
  "createdAt",
  "updatedAt",
  // User relation fields
  "user.id",
  "user.uuid",
  "user.email",
  "user.firstName",
  "user.lastName",
  "user.phoneNumber",
  "user.createdAt"
];
var MemberFilterSchema = FilterConditionSchema.refine(
  (filter) => ALLOWED_MEMBER_FIELDS.includes(filter.field),
  {
    message: `Invalid filter field. Allowed fields: ${ALLOWED_MEMBER_FIELDS.join(", ")}`
  }
);
var MemberSortSchema = SortConditionSchema.refine(
  (sort) => ALLOWED_MEMBER_FIELDS.includes(sort.field),
  {
    message: `Invalid sort field. Allowed fields: ${ALLOWED_MEMBER_FIELDS.join(", ")}`
  }
);
var QueryMembersDtoSchema = BaseQuerySchema.extend({
  filters: z.array(MemberFilterSchema).optional(),
  sort: z.array(MemberSortSchema).optional()
});
var UpdateMemberDtoSchema = z.object({
  membershipTierId: z.string().uuid().optional()
});

// src/memberships/organization-membership.dto.ts
var OrganizationMembershipDtoSchema = BaseMembershipDtoSchema.omit({
  userId: true,
  organizationId: true,
  membershipTierId: true
}).extend({
  user: UserDtoSchema,
  membershipTier: MembershipTierDtoSchema.nullable()
});

// src/memberships/query-memberships.dto.ts
var QueryMembershipsDtoSchema = BaseQuerySchema;
var TierChangePolicy = /* @__PURE__ */ ((TierChangePolicy2) => {
  TierChangePolicy2["NEXT_CYCLE"] = "NEXT_CYCLE";
  TierChangePolicy2["PRORATE"] = "PRORATE";
  TierChangePolicy2["IMMEDIATE"] = "IMMEDIATE";
  return TierChangePolicy2;
})(TierChangePolicy || {});
var DuesSettingsDtoSchema = z.object({
  uuid: z.string().uuid(),
  organizationId: bigintToString,
  paymentDeadlineDays: z.number().int().min(1).max(90),
  autoPayEnabled: z.boolean(),
  overdueGracePeriodDays: z.number().int().min(0).max(90).nullable(),
  exemptPermanentTiers: z.boolean(),
  tierChangePolicy: z.nativeEnum(TierChangePolicy),
  reminderStartDaysBefore: z.number().int().min(0).max(90),
  reminderIntervalDays: z.number().int().min(1).max(90),
  reminderMaxOccurrences: z.number().int().min(1).max(10),
  createdAt: dateToISOString,
  updatedAt: dateToISOString
});
var UpdateDuesSettingsDtoSchema = z.object({
  paymentDeadlineDays: z.number().int().min(1).max(90).optional(),
  autoPayEnabled: z.boolean().optional(),
  overdueGracePeriodDays: z.number().int().min(0).max(90).nullable().optional(),
  exemptPermanentTiers: z.boolean().optional(),
  tierChangePolicy: z.nativeEnum(TierChangePolicy).optional(),
  reminderStartDaysBefore: z.number().int().min(0).max(90).optional(),
  reminderIntervalDays: z.number().int().min(1).max(90).optional(),
  reminderMaxOccurrences: z.number().int().min(1).max(10).optional()
});
var DuesSettingsResponseDtoSchema = createResponseSchema(
  DuesSettingsDtoSchema
);

// src/memberships/assign-tier.dto.ts
var AssignTierDtoSchema = z.object({
  tierId: z.string().uuid("Tier ID must be a valid UUID"),
  isTierPermanent: z.boolean().optional().default(false).describe(
    "Whether the tier assignment is permanent (no expiration). Admin override only."
  ),
  voidExistingOpenInvoices: z.boolean().optional().default(true).describe(
    "Whether to void any existing open dues invoice from a prior tier when assigning this tier. When false, the old invoice is left open and a new invoice is generated anyway."
  ),
  tierChangePolicyOverride: z.nativeEnum(TierChangePolicy).nullable().optional().describe(
    "Per-assignment override of the organization's default tier change billing policy. Null/omitted uses the organization default. Only applies to tier CHANGES, not initial assignments."
  ),
  dueAt: z.iso.datetime().optional().describe(
    "Custom due date for the initial dues invoice, as an ISO datetime string. Defaults to now if omitted. Only applies to initial (non tier-change) paid tier assignments."
  )
});
var SuspendMemberDtoSchema = z.object({
  reason: z.string().min(1, "Reason is required").max(500, "Reason must be less than 500 characters").describe("The reason for suspending this membership")
});
var CreateMemberDtoSchema = z.object({
  email: z.string().email(),
  firstName: z.string().min(1).max(100),
  lastName: z.string().min(1).max(100),
  tierId: z.string().uuid().optional(),
  sendInvitation: z.boolean().optional().default(false)
});
var CreateMemberConflictCode = /* @__PURE__ */ ((CreateMemberConflictCode2) => {
  CreateMemberConflictCode2["USER_HAS_ACCOUNT"] = "USER_HAS_ACCOUNT";
  CreateMemberConflictCode2["ALREADY_MEMBER"] = "ALREADY_MEMBER";
  return CreateMemberConflictCode2;
})(CreateMemberConflictCode || {});
var CreateMemberConflictCodeSchema = z.nativeEnum(
  CreateMemberConflictCode
);
var CreateMemberConflictDtoSchema = z.object({
  code: CreateMemberConflictCodeSchema,
  user: z.object({
    email: z.string().email(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable()
  }).optional()
});
var MemberDuesStatus = /* @__PURE__ */ ((MemberDuesStatus2) => {
  MemberDuesStatus2["CURRENT"] = "CURRENT";
  MemberDuesStatus2["PENDING"] = "PENDING";
  MemberDuesStatus2["OVERDUE"] = "OVERDUE";
  MemberDuesStatus2["EXPIRED"] = "EXPIRED";
  MemberDuesStatus2["EXEMPT"] = "EXEMPT";
  MemberDuesStatus2["NO_DUES"] = "NO_DUES";
  return MemberDuesStatus2;
})(MemberDuesStatus || {});
var MemberGroupDuesDtoSchema = z.object({
  groupUuid: z.uuid(),
  groupName: z.string(),
  duesPrice: z.number(),
  duesIntervalType: z.enum(RecurrenceInterval).nullable(),
  duesIntervalLength: z.number().nullable()
});
var MemberDuesSummaryDtoSchema = z.object({
  hasDues: z.boolean(),
  duesStatus: z.nativeEnum(MemberDuesStatus).nullable(),
  lastDuesPaidAt: nullableDateToISOString,
  lastDuesPaidAmount: z.number().nullable(),
  nextDueDate: nullableDateToISOString,
  ytdAmountPaid: z.number(),
  /**
   * Active dues-bearing groups the member currently belongs to, for a
   * per-source breakdown of what makes up their dues (tier + groups).
   */
  groupDues: MemberGroupDuesDtoSchema.array(),
  /**
   * Human-readable note shown when a tier change is pending application at
   * the next billing cycle (e.g. "Billed at previous tier rate until MM/DD,
   * then $Y.YY/interval."). Null when no such note applies.
   */
  billingNote: z.string().nullable().optional()
});
var MemberDuesSummaryResponseDtoSchema = createResponseSchema(
  MemberDuesSummaryDtoSchema
);
var booleanQueryCoerce = z.preprocess((val) => {
  if (val === "true") return true;
  if (val === "false") return false;
  return val;
}, z.boolean());
var TierChangePreviewQueryDtoSchema = z.object({
  tierId: z.uuid("Tier ID must be a valid UUID"),
  isTierPermanent: booleanQueryCoerce.optional().default(false),
  tierChangePolicyOverride: z.nativeEnum(TierChangePolicy).nullable().optional()
});
var TierChangePreviewDtoSchema = z.object({
  effectivePolicy: z.nativeEnum(TierChangePolicy),
  willGenerateInvoiceNow: z.boolean(),
  previewAmount: z.number().nullable(),
  previewDueDate: dateToISOString.nullable(),
  currentCycleEndsAt: dateToISOString.nullable(),
  newTierName: z.string(),
  previousTierName: z.string().nullable()
});
var TierChangePreviewResponseDtoSchema = createResponseSchema(
  TierChangePreviewDtoSchema
);
var CreateMembershipTierDtoSchema = z.object({
  name: z.string().min(1, "Name is required").max(255, "Name must be less than 255 characters"),
  description: z.string().max(1e3, "Description must be less than 1000 characters").optional(),
  color: z.string().regex(/^#[0-9A-F]{6}$/i, "Color must be a valid hex color code").optional(),
  level: z.number().int("Level must be an integer").min(1, "Level must be at least 1"),
  price: z.number().int("Price must be an integer number of cents").min(0, "Price must be non-negative").optional(),
  intervalLength: z.number().int("Interval length must be an integer").min(1, "Interval length must be at least 1").optional(),
  intervalType: z.enum(["DAILY", "WEEKLY", "MONTHLY", "YEARLY"]).optional(),
  isDefault: z.boolean().optional(),
  isActive: z.boolean().optional(),
  metadata: z.record(z.string(), z.unknown()).optional()
});
var UpdateMembershipTierDtoSchema = z.object({
  name: z.string().min(1, "Name is required").max(255, "Name must be less than 255 characters").optional(),
  description: z.string().max(1e3, "Description must be less than 1000 characters").optional(),
  color: z.string().regex(/^#[0-9A-F]{6}$/i, "Color must be a valid hex color code").optional(),
  level: z.number().int("Level must be an integer").min(1, "Level must be at least 1").optional(),
  price: z.number().int("Price must be an integer number of cents").min(0, "Price must be non-negative").optional(),
  intervalLength: z.number().int("Interval length must be an integer").min(1, "Interval length must be at least 1").optional(),
  intervalType: z.enum(["DAILY", "WEEKLY", "MONTHLY", "QUARTERLY", "YEARLY"]).optional(),
  isDefault: z.boolean().optional(),
  isActive: z.boolean().optional(),
  metadata: z.record(z.string(), z.unknown()).optional()
});
var DeleteMembershipTierDtoSchema = z.object({
  replacementTierUuid: z.string().uuid().optional()
});
var OrganizationTaxRateDtoSchema = z.object({
  uuid: z.string(),
  name: z.string(),
  ratePercentage: numberCoerce,
  isActive: z.boolean(),
  createdAt: dateToISOString,
  updatedAt: dateToISOString
});
var OrganizationTaxRateResponseDtoSchema = createResponseSchema(
  OrganizationTaxRateDtoSchema
);
var OrganizationTaxRateArrayResponseDtoSchema = createResponseSchema(
  OrganizationTaxRateDtoSchema.array()
);
var CreateOrganizationTaxRateDtoSchema = z.object({
  name: z.string().min(1, "Name is required").max(255, "Name must be less than 255 characters"),
  ratePercentage: z.number().min(0, "Rate must be non-negative").max(100, "Rate must not exceed 100")
});
var UpdateOrganizationTaxRateDtoSchema = z.object({
  name: z.string().min(1, "Name is required").max(255, "Name must be less than 255 characters").optional(),
  ratePercentage: z.number().min(0, "Rate must be non-negative").max(100, "Rate must not exceed 100").optional(),
  isActive: z.boolean().optional()
});
var TaxableSourceType = /* @__PURE__ */ ((TaxableSourceType2) => {
  TaxableSourceType2["MEMBERSHIP_TIER"] = "MEMBERSHIP_TIER";
  TaxableSourceType2["DONATION"] = "DONATION";
  TaxableSourceType2["EVENT"] = "EVENT";
  TaxableSourceType2["STORE_PURCHASE"] = "STORE_PURCHASE";
  TaxableSourceType2["CUSTOM"] = "CUSTOM";
  return TaxableSourceType2;
})(TaxableSourceType || {});
var OrganizationTaxRateAssignmentDtoSchema = z.object({
  sourceType: z.enum(TaxableSourceType),
  taxRate: OrganizationTaxRateDtoSchema.nullable()
});
var OrganizationTaxRateAssignmentArrayResponseDtoSchema = createResponseSchema(OrganizationTaxRateAssignmentDtoSchema.array());
var UpdateOrganizationTaxRateAssignmentsDtoSchema = z.object({
  assignments: z.array(
    z.object({
      sourceType: z.enum(TaxableSourceType),
      taxRateId: z.uuid().nullable()
    })
  )
});
var InvitationStatus = /* @__PURE__ */ ((InvitationStatus2) => {
  InvitationStatus2["PENDING"] = "PENDING";
  InvitationStatus2["ACCEPTED"] = "ACCEPTED";
  InvitationStatus2["EXPIRED"] = "EXPIRED";
  InvitationStatus2["REVOKED"] = "REVOKED";
  return InvitationStatus2;
})(InvitationStatus || {});
var InvitationStatusSchema = z.nativeEnum(InvitationStatus);
var RolePermissionDtoSchema = z.object({
  resource: z.enum(Resource),
  action: z.enum(Action),
  createdAt: dateToISOString
});
var OrganizationRoleDtoSchema = z.object({
  uuid: z.string().uuid(),
  organizationId: bigintToString,
  name: z.string(),
  description: z.string().nullable(),
  isSystem: z.boolean(),
  isTemplate: z.boolean(),
  maxAssignments: z.number().nullable(),
  createdAt: dateToISOString,
  updatedAt: dateToISOString
});
var OrganizationRoleWithRelationsDtoSchema = OrganizationRoleDtoSchema.extend({
  permissions: z.array(RolePermissionDtoSchema),
  _count: z.object({
    assignments: z.number(),
    groupAssignments: z.number()
  }).optional()
});
var OrganizationRoleResponseDtoSchema = createResponseSchema(
  OrganizationRoleDtoSchema
);
var OrganizationRoleWithRelationsResponseDtoSchema = createResponseSchema(OrganizationRoleWithRelationsDtoSchema);
var OrganizationRoleArrayResponseDtoSchema = createResponseSchema(
  OrganizationRoleDtoSchema.array()
);
var OrganizationRoleWithRelationsArrayResponseDtoSchema = createResponseSchema(OrganizationRoleWithRelationsDtoSchema.array());
var OrganizationRoleDeletedDtoSchema = z.object({
  deleted: z.boolean()
});
var OrganizationRoleDeletedResponseDtoSchema = createResponseSchema(
  OrganizationRoleDeletedDtoSchema
);

// src/invitations/invitation.dto.ts
var InvitationMembershipDtoSchema = OrganizationMembershipDtoSchema.pick({
  uuid: true,
  user: true
});
var InvitationDtoSchema = z.object({
  uuid: z.string().uuid(),
  token: z.string().uuid(),
  email: z.string().email(),
  name: z.string(),
  status: InvitationStatusSchema,
  sentAt: dateToISOString,
  acceptedAt: nullableDateToISOString,
  expiresAt: dateToISOString,
  reminderSentAt: nullableDateToISOString,
  createdAt: dateToISOString,
  updatedAt: dateToISOString
});
var InvitationWithRelationsDtoSchema = InvitationDtoSchema.omit({
  token: true
}).extend({
  tier: MembershipTierDtoSchema.nullable(),
  organizationRole: OrganizationRoleDtoSchema.nullable(),
  invitedByMembership: InvitationMembershipDtoSchema
});
var CreateInvitationDtoSchema = z.object({
  email: z.string().email(),
  name: z.string().min(1).max(255),
  tierId: z.string().uuid().optional(),
  organizationRoleId: z.string().uuid().optional()
});
var AcceptInvitationDtoSchema = z.object({
  token: z.string().uuid()
});
var PublicInvitationDtoSchema = z.object({
  uuid: z.string().uuid(),
  organizationName: z.string(),
  tierName: z.string().nullable(),
  organizationRoleName: z.string().nullable(),
  invitedByName: z.string(),
  expiresAt: dateToISOString,
  status: InvitationStatusSchema
});
var InvitationEmailDtoSchema = z.object({
  recipientName: z.string(),
  recipientEmail: z.string().email(),
  organizationName: z.string(),
  tierName: z.string().nullable(),
  invitedByName: z.string(),
  acceptUrl: z.string().url(),
  expiresAt: z.string()
  // ISO date string
});
var InvitationReminderEmailDtoSchema = InvitationEmailDtoSchema.extend(
  {
    daysSinceSent: z.number()
  }
);
var GroupTypeSchema = z.enum([
  "COMMITTEE",
  "CHAPTER",
  "INTEREST_GROUP",
  "TEAM",
  "OTHER"
]);
var GroupTypeEnum = /* @__PURE__ */ ((GroupTypeEnum2) => {
  GroupTypeEnum2["COMMITTEE"] = "COMMITTEE";
  GroupTypeEnum2["CHAPTER"] = "CHAPTER";
  GroupTypeEnum2["INTEREST_GROUP"] = "INTEREST_GROUP";
  GroupTypeEnum2["TEAM"] = "TEAM";
  GroupTypeEnum2["OTHER"] = "OTHER";
  return GroupTypeEnum2;
})(GroupTypeEnum || {});
var GroupVisibilitySchema = z.enum(["PUBLIC", "PRIVATE", "HIDDEN"]);
var GroupVisibilityEnum = /* @__PURE__ */ ((GroupVisibilityEnum2) => {
  GroupVisibilityEnum2["PUBLIC"] = "PUBLIC";
  GroupVisibilityEnum2["PRIVATE"] = "PRIVATE";
  GroupVisibilityEnum2["HIDDEN"] = "HIDDEN";
  return GroupVisibilityEnum2;
})(GroupVisibilityEnum || {});
var GroupRoleDtoSchema = z.object({
  uuid: z.string().uuid(),
  organizationId: bigintToString,
  groupId: nullableBigintToString,
  name: z.string(),
  description: z.string().nullable(),
  isLeader: z.boolean(),
  sortOrder: z.number(),
  createdAt: dateToISOString,
  updatedAt: dateToISOString
});
var GroupRoleResponseDtoSchema = createResponseSchema(GroupRoleDtoSchema);
var GroupRoleArrayResponseDtoSchema = createResponseSchema(
  GroupRoleDtoSchema.array()
);
var CreateGroupRoleDtoSchema = z.object({
  name: z.string().min(1).max(100),
  description: z.string().max(500).optional(),
  isLeader: z.boolean().optional().default(false),
  sortOrder: z.number().int().min(0).optional(),
  groupUuid: z.string().uuid().optional()
  // omit for org-wide, provide for group-specific
});
var UpdateGroupRoleDtoSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  description: z.string().max(500).nullable().optional(),
  isLeader: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional()
});
var GroupDtoSchema = z.object({
  uuid: z.string().uuid(),
  organizationId: bigintToString,
  name: z.string(),
  description: z.string().nullable(),
  type: GroupTypeSchema,
  visibility: GroupVisibilitySchema,
  duesPrice: z.number().int().nullable(),
  duesIntervalType: z.enum(RecurrenceInterval).nullable(),
  duesIntervalLength: z.number().int().min(1).nullable(),
  duesIsActive: z.boolean(),
  createdAt: dateToISOString,
  updatedAt: dateToISOString,
  _count: z.object({
    members: z.number()
  }).optional()
});
var GroupWithRelationsDtoSchema = GroupDtoSchema.extend({
  logoStorageItem: StorageItemDtoSchema.nullable().optional()
});
var GroupResponseDtoSchema = createResponseSchema(
  GroupWithRelationsDtoSchema
);
var GroupArrayResponseDtoSchema = createResponseSchema(
  GroupDtoSchema.array()
);
var CreateGroupDtoSchema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().max(5e3).optional(),
  type: GroupTypeSchema.optional().default("OTHER"),
  visibility: GroupVisibilitySchema.optional().default("PUBLIC")
});
var UpdateGroupDtoSchema = z.object({
  name: z.string().min(1).max(255).optional(),
  description: z.string().max(5e3).nullable().optional(),
  type: GroupTypeSchema.optional(),
  visibility: GroupVisibilitySchema.optional()
});

// src/groups/query-group.dto.ts
var ALLOWED_GROUP_FIELDS = [
  "uuid",
  "name",
  "description",
  "type",
  "visibility",
  "createdAt",
  "updatedAt",
  "organizationId",
  "deleted"
];
var QueryGroupSchema = createValidatedQuerySchema([
  ...ALLOWED_GROUP_FIELDS
]);
var GroupMemberDtoSchema = z.object({
  uuid: z.string().uuid(),
  groupId: bigintToString,
  membershipId: bigintToString,
  groupRoleId: bigintToString,
  title: z.string().nullable(),
  joinedAt: dateToISOString,
  createdAt: dateToISOString,
  updatedAt: dateToISOString
});
var GroupMemberWithRelationsDtoSchema = GroupMemberDtoSchema.extend({
  groupRole: GroupRoleDtoSchema,
  membership: OrganizationMembershipDtoSchema,
  group: GroupDtoSchema.optional()
});
var GroupMemberResponseDtoSchema = createResponseSchema(
  GroupMemberWithRelationsDtoSchema
);
var GroupMemberArrayResponseDtoSchema = createResponseSchema(
  GroupMemberWithRelationsDtoSchema.array()
);
var CreateGroupMemberDtoSchema = z.object({
  membershipUuid: z.string().uuid(),
  groupRoleUuid: z.string().uuid().optional(),
  // defaults to "Member" role if omitted
  title: z.string().max(255).optional(),
  duesFirstBillingDate: z.iso.datetime().optional().describe(
    "Custom first-billing date for the group's dues, as an ISO datetime string. Defaults to now if omitted. Only applies when the group has active dues configured; anchors both the initial invoice and the recurring cycle."
  )
});
var UpdateGroupMemberDtoSchema = z.object({
  groupRoleUuid: z.string().uuid().optional(),
  title: z.string().max(255).nullable().optional()
});

// src/groups/query-group-member.dto.ts
var ALLOWED_GROUP_MEMBER_FIELDS = [
  "groupRoleId",
  "groupRole.name",
  "groupRole.isLeader",
  "title",
  "joinedAt",
  "createdAt",
  "groupId",
  "membershipId",
  "deleted",
  "membership.uuid",
  "membership.user.firstName",
  "membership.user.lastName",
  "membership.user.email",
  "group.name",
  "group.type",
  "group.visibility"
];
var QueryGroupMemberSchema = createValidatedQuerySchema([
  ...ALLOWED_GROUP_MEMBER_FIELDS
]);
var GroupDuesDtoSchema = z.object({
  duesPrice: z.number().int().nullable(),
  duesIntervalType: z.enum(RecurrenceInterval).nullable(),
  duesIntervalLength: z.number().int().min(1).nullable(),
  duesIsActive: z.boolean(),
  activeMemberCount: z.number(),
  // computed, not stored
  estimatedPeriodicRevenue: z.number()
  // computed: duesPrice * activeMemberCount
});
var UpdateGroupDuesDtoSchema = z.object({
  duesPrice: z.number().int("Dues price must be an integer number of cents").min(0, "Dues price must be non-negative").nullable().optional(),
  duesIntervalType: z.enum(RecurrenceInterval).nullable().optional(),
  duesIntervalLength: z.number().int("Interval length must be an integer").min(1, "Interval length must be at least 1").nullable().optional(),
  duesIsActive: z.boolean().optional()
});
var GroupDuesResponseDtoSchema = createResponseSchema(GroupDuesDtoSchema);
var RolePermissionInputSchema = z.object({
  resource: z.enum(Resource),
  action: z.enum(Action)
});
var CreateOrganizationRoleDtoSchema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().max(5e3).optional(),
  maxAssignments: z.number().int().positive().optional(),
  permissions: z.array(RolePermissionInputSchema).default([])
});
var UpdateOrganizationRoleDtoSchema = z.object({
  name: z.string().min(1).max(255).optional(),
  description: z.string().max(5e3).nullable().optional(),
  maxAssignments: z.number().int().positive().nullable().optional(),
  permissions: z.array(RolePermissionInputSchema).optional()
});

// src/roles/query-organization-role.dto.ts
var ALLOWED_ORGANIZATION_ROLE_FIELDS = [
  "uuid",
  "name",
  "description",
  "isSystem",
  "isTemplate",
  "createdAt",
  "updatedAt",
  "organizationId",
  "deleted"
];
var QueryOrganizationRoleSchema = createValidatedQuerySchema([
  ...ALLOWED_ORGANIZATION_ROLE_FIELDS
]);
var RoleAssignmentDtoSchema = z.object({
  uuid: z.string().uuid(),
  organizationRoleId: bigintToString,
  membershipId: bigintToString,
  assignedById: nullableBigintToString,
  expiresAt: nullableDateToISOString,
  revokedAt: nullableDateToISOString,
  createdAt: dateToISOString,
  updatedAt: dateToISOString
});
var RoleAssignmentWithRelationsDtoSchema = RoleAssignmentDtoSchema.extend({
  organizationRole: OrganizationRoleDtoSchema,
  membership: OrganizationMembershipDtoSchema
});
var RoleAssignmentResponseDtoSchema = createResponseSchema(
  RoleAssignmentDtoSchema
);
var RoleAssignmentWithRelationsResponseDtoSchema = createResponseSchema(RoleAssignmentWithRelationsDtoSchema);
var RoleAssignmentArrayResponseDtoSchema = createResponseSchema(
  RoleAssignmentDtoSchema.array()
);
var RoleAssignmentWithRelationsArrayResponseDtoSchema = createResponseSchema(RoleAssignmentWithRelationsDtoSchema.array());
var CreateRoleAssignmentDtoSchema = z.object({
  membershipUuid: z.string().uuid(),
  expiresAt: z.string().datetime().optional()
});
var GroupRoleAssignmentDtoSchema = z.object({
  uuid: z.string().uuid(),
  organizationRoleId: bigintToString,
  groupId: bigintToString,
  assignedById: nullableBigintToString,
  expiresAt: nullableDateToISOString,
  revokedAt: nullableDateToISOString,
  createdAt: dateToISOString,
  updatedAt: dateToISOString
});
var GroupRoleAssignmentWithRelationsDtoSchema = GroupRoleAssignmentDtoSchema.extend({
  organizationRole: OrganizationRoleDtoSchema,
  group: GroupDtoSchema
});
var GroupRoleAssignmentResponseDtoSchema = createResponseSchema(
  GroupRoleAssignmentDtoSchema
);
var GroupRoleAssignmentWithRelationsResponseDtoSchema = createResponseSchema(GroupRoleAssignmentWithRelationsDtoSchema);
var GroupRoleAssignmentArrayResponseDtoSchema = createResponseSchema(
  GroupRoleAssignmentDtoSchema.array()
);
var GroupRoleAssignmentWithRelationsArrayResponseDtoSchema = createResponseSchema(GroupRoleAssignmentWithRelationsDtoSchema.array());
var CreateGroupRoleAssignmentDtoSchema = z.object({
  groupUuid: z.string().uuid(),
  expiresAt: z.string().datetime().optional()
});
var RoleAssignmentWithPermissionsDtoSchema = RoleAssignmentWithRelationsDtoSchema.extend({
  organizationRole: OrganizationRoleWithRelationsDtoSchema
});
var GroupRoleAssignmentWithPermissionsDtoSchema = GroupRoleAssignmentWithRelationsDtoSchema.extend({
  organizationRole: OrganizationRoleWithRelationsDtoSchema
});
var MemberRolesResponseDtoSchema = z.object({
  directRoles: z.array(RoleAssignmentWithPermissionsDtoSchema),
  groupInheritedRoles: z.array(GroupRoleAssignmentWithPermissionsDtoSchema)
});
var MemberRolesResponseResponseDtoSchema = createResponseSchema(
  MemberRolesResponseDtoSchema
);
var FeatureFlagDtoSchema = z.object({
  uuid: z.string(),
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  enabled: z.boolean(),
  isPublic: z.boolean(),
  createdAt: dateToISOString,
  updatedAt: dateToISOString,
  deleted: nullableDateToISOString
});
var PublicFeatureFlagDtoSchema = z.object({
  key: z.string(),
  enabled: z.boolean()
});

// src/config/config.dto.ts
var MimberConfigDtoSchema = z.object({
  mimberMaintenance: z.boolean(),
  mimberRequiredVersion: z.string()
});
var MimberAdminConfigDtoSchema = z.object({
  mimberAdminMaintenance: z.boolean(),
  mimberAdminRequiredVersion: z.string()
});
var MimberOrganizationConfigDtoSchema = z.object({
  mimberOrganizationMaintenance: z.boolean(),
  mimberOrganizationRequiredVersion: z.string(),
  publicBetaMode: z.boolean(),
  featureFlags: z.array(PublicFeatureFlagDtoSchema)
});
var FullConfigurationDtoSchema = z.object({
  uuid: z.string(),
  mimberMaintenance: z.boolean(),
  mimberAdminMaintenance: z.boolean(),
  mimberOrganizationMaintenance: z.boolean(),
  mimberRequiredVersion: z.string(),
  mimberAdminRequiredVersion: z.string(),
  mimberOrganizationRequiredVersion: z.string(),
  publicBetaMode: z.boolean(),
  createdAt: dateToISOString,
  updatedAt: dateToISOString
});
var UpdateConfigurationDtoSchema = z.object({
  mimberMaintenance: z.boolean().optional(),
  mimberAdminMaintenance: z.boolean().optional(),
  mimberOrganizationMaintenance: z.boolean().optional(),
  mimberRequiredVersion: z.string().optional(),
  mimberAdminRequiredVersion: z.string().optional(),
  mimberOrganizationRequiredVersion: z.string().optional(),
  publicBetaMode: z.boolean().optional()
});
var DocumentStatusDtoSchema = z.object({
  message: z.string().min(1),
  status: z.enum(["processing", "ready", "failed"])
}).strict();
var ResourceEnum = z.enum([
  "ALL",
  "ADMINISTRATOR",
  "AUDIT_LOG",
  "FEATURE_FLAGS",
  "INVOICE",
  "MEMBERSHIP",
  "MEMBERSHIP_TIER",
  "ORGANIZATION",
  "PAYMENT",
  "STORAGE",
  "USER"
]);
var ResourceReferenceDtoSchema = z.object({
  resource: ResourceEnum,
  resourceId: z.string().min(1, "Resource ID cannot be empty")
});
var ResourceAttachmentDtoSchema = z.object({
  id: bigintToString,
  uuid: z.string(),
  // File reference
  storageItemId: bigintToString,
  // Resource reference
  resource: ResourceEnum,
  resourceId: z.string(),
  relationshipType: z.string().nullable(),
  // Metadata
  displayOrder: z.number().nullable(),
  isRequired: z.boolean(),
  autoCleanup: z.boolean(),
  // Timestamps
  createdAt: dateToISOString,
  updatedAt: dateToISOString
});
var PresignedUrlDtoSchema = z.object({
  url: z.string(),
  fields: z.record(z.string(), z.string()).optional(),
  headers: z.record(z.string(), z.string()).optional(),
  expiresAt: dateToISOString,
  uploadKey: z.string()
});
var FileUploadDtoSchema = z.object({
  originalName: z.string(),
  contentType: z.string().optional(),
  path: z.string().optional(),
  resourceReference: ResourceReferenceDtoSchema.optional(),
  attachmentType: z.string().optional(),
  description: z.string().optional(),
  metadata: z.record(z.string(), z.string()).optional(),
  tags: z.array(z.string()).optional()
});
var PresignedUploadRequestDtoSchema = z.object({
  filename: z.string(),
  contentType: z.string().optional(),
  maxFileSize: z.number().int().min(1).max(100 * 1024 * 1024).optional(),
  expiresIn: z.number().int().min(60).max(86400).optional(),
  path: z.string().optional(),
  resourceReference: ResourceReferenceDtoSchema.optional(),
  attachmentType: z.string().optional(),
  description: z.string().optional(),
  metadata: z.record(z.string(), z.string()).optional()
});
var CompletePresignedUploadDtoSchema = z.object({
  uploadKey: z.string(),
  originalName: z.string(),
  size: z.number().int().min(1),
  contentType: z.string().optional(),
  tags: z.array(z.string()).optional(),
  resourceReference: ResourceReferenceDtoSchema.optional(),
  attachmentType: z.string().optional(),
  description: z.string().optional(),
  metadata: z.record(z.string(), z.string()).optional()
});
var ListResourceFilesQueryDtoSchema = z.object({
  attachmentType: z.string().optional(),
  limit: numberCoerce.pipe(z.number().int().min(1).max(100)).optional(),
  offset: numberCoerce.pipe(z.number().int().min(0)).optional()
});
var OverrideType = /* @__PURE__ */ ((OverrideType2) => {
  OverrideType2["ORGANIZATION"] = "ORGANIZATION";
  OverrideType2["USER"] = "USER";
  return OverrideType2;
})(OverrideType || {});
var FeatureFlagOverrideDtoSchema = z.object({
  uuid: z.string(),
  featureFlagId: bigintToString,
  type: z.enum(OverrideType),
  userId: nullableBigintToString,
  organizationId: nullableBigintToString,
  enabled: z.boolean(),
  createdAt: dateToISOString,
  updatedAt: dateToISOString
});
var CreateFeatureFlagDtoSchema = z.object({
  key: z.string().min(1, "Key is required").max(100, "Key must be less than 100 characters").regex(
    /^[a-z0-9]+(-[a-z0-9]+)*$/,
    "Key must be lowercase alphanumeric with hyphens only, cannot start or end with hyphen"
  ),
  name: z.string().min(1, "Name is required").max(255, "Name must be less than 255 characters"),
  description: z.string().max(1e3, "Description must be less than 1000 characters").nullable().optional(),
  enabled: z.boolean().optional(),
  isPublic: z.boolean().optional()
});
var UpdateFeatureFlagDtoSchema = z.object({
  name: z.string().min(1, "Name is required").max(255, "Name must be less than 255 characters").optional(),
  description: z.string().max(1e3, "Description must be less than 1000 characters").nullable().optional(),
  enabled: z.boolean().optional(),
  isPublic: z.boolean().optional()
});
var CreateFeatureFlagOverrideDtoSchema = z.object({
  featureFlagId: optionalBigintToString,
  type: z.enum(OverrideType, {
    message: "Type is required"
  }),
  organizationId: optionalBigintToString,
  userId: optionalBigintToString,
  enabled: z.boolean({
    message: "Enabled status is required"
  })
}).refine(
  (data) => {
    if (data.type === "ORGANIZATION") {
      return !!data.organizationId;
    }
    if (data.type === "USER") {
      return !!data.userId;
    }
    return true;
  },
  {
    message: "organizationId is required when type is ORGANIZATION, userId is required when type is USER"
  }
);
var UpdateFeatureFlagOverrideDtoSchema = z.object({
  enabled: z.boolean().optional()
}).strict();
var DeadLetterJobStatus = /* @__PURE__ */ ((DeadLetterJobStatus2) => {
  DeadLetterJobStatus2["PENDING"] = "PENDING";
  DeadLetterJobStatus2["REPLAYED"] = "REPLAYED";
  DeadLetterJobStatus2["RESOLVED"] = "RESOLVED";
  return DeadLetterJobStatus2;
})(DeadLetterJobStatus || {});
var DeadLetterJobDtoSchema = z.object({
  uuid: z.string(),
  queueName: z.string(),
  jobName: z.string(),
  jobId: z.string(),
  jobData: z.record(z.string(), z.unknown()),
  jobOpts: z.record(z.string(), z.unknown()),
  failedReason: z.string(),
  stacktrace: z.string().nullable(),
  attemptsMade: z.number().int(),
  status: z.enum(DeadLetterJobStatus),
  replayedAt: nullableDateToISOString,
  resolvedAt: nullableDateToISOString,
  createdAt: dateToISOString,
  updatedAt: dateToISOString
});
var DeadLetterJobResponseDtoSchema = createResponseSchema(
  DeadLetterJobDtoSchema
);
var DeadLetterJobArrayResponseDtoSchema = createResponseSchema(
  DeadLetterJobDtoSchema.array()
);
var AuditLogUserDtoSchema = z.object({
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string()
});
var AuditLogDtoSchema = z.object({
  uuid: z.string(),
  action: z.string(),
  resource: z.string(),
  resourceUuid: z.string().nullable(),
  userId: nullableBigintToString,
  user: AuditLogUserDtoSchema.nullable().optional(),
  administratorId: nullableBigintToString,
  organizationId: nullableBigintToString,
  ipAddress: z.string().nullable(),
  userAgent: z.string().nullable(),
  requestId: z.string().nullable(),
  oldData: z.record(z.string(), z.any()).nullable(),
  newData: z.record(z.string(), z.any()).nullable(),
  metadata: z.record(z.string(), z.any()).nullable(),
  createdAt: dateToISOString
});
var InvoiceLineItemSourceTypeSchema = z.enum([
  "MEMBERSHIP_TIER",
  "GROUP_DUES"
]);

// src/invoices/invoice-line-item.dto.ts
var InvoiceLineItemDtoSchema = z.object({
  uuid: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  quantity: z.number(),
  unitAmount: nullableNumberCoerce,
  // Decimal converted to number
  subtotalAmount: nullableNumberCoerce,
  // Decimal converted to number
  metadata: z.any().nullable(),
  sourceType: InvoiceLineItemSourceTypeSchema.nullable(),
  sourceId: bigintToString.nullable(),
  forMembershipId: bigintToString.nullable(),
  createdAt: dateToISOString,
  updatedAt: dateToISOString
});
var PaymentMethod = /* @__PURE__ */ ((PaymentMethod2) => {
  PaymentMethod2["CARD"] = "CARD";
  PaymentMethod2["ACH"] = "ACH";
  PaymentMethod2["CASH"] = "CASH";
  PaymentMethod2["CHECK"] = "CHECK";
  PaymentMethod2["VENMO"] = "VENMO";
  PaymentMethod2["ZELLE"] = "ZELLE";
  PaymentMethod2["PAYPAL"] = "PAYPAL";
  PaymentMethod2["CASH_APP"] = "CASH_APP";
  PaymentMethod2["COMP"] = "COMP";
  PaymentMethod2["OTHER"] = "OTHER";
  return PaymentMethod2;
})(PaymentMethod || {});
var PAYMENT_METHOD_DISPLAY = {
  ["CARD" /* CARD */]: { label: "Card", color: "blue" },
  ["ACH" /* ACH */]: { label: "ACH", color: "geekblue" },
  ["CASH" /* CASH */]: { label: "Cash", color: "green" },
  ["CHECK" /* CHECK */]: { label: "Check", color: "cyan" },
  ["VENMO" /* VENMO */]: { label: "Venmo", color: "purple" },
  ["ZELLE" /* ZELLE */]: { label: "Zelle", color: "purple" },
  ["PAYPAL" /* PAYPAL */]: { label: "PayPal", color: "purple" },
  ["CASH_APP" /* CASH_APP */]: { label: "Cash App", color: "purple" },
  ["COMP" /* COMP */]: { label: "Complimentary", color: "gold" },
  ["OTHER" /* OTHER */]: { label: "Other", color: "default" }
};
var PaymentProvider = /* @__PURE__ */ ((PaymentProvider2) => {
  PaymentProvider2["STRIPE"] = "STRIPE";
  PaymentProvider2["MANUAL"] = "MANUAL";
  PaymentProvider2["OTHER"] = "OTHER";
  return PaymentProvider2;
})(PaymentProvider || {});
var PaymentStatus = /* @__PURE__ */ ((PaymentStatus2) => {
  PaymentStatus2["PENDING"] = "PENDING";
  PaymentStatus2["SUCCEEDED"] = "SUCCEEDED";
  PaymentStatus2["FAILED"] = "FAILED";
  PaymentStatus2["CANCELED"] = "CANCELED";
  PaymentStatus2["REFUNDED"] = "REFUNDED";
  return PaymentStatus2;
})(PaymentStatus || {});
var PaymentDtoSchema = z.object({
  uuid: z.string(),
  amount: nullableNumberCoerce,
  // Decimal converted to number
  method: z.nativeEnum(PaymentMethod),
  provider: z.nativeEnum(PaymentProvider),
  stripePaymentIntentId: z.string().nullable(),
  stripeChargeId: z.string().nullable(),
  stripeCheckoutSessionId: z.string().nullable(),
  stripeCustomerId: z.string().nullable(),
  webhookEventId: z.string().nullable(),
  platformFeeAmount: nullableNumberCoerce,
  stripeFeeAmount: nullableNumberCoerce,
  netAmount: nullableNumberCoerce,
  status: z.nativeEnum(PaymentStatus),
  failureCode: z.string().nullable(),
  failureMessage: z.string().nullable(),
  paidAt: dateToISOString.nullable(),
  note: z.string().nullable(),
  metadata: z.any().nullable(),
  invoiceId: nullableBigintToString,
  orderId: nullableBigintToString,
  createdAt: dateToISOString,
  updatedAt: dateToISOString
});

// src/invoices/invoice.dto.ts
var InvoiceStatus = /* @__PURE__ */ ((InvoiceStatus2) => {
  InvoiceStatus2["DRAFT"] = "DRAFT";
  InvoiceStatus2["OPEN"] = "OPEN";
  InvoiceStatus2["PAID"] = "PAID";
  InvoiceStatus2["OVERDUE"] = "OVERDUE";
  InvoiceStatus2["VOID"] = "VOID";
  return InvoiceStatus2;
})(InvoiceStatus || {});
var IssuerType = /* @__PURE__ */ ((IssuerType2) => {
  IssuerType2["MIMBER"] = "MIMBER";
  IssuerType2["ORGANIZATION"] = "ORGANIZATION";
  return IssuerType2;
})(IssuerType || {});
var InvoiceSourceType = /* @__PURE__ */ ((InvoiceSourceType2) => {
  InvoiceSourceType2["MEMBERSHIP_TIER"] = "MEMBERSHIP_TIER";
  InvoiceSourceType2["DONATION"] = "DONATION";
  InvoiceSourceType2["CUSTOM"] = "CUSTOM";
  InvoiceSourceType2["PLATFORM_PLAN"] = "PLATFORM_PLAN";
  return InvoiceSourceType2;
})(InvoiceSourceType || {});
var PaymentTerms = /* @__PURE__ */ ((PaymentTerms2) => {
  PaymentTerms2["IMMEDIATE"] = "IMMEDIATE";
  PaymentTerms2["DUE_ON_DATE"] = "DUE_ON_DATE";
  PaymentTerms2["NET_30"] = "NET_30";
  PaymentTerms2["NET_60"] = "NET_60";
  return PaymentTerms2;
})(PaymentTerms || {});
var InvoiceDtoSchema = z.object({
  uuid: z.string(),
  issuerType: z.nativeEnum(IssuerType),
  status: z.nativeEnum(InvoiceStatus),
  sourceType: z.nativeEnum(InvoiceSourceType),
  sourceId: z.string().nullable(),
  invoiceNumber: z.string(),
  currency: z.string(),
  issuedAt: dateToISOString.nullable(),
  dueAt: dateToISOString.nullable(),
  memo: z.string().nullable(),
  publicNote: z.string().nullable(),
  internalNote: z.string().nullable(),
  subtotalAmount: nullableNumberCoerce,
  discountAmount: nullableNumberCoerce,
  taxAmount: nullableNumberCoerce,
  taxRateId: nullableBigintToString,
  totalAmount: nullableNumberCoerce,
  platformFeeAmount: nullableNumberCoerce,
  stripeFeeAmount: nullableNumberCoerce,
  memberPaysTotal: nullableNumberCoerce,
  billingName: z.string().nullable(),
  billingEmail: z.string().nullable(),
  billingAddress: z.string().nullable(),
  refundedAt: dateToISOString.nullable(),
  voidedAt: dateToISOString.nullable(),
  voidReason: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  createdAt: dateToISOString,
  updatedAt: dateToISOString
});
var InvoiceMembershipDtoSchema = OrganizationMembershipDtoSchema.pick({
  uuid: true,
  user: true
});
var InvoiceWithRelationsDtoSchema = InvoiceDtoSchema.extend({
  lineItems: z.array(InvoiceLineItemDtoSchema),
  payment: PaymentDtoSchema.nullable(),
  organization: OrganizationDtoSchema.nullable().optional(),
  membership: InvoiceMembershipDtoSchema.nullable(),
  pdfStorageItem: StorageItemDtoSchema.nullable().optional(),
  amountDue: nullableNumberCoerce.optional()
});
var CreateInvoiceLineItemDtoSchema = z.object({
  name: z.string().min(1, "Line item name is required"),
  description: z.string().optional(),
  quantity: z.number().int().min(1, "Quantity must be at least 1").default(1),
  unitAmount: z.number().min(0, "Unit amount must be non-negative"),
  metadata: z.record(z.string(), z.any()).optional()
});
var CreateInvoiceDtoSchema = z.object({
  membershipId: z.string().uuid().optional(),
  // If omitted, it's an org invoice
  issuerType: z.nativeEnum(IssuerType).default("ORGANIZATION" /* ORGANIZATION */),
  sourceType: z.nativeEnum(InvoiceSourceType),
  sourceId: z.string().optional(),
  dueAt: z.string().datetime().optional(),
  // ISO 8601 datetime
  memo: z.string().optional(),
  publicNote: z.string().optional(),
  internalNote: z.string().optional(),
  discountAmount: z.number().min(0).default(0),
  lineItems: z.array(CreateInvoiceLineItemDtoSchema).min(1, "At least one line item is required"),
  metadata: z.record(z.string(), z.any()).optional()
});
var UpdateInvoiceDtoSchema = z.object({
  dueAt: z.string().datetime().optional(),
  memo: z.string().optional(),
  publicNote: z.string().optional(),
  internalNote: z.string().optional(),
  discountAmount: z.number().min(0).optional(),
  metadata: z.record(z.string(), z.any()).optional()
});
var IssueInvoiceDtoSchema = z.object({
  // Empty for now, but could include options like sendEmail in future
});
var VoidInvoiceDtoSchema = z.object({
  voidReason: z.string().min(1, "Void reason is required")
});
var RecordPaymentDtoSchema = z.object({
  amount: z.number().min(0.01, "Payment amount must be greater than 0"),
  method: z.nativeEnum(PaymentMethod),
  paidAt: z.string().datetime().optional(),
  // Defaults to now if not provided
  note: z.string().optional(),
  stripePaymentIntentId: z.string().optional(),
  // For tracking Stripe payment intent IDs
  metadata: z.record(z.string(), z.any()).optional()
});
var CreateLineItemDtoSchema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().max(1e3).optional(),
  quantity: z.number().int().min(1),
  unitAmount: z.number().min(0),
  metadata: z.record(z.string(), z.any()).optional()
});
var UpdateLineItemDtoSchema = z.object({
  name: z.string().min(1).max(255).optional(),
  description: z.string().max(1e3).optional(),
  quantity: z.number().int().min(1).optional(),
  unitAmount: z.number().min(0).optional(),
  metadata: z.record(z.string(), z.any()).optional()
});
var CartCheckoutItemDtoSchema = z.object({
  name: z.string().min(1, "Item name is required").max(255),
  description: z.string().max(500).optional(),
  quantity: z.number().int().positive("Quantity must be positive"),
  unitAmount: z.number().int().nonnegative("Unit amount must be non-negative")
});
var GuestCheckoutDtoSchema = z.object({
  organizationId: z.string().uuid("Invalid organization ID"),
  email: z.string().email("Invalid email address"),
  lineItems: z.array(CartCheckoutItemDtoSchema).min(1, "At least one line item is required"),
  returnUrl: z.string().url("Invalid return URL"),
  discountAmount: z.number().int().nonnegative().default(0)
});
var AuthCheckoutDtoSchema = z.object({
  organizationId: z.string().uuid("Invalid organization ID"),
  lineItems: z.array(CartCheckoutItemDtoSchema).min(1, "At least one line item is required"),
  returnUrl: z.string().url("Invalid return URL"),
  discountAmount: z.number().int().nonnegative().default(0)
});
var OrganizationCheckoutDtoSchema = z.object({
  invoiceUuid: z.string().uuid("Invalid invoice UUID format"),
  returnUrl: z.string().url("Invalid return URL format")
});
var CheckoutResultDtoSchema = z.object({
  invoice: InvoiceWithRelationsDtoSchema,
  paymentUrl: StripeUrlSchema
});
var CheckoutResponseDtoSchema = createResponseSchema(
  CheckoutResultDtoSchema
);
var OrderLineItemDtoSchema = z.object({
  uuid: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  quantity: z.number(),
  unitAmount: nullableNumberCoerce,
  // Decimal converted to number
  subtotalAmount: nullableNumberCoerce,
  // Decimal converted to number
  metadata: z.any().nullable(),
  createdAt: dateToISOString,
  updatedAt: dateToISOString
});
var EventStatusSchema = z.enum(["DRAFT", "PUBLISHED", "CANCELLED"]);
var EventStatus = {
  DRAFT: "DRAFT",
  PUBLISHED: "PUBLISHED",
  CANCELLED: "CANCELLED"
};
var EVENT_STATUS_DISPLAY = {
  DRAFT: { label: "Draft", color: "default" },
  PUBLISHED: { label: "Published", color: "green" },
  CANCELLED: { label: "Cancelled", color: "red" }
};
var EventLocationTypeSchema = z.enum(["PHYSICAL", "VIRTUAL", "HYBRID"]);
var EventLocationType = {
  PHYSICAL: "PHYSICAL",
  VIRTUAL: "VIRTUAL",
  HYBRID: "HYBRID"
};
var EVENT_LOCATION_TYPE_DISPLAY = {
  PHYSICAL: { label: "In Person", color: "blue" },
  VIRTUAL: { label: "Virtual", color: "purple" },
  HYBRID: { label: "Hybrid", color: "cyan" }
};
var EventVisibilitySchema = z.enum([
  "PUBLIC",
  "MEMBERS_ONLY",
  "GROUP_ONLY"
]);
var EventVisibility = {
  PUBLIC: "PUBLIC",
  MEMBERS_ONLY: "MEMBERS_ONLY",
  GROUP_ONLY: "GROUP_ONLY"
};
var EVENT_VISIBILITY_DISPLAY = {
  PUBLIC: { label: "Public", color: "green" },
  MEMBERS_ONLY: { label: "Members Only", color: "blue" },
  GROUP_ONLY: { label: "Group Only", color: "orange" }
};
var EventTicketTierDtoSchema = z.object({
  uuid: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  price: z.number().int(),
  capacity: z.number().int().nullable(),
  sortOrder: z.number().int(),
  salesStartAt: dateToISOString.nullable(),
  salesEndAt: dateToISOString.nullable(),
  isActive: z.boolean(),
  createdAt: dateToISOString,
  updatedAt: dateToISOString,
  // Computed fields (from _count)
  soldCount: z.number().int().optional(),
  remainingCapacity: z.number().int().nullable().optional()
});
var EventTicketTierResponseDtoSchema = createResponseSchema(
  EventTicketTierDtoSchema
);
var EventTicketTierArrayResponseDtoSchema = createResponseSchema(
  EventTicketTierDtoSchema.array()
);
var AudienceTargetTypeSchema = z.enum(["ALL", "TARGETED"]);
var AudienceTargetType = {
  ALL: "ALL",
  TARGETED: "TARGETED"
};

// src/audience-targets/audience-target.dto.ts
var AudienceTierTargetDtoSchema = z.object({
  membershipTier: MembershipTierDtoSchema.pick({ uuid: true, name: true })
});
var AudienceGroupTargetDtoSchema = z.object({
  group: GroupDtoSchema.pick({ uuid: true, name: true })
});
var AudienceRoleTargetDtoSchema = z.object({
  organizationRole: OrganizationRoleDtoSchema.pick({ uuid: true, name: true })
});
var AudienceMemberTargetDtoSchema = z.object({
  membership: BaseMembershipDtoSchema.pick({ uuid: true }).extend({
    user: UserObjectDtoSchema.pick({
      firstName: true,
      lastName: true,
      email: true
    })
  })
});
var AudienceTargetDtoSchema = z.object({
  uuid: z.string().uuid(),
  organizationId: bigintToString,
  targetType: AudienceTargetTypeSchema,
  createdAt: dateToISOString,
  updatedAt: dateToISOString
});
var AudienceTargetWithRelationsDtoSchema = AudienceTargetDtoSchema.extend({
  tierTargets: z.array(AudienceTierTargetDtoSchema).optional(),
  groupTargets: z.array(AudienceGroupTargetDtoSchema).optional(),
  roleTargets: z.array(AudienceRoleTargetDtoSchema).optional(),
  memberTargets: z.array(AudienceMemberTargetDtoSchema).optional()
});
var AudienceTargetResponseDtoSchema = createResponseSchema(
  AudienceTargetWithRelationsDtoSchema
);
var CreateAudienceTargetDtoSchema = z.object({
  targetType: z.nativeEnum(AudienceTargetType).default(AudienceTargetType.ALL),
  tierTargetIds: z.array(z.string().uuid()).optional(),
  groupTargetIds: z.array(z.string().uuid()).optional(),
  roleTargetIds: z.array(z.string().uuid()).optional(),
  memberTargetIds: z.array(z.string().uuid()).optional()
}).strict().refine(
  (data) => {
    var _a, _b, _c, _d;
    if (data.targetType === AudienceTargetType.TARGETED) {
      const hasTiers = (((_a = data.tierTargetIds) == null ? void 0 : _a.length) ?? 0) > 0;
      const hasGroups = (((_b = data.groupTargetIds) == null ? void 0 : _b.length) ?? 0) > 0;
      const hasRoles = (((_c = data.roleTargetIds) == null ? void 0 : _c.length) ?? 0) > 0;
      const hasMembers = (((_d = data.memberTargetIds) == null ? void 0 : _d.length) ?? 0) > 0;
      return hasTiers || hasGroups || hasRoles || hasMembers;
    }
    return true;
  },
  {
    message: "At least one target (tier, group, role, or member) is required when targetType is TARGETED",
    path: ["targetType"]
  }
);
var UpdateAudienceTargetDtoSchema = z.object({
  targetType: z.nativeEnum(AudienceTargetType).optional(),
  tierTargetIds: z.array(z.string().uuid()).optional(),
  groupTargetIds: z.array(z.string().uuid()).optional(),
  roleTargetIds: z.array(z.string().uuid()).optional(),
  memberTargetIds: z.array(z.string().uuid()).optional()
}).strict();

// src/events/event.dto.ts
var EventDtoSchema = z.object({
  uuid: z.string().uuid(),
  organizationId: bigintToString,
  title: z.string(),
  description: z.string().nullable(),
  category: z.string().nullable(),
  status: EventStatusSchema,
  visibility: EventVisibilitySchema,
  startDate: dateToISOString,
  endDate: dateToISOString,
  timezone: z.string(),
  locationType: EventLocationTypeSchema,
  physicalAddress: z.string().nullable(),
  virtualLink: z.string().nullable(),
  registrationCap: z.number().int().positive().nullable(),
  eventSeriesId: bigintToString.nullable(),
  isSeriesException: z.boolean(),
  seriesOccurrenceDate: dateToISOString.nullable(),
  qrCheckInToken: z.string().uuid(),
  isPaidEvent: z.boolean(),
  createdAt: dateToISOString,
  updatedAt: dateToISOString,
  _count: z.object({
    registrations: z.number()
  }).optional()
});
var EventWithRelationsDtoSchema = EventDtoSchema.extend({
  imageStorageItem: PublicStorageItemDtoSchema.nullable().optional(),
  audienceTarget: AudienceTargetWithRelationsDtoSchema.nullable().optional(),
  remainingCapacity: z.number().int().nullable().optional(),
  ticketTiers: EventTicketTierDtoSchema.array().optional()
});
var EventResponseDtoSchema = createResponseSchema(
  EventWithRelationsDtoSchema
);
var EventArrayResponseDtoSchema = createResponseSchema(
  EventDtoSchema.array()
);
var DonorProfileDtoSchema = z.object({
  uuid: z.string().uuid(),
  email: z.string().email(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  phoneNumber: z.string().nullable(),
  createdAt: dateToISOString,
  updatedAt: dateToISOString
});
var DonorProfileResponseDtoSchema = createResponseSchema(
  DonorProfileDtoSchema
);
var DonorProfileArrayResponseDtoSchema = createResponseSchema(
  DonorProfileDtoSchema.array()
);
var DonationPageDtoSchema = z.object({
  uuid: z.string().uuid(),
  slug: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  content: z.string().nullable(),
  currency: z.string(),
  minimumAmount: z.number().int(),
  allowCustomAmount: z.boolean(),
  allowGuestCheckout: z.boolean(),
  isPublished: z.boolean(),
  isPrimary: z.boolean(),
  headerStorageItem: PublicStorageItemDtoSchema.nullable().optional(),
  createdAt: dateToISOString,
  updatedAt: dateToISOString
});
var DonationPageSummaryDtoSchema = DonationPageDtoSchema.pick({
  uuid: true,
  title: true,
  slug: true
});
var DonationPageWithRelationsDtoSchema = DonationPageDtoSchema.extend({
  suggestedAmounts: z.array(
    z.object({
      uuid: z.string().uuid(),
      amount: z.number().int(),
      sortOrder: z.number().int()
    })
  ).optional()
});
var PublicDonationPageDtoSchema = DonationPageWithRelationsDtoSchema;
var DonationPageResponseDtoSchema = createResponseSchema(
  DonationPageWithRelationsDtoSchema
);
var DonationPageArrayResponseDtoSchema = createResponseSchema(
  DonationPageDtoSchema.array()
);
var RecurringDonationStatusSchema = z.enum([
  "ACTIVE",
  "PAUSED",
  "CANCELED",
  "FAILED"
]);
var RecurringDonationStatus = {
  ACTIVE: "ACTIVE",
  PAUSED: "PAUSED",
  CANCELED: "CANCELED",
  FAILED: "FAILED"
};

// src/donations/recurring-donation.dto.ts
var RecurringDonationDtoSchema = z.object({
  uuid: z.string().uuid(),
  amount: z.number().int(),
  currency: z.string(),
  status: RecurringDonationStatusSchema,
  lastChargedAt: dateToISOString.nullable(),
  startedAt: dateToISOString,
  pausedAt: dateToISOString.nullable(),
  canceledAt: dateToISOString.nullable(),
  failedAt: dateToISOString.nullable(),
  createdAt: dateToISOString,
  updatedAt: dateToISOString
});
var RecurringDonationResponseDtoSchema = createResponseSchema(
  RecurringDonationDtoSchema
);
var RecurringDonationArrayResponseDtoSchema = createResponseSchema(
  RecurringDonationDtoSchema.array()
);
var DonationReceiptDtoSchema = z.object({
  uuid: z.string().uuid(),
  receiptNumber: z.string(),
  currency: z.string(),
  amount: z.number().int(),
  issuedAt: dateToISOString,
  legalName: z.string().nullable(),
  legalAddress: z.string().nullable(),
  taxId: z.string().nullable(),
  snapshot: z.record(z.string(), z.unknown()).nullable(),
  resendCount: z.number().int(),
  lastSentAt: dateToISOString.nullable(),
  createdAt: dateToISOString,
  updatedAt: dateToISOString
});
var DonationReceiptResponseDtoSchema = createResponseSchema(
  DonationReceiptDtoSchema
);

// src/donations/donation.dto.ts
var DonationDtoSchema = z.object({
  uuid: z.string().uuid(),
  amount: z.number().int(),
  currency: z.string(),
  isGuest: z.boolean(),
  donatedAt: dateToISOString.nullable(),
  note: z.string().nullable(),
  createdAt: dateToISOString,
  updatedAt: dateToISOString
});
var DonationWithRelationsDtoSchema = DonationDtoSchema.extend({
  donorProfile: DonorProfileDtoSchema.optional(),
  donationPage: DonationPageSummaryDtoSchema.nullable().optional(),
  payment: PaymentDtoSchema.nullable().optional(),
  invoice: InvoiceDtoSchema.nullable().optional(),
  recurringDonation: RecurringDonationDtoSchema.nullable().optional(),
  receipt: DonationReceiptDtoSchema.nullable().optional()
});
var DonationResponseDtoSchema = createResponseSchema(
  DonationWithRelationsDtoSchema
);
var DonationArrayResponseDtoSchema = createResponseSchema(
  DonationDtoSchema.array()
);

// src/orders/order.dto.ts
var OrderStatus = /* @__PURE__ */ ((OrderStatus2) => {
  OrderStatus2["PENDING"] = "PENDING";
  OrderStatus2["PAID"] = "PAID";
  OrderStatus2["EXPIRED"] = "EXPIRED";
  OrderStatus2["VOID"] = "VOID";
  return OrderStatus2;
})(OrderStatus || {});
var OrderSourceType = /* @__PURE__ */ ((OrderSourceType2) => {
  OrderSourceType2["EVENT"] = "EVENT";
  OrderSourceType2["STORE_PURCHASE"] = "STORE_PURCHASE";
  OrderSourceType2["DONATION"] = "DONATION";
  return OrderSourceType2;
})(OrderSourceType || {});
var OrderDtoSchema = z.object({
  uuid: z.string(),
  status: z.nativeEnum(OrderStatus),
  sourceType: z.nativeEnum(OrderSourceType),
  // Convention: EVENT -> event.uuid, DONATION -> donation.uuid (once donation
  // checkout is built), STORE_PURCHASE -> null (no dedicated source entity).
  sourceId: z.string().nullable(),
  orderNumber: z.string(),
  currency: z.string(),
  expiresAt: dateToISOString.nullable(),
  subtotalAmount: nullableNumberCoerce,
  discountAmount: nullableNumberCoerce,
  taxAmount: nullableNumberCoerce,
  taxRateId: nullableBigintToString,
  totalAmount: nullableNumberCoerce,
  platformFeeAmount: nullableNumberCoerce,
  stripeFeeAmount: nullableNumberCoerce,
  memberPaysTotal: nullableNumberCoerce,
  billingName: z.string().nullable(),
  billingEmail: z.string().nullable(),
  billingAddress: z.string().nullable(),
  refundedAt: dateToISOString.nullable(),
  voidedAt: dateToISOString.nullable(),
  voidReason: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  createdAt: dateToISOString,
  updatedAt: dateToISOString
});
var OrderMembershipDtoSchema = OrganizationMembershipDtoSchema.pick({
  uuid: true,
  user: true
});
var OrderSourceEventDtoSchema = EventDtoSchema.pick({
  uuid: true,
  title: true,
  startDate: true,
  status: true
});
var OrderSourceDonationDtoSchema = DonationDtoSchema.extend({
  donationPage: DonationPageSummaryDtoSchema.pick({
    title: true
  }).nullable().optional()
});
var OrderWithRelationsDtoSchema = OrderDtoSchema.extend({
  lineItems: z.array(OrderLineItemDtoSchema),
  payments: z.array(PaymentDtoSchema),
  organization: OrganizationDtoSchema.nullable().optional(),
  membership: OrderMembershipDtoSchema.nullable(),
  donations: z.array(OrderSourceDonationDtoSchema).optional(),
  eventTickets: z.array(z.object({ event: OrderSourceEventDtoSchema })).optional(),
  pdfStorageItem: StorageItemDtoSchema.nullable().optional()
});
var OrderCheckoutItemDtoSchema = z.object({
  name: z.string().min(1, "Item name is required").max(255),
  description: z.string().max(500).optional(),
  quantity: z.number().int().positive("Quantity must be positive"),
  unitAmount: z.number().int().nonnegative("Unit amount must be non-negative")
});
var GuestOrderCheckoutDtoSchema = z.object({
  organizationId: z.string().uuid("Invalid organization ID"),
  email: z.string().email("Invalid email address"),
  lineItems: z.array(OrderCheckoutItemDtoSchema).min(1, "At least one line item is required"),
  returnUrl: z.string().url("Invalid return URL"),
  discountAmount: z.number().int().nonnegative().default(0)
});
var AuthOrderCheckoutDtoSchema = z.object({
  organizationId: z.string().uuid("Invalid organization ID"),
  lineItems: z.array(OrderCheckoutItemDtoSchema).min(1, "At least one line item is required"),
  returnUrl: z.string().url("Invalid return URL"),
  discountAmount: z.number().int().nonnegative().default(0)
});
var OrderCheckoutResultDtoSchema = z.object({
  order: OrderWithRelationsDtoSchema,
  clientSecret: z.string(),
  paymentIntentId: z.string(),
  stripeCustomerId: z.string(),
  customerSessionClientSecret: z.string().optional(),
  customerEphemeralKeySecret: z.string().optional()
});
var OrderCheckoutResponseDtoSchema = createResponseSchema(
  OrderCheckoutResultDtoSchema
);
var SavedPaymentMethodType = /* @__PURE__ */ ((SavedPaymentMethodType2) => {
  SavedPaymentMethodType2["CARD"] = "CARD";
  SavedPaymentMethodType2["US_BANK_ACCOUNT"] = "US_BANK_ACCOUNT";
  SavedPaymentMethodType2["SEPA_DEBIT"] = "SEPA_DEBIT";
  SavedPaymentMethodType2["OTHER"] = "OTHER";
  return SavedPaymentMethodType2;
})(SavedPaymentMethodType || {});
var SavedPaymentMethodStatus = /* @__PURE__ */ ((SavedPaymentMethodStatus2) => {
  SavedPaymentMethodStatus2["ACTIVE"] = "ACTIVE";
  SavedPaymentMethodStatus2["INACTIVE"] = "INACTIVE";
  SavedPaymentMethodStatus2["EXPIRED"] = "EXPIRED";
  SavedPaymentMethodStatus2["REQUIRES_ACTION"] = "REQUIRES_ACTION";
  return SavedPaymentMethodStatus2;
})(SavedPaymentMethodStatus || {});
var SavedPaymentMethodDtoSchema = z.object({
  uuid: z.string(),
  type: z.nativeEnum(SavedPaymentMethodType),
  status: z.nativeEnum(SavedPaymentMethodStatus),
  isDefault: z.boolean(),
  // Card-specific fields
  cardBrand: z.string().nullable(),
  cardLast4: z.string().nullable(),
  cardExpMonth: z.number().nullable(),
  cardExpYear: z.number().nullable(),
  // Billing details
  billingName: z.string().nullable(),
  billingEmail: z.string().nullable(),
  billingPhone: z.string().nullable(),
  billingAddressLine1: z.string().nullable(),
  billingAddressLine2: z.string().nullable(),
  billingCity: z.string().nullable(),
  billingState: z.string().nullable(),
  billingPostalCode: z.string().nullable(),
  billingCountry: z.string().nullable(),
  createdAt: dateToISOString,
  updatedAt: dateToISOString
});
var SetupIntentRequestSchema = z.object({
  setAsDefault: z.boolean().optional().default(false)
});
var SetupIntentResponseSchema = z.object({
  clientSecret: z.string(),
  setupIntentId: z.string()
});
var CreatePaymentIntentDtoSchema = z.object({
  savedPaymentMethodUuid: z.string().uuid().optional()
});
var PaymentIntentResultDtoSchema = z.object({
  clientSecret: z.string(),
  paymentIntentId: z.string(),
  amount: z.number(),
  subtotalAmount: z.number(),
  platformFeeAmount: z.number(),
  stripeFeeAmount: z.number(),
  currency: z.string(),
  stripeCustomerId: z.string(),
  // Lets Stripe Elements/PaymentSheet display the customer's saved payment methods
  customerSessionClientSecret: z.string(),
  customerEphemeralKeySecret: z.string()
});
var PaymentIntentResponseDtoSchema = createResponseSchema(
  PaymentIntentResultDtoSchema
);
var PayoutInterval = /* @__PURE__ */ ((PayoutInterval2) => {
  PayoutInterval2["DAILY"] = "daily";
  PayoutInterval2["WEEKLY"] = "weekly";
  PayoutInterval2["MONTHLY"] = "monthly";
  PayoutInterval2["MANUAL"] = "manual";
  return PayoutInterval2;
})(PayoutInterval || {});
var PayoutStatus = /* @__PURE__ */ ((PayoutStatus2) => {
  PayoutStatus2["PAID"] = "paid";
  PayoutStatus2["PENDING"] = "pending";
  PayoutStatus2["IN_TRANSIT"] = "in_transit";
  PayoutStatus2["CANCELED"] = "canceled";
  PayoutStatus2["FAILED"] = "failed";
  return PayoutStatus2;
})(PayoutStatus || {});
var PayoutMethod = /* @__PURE__ */ ((PayoutMethod2) => {
  PayoutMethod2["STANDARD"] = "standard";
  PayoutMethod2["INSTANT"] = "instant";
  return PayoutMethod2;
})(PayoutMethod || {});
var PayoutScheduleDtoSchema = z.object({
  interval: z.nativeEnum(PayoutInterval),
  delayDays: z.number(),
  monthlyAnchor: z.number().nullable(),
  weeklyAnchor: z.string().nullable()
});
var PayoutDtoSchema = z.object({
  id: z.string(),
  amount: z.number(),
  currency: z.string(),
  arrivalDate: dateToISOString,
  status: z.nativeEnum(PayoutStatus),
  type: z.string(),
  method: z.nativeEnum(PayoutMethod),
  bankAccountLast4: z.string().nullable(),
  bankName: z.string().nullable(),
  failureCode: z.string().nullable(),
  failureMessage: z.string().nullable(),
  created: dateToISOString
});
var BalanceTransactionDtoSchema = z.object({
  id: z.string(),
  amount: z.number(),
  fee: z.number(),
  net: z.number(),
  type: z.string(),
  source: z.string().nullable(),
  description: z.string().nullable(),
  created: dateToISOString,
  currency: z.string()
});
var GetPayoutsQuerySchema = z.object({
  startDate: z.string().datetime({ message: "startDate must be a valid ISO 8601 datetime" }).optional(),
  endDate: z.string().datetime({ message: "endDate must be a valid ISO 8601 datetime" }).optional(),
  limit: z.coerce.number().optional().default(100)
});
var StartStripeConnectSchema = z.object({
  returnUrl: z.string().url("Return URL must be a valid URL"),
  refreshUrl: z.string().url("Refresh URL must be a valid URL")
});
var StripeOnboardingUrlSchema = z.object({
  onboardingUrl: StripeUrlSchema
});
var StripeAccountRequirementsSchema = z.object({
  currentlyDue: z.array(z.string()),
  eventuallyDue: z.array(z.string()),
  pastDue: z.array(z.string()),
  pendingVerification: z.array(z.string()),
  disabledReason: z.string().nullable()
});
var StripeAccountStatusSchema = z.object({
  accountId: z.string().nullable(),
  status: z.nativeEnum(StripeAccountStatus).nullable(),
  chargesEnabled: z.boolean(),
  payoutsEnabled: z.boolean(),
  onboardingComplete: z.boolean(),
  detailsSubmitted: z.boolean(),
  requirements: StripeAccountRequirementsSchema.nullable()
});
var EmailLineItemDtoSchema = z.object({
  name: z.string(),
  quantity: z.number().int().positive(),
  unitAmount: z.number().optional(),
  // In dollars (not cents)
  subtotalAmount: z.number()
  // In dollars (not cents)
});
var PaymentReceivedEmailDtoSchema = z.object({
  invoiceNumber: z.string(),
  subtotalAmount: z.number(),
  // Amount in dollars (not cents), before tax
  taxAmount: z.number(),
  // Amount in dollars (not cents); always shown, even $0.00
  discountAmount: z.number().optional(),
  // Amount in dollars (not cents); omitted from display when falsy
  platformFeeAmount: z.number().optional(),
  // Mimber platform fee in dollars (not cents)
  processorFeeAmount: z.number().optional(),
  // Payment processor (e.g. Stripe) fee in dollars (not cents)
  lineItems: z.array(EmailLineItemDtoSchema).optional(),
  // Itemized line items for the full price breakdown
  amountPaid: z.number(),
  // Amount in dollars (not cents) — total (subtotal + tax)
  currency: z.string(),
  // Currency code (e.g., "USD", "EUR")
  paidAt: z.coerce.date()
  // Coerce to support BullMQ JSON serialization (ISO strings)
});
var PaymentFailedEmailDtoSchema = z.object({
  invoiceNumber: z.string(),
  subtotalAmount: z.number(),
  // Amount in dollars (not cents), before tax
  taxAmount: z.number(),
  // Amount in dollars (not cents); always shown, even $0.00
  discountAmount: z.number().optional(),
  // Amount in dollars (not cents); omitted from display when falsy
  platformFeeAmount: z.number().optional(),
  // Mimber platform fee in dollars (not cents)
  processorFeeAmount: z.number().optional(),
  // Payment processor (e.g. Stripe) fee in dollars (not cents)
  lineItems: z.array(EmailLineItemDtoSchema).optional(),
  // Itemized line items for the full price breakdown
  amountDue: z.number(),
  // Amount in dollars (not cents) — total (subtotal + tax)
  currency: z.string(),
  // Currency code (e.g., "USD", "EUR")
  attemptCount: z.number(),
  nextAttempt: z.coerce.date().nullable(),
  // Coerce to support BullMQ JSON serialization (ISO strings)
  failureReason: z.string(),
  isLastAttempt: z.boolean(),
  recipientName: z.string().optional(),
  organizationName: z.string().optional()
});
var InvoiceIssuedEmailDtoSchema = z.object({
  recipientName: z.string(),
  invoiceNumber: z.string(),
  subtotalAmount: z.number(),
  // Amount in dollars (not cents), before tax
  taxAmount: z.number(),
  // Amount in dollars (not cents); always shown, even $0.00
  amount: z.number(),
  // Amount in dollars (not cents) — total (subtotal + tax)
  discountAmount: z.number().optional(),
  // Amount in dollars (not cents); omitted from display when falsy
  platformFeeAmount: z.number().optional(),
  // Mimber platform fee in dollars (not cents)
  processorFeeAmount: z.number().optional(),
  // Payment processor (e.g. Stripe) fee in dollars (not cents)
  lineItems: z.array(EmailLineItemDtoSchema).optional(),
  // Itemized line items for the full price breakdown
  currency: z.string().default("USD"),
  dueAt: z.coerce.date().optional(),
  // Coerce to support BullMQ JSON serialization; invoices without a due date omit this
  organizationName: z.string(),
  paymentUrl: z.string().url(),
  invoiceUrl: z.string().url()
});
var GroupDuesInvoiceIssuedEmailDtoSchema = z.object({
  recipientName: z.string(),
  groupName: z.string(),
  invoiceNumber: z.string(),
  subtotalAmount: z.number(),
  // Amount in dollars (not cents), before tax
  taxAmount: z.number(),
  // Amount in dollars (not cents); always shown, even $0.00
  amount: z.number(),
  // Amount in dollars (not cents) — total (subtotal + tax)
  lineItems: z.array(EmailLineItemDtoSchema).optional(),
  currency: z.string().default("USD"),
  dueAt: z.coerce.date().optional(),
  organizationName: z.string(),
  paymentUrl: z.url(),
  invoiceUrl: z.url()
});
var InvoicePaidEmailDtoSchema = z.object({
  recipientName: z.string(),
  invoiceNumber: z.string(),
  amount: z.number(),
  // Amount in dollars (not cents)
  subtotalAmount: z.number().optional(),
  // Amount in dollars (not cents), before tax
  taxAmount: z.number().optional(),
  // Amount in dollars (not cents); shown whenever provided, even $0.00
  discountAmount: z.number().optional(),
  // Amount in dollars (not cents); omitted from display when falsy
  platformFeeAmount: z.number().optional(),
  // Mimber platform fee in dollars (not cents)
  processorFeeAmount: z.number().optional(),
  // Payment processor (e.g. Stripe) fee in dollars (not cents)
  lineItems: z.array(EmailLineItemDtoSchema).optional(),
  // Itemized line items for the full price breakdown
  currency: z.string().default("USD"),
  paidAt: z.coerce.date(),
  // Coerce to support BullMQ JSON serialization
  organizationName: z.string(),
  invoiceUrl: z.string().url()
});
var InvoiceVoidedEmailDtoSchema = z.object({
  recipientName: z.string(),
  invoiceNumber: z.string(),
  amount: z.number(),
  // Amount in dollars (not cents)
  subtotalAmount: z.number().optional(),
  // Amount in dollars (not cents), before tax
  taxAmount: z.number().optional(),
  // Amount in dollars (not cents); shown whenever provided, even $0.00
  discountAmount: z.number().optional(),
  // Amount in dollars (not cents); omitted from display when falsy
  platformFeeAmount: z.number().optional(),
  // Mimber platform fee in dollars (not cents)
  processorFeeAmount: z.number().optional(),
  // Payment processor (e.g. Stripe) fee in dollars (not cents)
  lineItems: z.array(EmailLineItemDtoSchema).optional(),
  // Itemized line items for the full price breakdown
  currency: z.string().default("USD"),
  voidedAt: z.coerce.date(),
  // Coerce to support BullMQ JSON serialization
  organizationName: z.string(),
  voidReason: z.string().optional(),
  invoiceUrl: z.string().url()
});
var InvoiceOverdueEmailDtoSchema = z.object({
  recipientName: z.string(),
  invoiceNumber: z.string(),
  subtotalAmount: z.number(),
  // Amount in dollars (not cents), before tax
  taxAmount: z.number(),
  // Amount in dollars (not cents); always shown, even $0.00
  amount: z.number(),
  // Amount in dollars (not cents) — total (subtotal + tax)
  discountAmount: z.number().optional(),
  // Amount in dollars (not cents); omitted from display when falsy
  platformFeeAmount: z.number().optional(),
  // Mimber platform fee in dollars (not cents)
  processorFeeAmount: z.number().optional(),
  // Payment processor (e.g. Stripe) fee in dollars (not cents)
  lineItems: z.array(EmailLineItemDtoSchema).optional(),
  // Itemized line items for the full price breakdown
  currency: z.string().default("USD"),
  dueAt: z.coerce.date(),
  // Original due date
  organizationName: z.string(),
  paymentUrl: z.string().url(),
  daysOverdue: z.number(),
  // Number of days past due
  invoiceUrl: z.string().url()
});
var InvoiceExpiringEmailDtoSchema = z.object({
  recipientName: z.string(),
  invoiceNumber: z.string(),
  subtotalAmount: z.number(),
  // Amount in dollars (not cents), before tax
  taxAmount: z.number(),
  // Amount in dollars (not cents); always shown, even $0.00
  amount: z.number(),
  // Amount in dollars (not cents) — total (subtotal + tax)
  discountAmount: z.number().optional(),
  // Amount in dollars (not cents); omitted from display when falsy
  platformFeeAmount: z.number().optional(),
  // Mimber platform fee in dollars (not cents)
  processorFeeAmount: z.number().optional(),
  // Payment processor (e.g. Stripe) fee in dollars (not cents)
  lineItems: z.array(EmailLineItemDtoSchema).optional(),
  // Itemized line items for the full price breakdown
  currency: z.string().default("USD"),
  expiresAt: z.coerce.date(),
  // Expiration date
  organizationName: z.string(),
  paymentUrl: z.string().url(),
  hoursUntilExpiration: z.number()
  // Hours remaining until expiration
});
var InvoicePdfReadyEmailDtoSchema = z.object({
  recipientName: z.string(),
  invoiceNumber: z.string(),
  organizationName: z.string(),
  invoiceUrl: z.string().url()
});
var OrderReceiptEmailDtoSchema = z.object({
  recipientName: z.string(),
  organizationName: z.string(),
  orderNumber: z.string(),
  lineItems: z.array(EmailLineItemDtoSchema),
  subtotalAmount: z.number(),
  // Order-level subtotal in dollars (not cents), before tax
  discountAmount: z.number().optional(),
  // In dollars (not cents); omitted from display when falsy
  taxAmount: z.number(),
  // In dollars (not cents); always shown, even $0.00
  platformFeeAmount: z.number().optional(),
  // Mimber platform fee in dollars (not cents)
  processorFeeAmount: z.number().optional(),
  // Payment processor (e.g. Stripe) fee in dollars (not cents)
  totalAmount: z.number(),
  // In dollars (not cents)
  currency: z.string().default("USD"),
  purchaseDate: z.string(),
  receiptPdfUrl: z.string().url()
});
var RefundProcessedEmailDtoSchema = z.object({
  recipientName: z.string(),
  invoiceNumber: z.string(),
  refundAmount: z.number(),
  // Refund amount in dollars (not cents)
  originalAmount: z.number(),
  // Original invoice amount in dollars
  subtotalAmount: z.number().optional(),
  // Original subtotal in dollars (not cents), before tax
  taxAmount: z.number().optional(),
  // Original tax amount in dollars (not cents); shown whenever provided, even $0.00
  platformFeeAmount: z.number().optional(),
  // Mimber platform fee in dollars (not cents)
  processorFeeAmount: z.number().optional(),
  // Payment processor (e.g. Stripe) fee in dollars (not cents)
  lineItems: z.array(EmailLineItemDtoSchema).optional(),
  // Itemized line items for the original invoice
  currency: z.string().default("USD"),
  refundedAt: z.coerce.date(),
  // Coerce to support BullMQ JSON serialization
  organizationName: z.string()
});
var InvitationAcceptedAdminAlertEmailDtoSchema = z.object({
  memberName: z.string(),
  memberEmail: z.email(),
  organizationName: z.string(),
  tierName: z.string().nullable(),
  membershipUrl: z.url()
});
var DuesUpcomingRenewalEmailDtoSchema = z.object({
  recipientName: z.string(),
  organizationName: z.string(),
  tierName: z.string(),
  amountDue: z.number(),
  subtotalAmount: z.number().optional(),
  // Amount in dollars (not cents), before tax
  taxAmount: z.number().optional(),
  // Amount in dollars (not cents); shown whenever provided, even $0.00
  discountAmount: z.number().optional(),
  // Amount in dollars (not cents); omitted from display when falsy
  platformFeeAmount: z.number().optional(),
  // Mimber platform fee in dollars (not cents)
  processorFeeAmount: z.number().optional(),
  // Payment processor (e.g. Stripe) fee in dollars (not cents)
  lineItems: z.array(EmailLineItemDtoSchema).optional(),
  // Itemized line items for the full price breakdown
  currency: z.string(),
  dueDate: z.coerce.date(),
  daysUntilDue: z.number(),
  isAutoPayEnabled: z.boolean(),
  paymentMethodLast4: z.string().nullable().optional(),
  paymentMethodBrand: z.string().nullable().optional(),
  payNowUrl: z.string().optional()
});
var DuesAutoPayEnrolledEmailDtoSchema = z.object({
  recipientName: z.string(),
  organizationName: z.string(),
  tierName: z.string(),
  amountDue: z.number(),
  currency: z.string(),
  paymentMethodLast4: z.string(),
  paymentMethodBrand: z.string(),
  nextBillingDate: z.coerce.date(),
  billingInterval: z.string()
});
var DuesMembershipSuspendedEmailDtoSchema = z.object({
  recipientName: z.string(),
  organizationName: z.string(),
  tierName: z.string(),
  amountDue: z.number(),
  currency: z.string(),
  failedAttemptCount: z.number(),
  lastFailureReason: z.string(),
  updatePaymentMethodUrl: z.string().optional(),
  payNowUrl: z.string().optional()
});
var SecurityPasswordChangedEmailDtoSchema = z.object({
  userName: z.string(),
  changedAt: z.string()
});
var SecurityEmailChangedEmailDtoSchema = z.object({
  userName: z.string(),
  previousEmail: z.string().email().optional(),
  newEmail: z.string().email().optional(),
  changedAt: z.string()
});
var SecurityMfaEnabledEmailDtoSchema = z.object({
  userName: z.string(),
  mfaType: z.enum(["TOTP", "SMS"]),
  changedAt: z.string()
});
var SecurityMfaDisabledEmailDtoSchema = z.object({
  userName: z.string(),
  mfaType: z.enum(["TOTP", "SMS"]),
  changedAt: z.string()
});
var SecurityAccountDeactivatedEmailDtoSchema = z.object({
  userName: z.string(),
  deactivatedAt: z.string()
});
var SecurityDeletionScheduledEmailDtoSchema = z.object({
  userName: z.string(),
  deletionDate: z.string()
});
var SecurityDeletionCancelledEmailDtoSchema = z.object({
  userName: z.string(),
  cancelledAt: z.string()
});
var SecuritySocialProviderChangedEmailDtoSchema = z.object({
  userName: z.string(),
  provider: z.string(),
  action: z.enum(["linked", "unlinked"]),
  changedAt: z.string()
});
var CommunicationChannel = /* @__PURE__ */ ((CommunicationChannel2) => {
  CommunicationChannel2["EMAIL"] = "EMAIL";
  CommunicationChannel2["SMS"] = "SMS";
  CommunicationChannel2["PUSH"] = "PUSH";
  return CommunicationChannel2;
})(CommunicationChannel || {});
var CommunicationTemplateStatus = /* @__PURE__ */ ((CommunicationTemplateStatus2) => {
  CommunicationTemplateStatus2["DRAFT"] = "DRAFT";
  CommunicationTemplateStatus2["ACTIVE"] = "ACTIVE";
  CommunicationTemplateStatus2["ARCHIVED"] = "ARCHIVED";
  return CommunicationTemplateStatus2;
})(CommunicationTemplateStatus || {});
var CommunicationTemplateDtoSchema = z.object({
  uuid: z.string().uuid(),
  organizationId: bigintToString,
  name: z.string(),
  subject: z.string(),
  content: z.string(),
  channel: z.nativeEnum(CommunicationChannel),
  status: z.nativeEnum(CommunicationTemplateStatus),
  createdAt: dateToISOString,
  updatedAt: dateToISOString
});
var CommunicationTemplateResponseDtoSchema = createResponseSchema(
  CommunicationTemplateDtoSchema
);
var CommunicationTemplateArrayResponseDtoSchema = createResponseSchema(
  CommunicationTemplateDtoSchema.array()
);

// src/announcements/announcement.dto.ts
var AnnouncementStatus = /* @__PURE__ */ ((AnnouncementStatus2) => {
  AnnouncementStatus2["DRAFT"] = "DRAFT";
  AnnouncementStatus2["SCHEDULED"] = "SCHEDULED";
  AnnouncementStatus2["PUBLISHED"] = "PUBLISHED";
  return AnnouncementStatus2;
})(AnnouncementStatus || {});
var AnnouncementPriority = /* @__PURE__ */ ((AnnouncementPriority2) => {
  AnnouncementPriority2["NORMAL"] = "NORMAL";
  AnnouncementPriority2["IMPORTANT"] = "IMPORTANT";
  AnnouncementPriority2["URGENT"] = "URGENT";
  return AnnouncementPriority2;
})(AnnouncementPriority || {});
var AnnouncementAuthorDtoSchema = BaseMembershipDtoSchema.pick({
  uuid: true
}).extend({
  user: UserObjectDtoSchema.pick({ firstName: true, lastName: true })
});
var AnnouncementDtoSchema = z.object({
  uuid: z.uuid(),
  organizationId: bigintToString,
  status: z.enum(AnnouncementStatus),
  priority: z.enum(AnnouncementPriority),
  isPinned: z.boolean(),
  scheduledPublishAt: nullableDateToISOString,
  publishedAt: nullableDateToISOString,
  expiresAt: nullableDateToISOString,
  createdAt: dateToISOString,
  updatedAt: dateToISOString
});
var AnnouncementWithRelationsDtoSchema = AnnouncementDtoSchema.extend({
  emailTemplate: CommunicationTemplateDtoSchema.nullable().optional(),
  smsTemplate: CommunicationTemplateDtoSchema.nullable().optional(),
  pushTemplate: CommunicationTemplateDtoSchema.nullable().optional(),
  author: AnnouncementAuthorDtoSchema.optional(),
  audienceTarget: AudienceTargetWithRelationsDtoSchema.nullable().optional()
});
var AnnouncementPublicTemplateDtoSchema = CommunicationTemplateDtoSchema.pick({
  subject: true,
  content: true
});
var PublicAnnouncementDtoSchema = AnnouncementDtoSchema.omit({
  organizationId: true
}).extend({
  title: z.string(),
  content: z.string()
});
var PublicAnnouncementSourceDtoSchema = AnnouncementDtoSchema.omit({
  organizationId: true
}).extend({
  emailTemplate: AnnouncementPublicTemplateDtoSchema.nullable().optional(),
  smsTemplate: AnnouncementPublicTemplateDtoSchema.nullable().optional(),
  pushTemplate: AnnouncementPublicTemplateDtoSchema.nullable().optional()
});
var PublicAnnouncementTransformDtoSchema = PublicAnnouncementSourceDtoSchema.transform(
  ({ emailTemplate, smsTemplate, pushTemplate, ...announcement }) => {
    const activeTemplate = emailTemplate ?? pushTemplate ?? smsTemplate;
    return PublicAnnouncementDtoSchema.parse({
      ...announcement,
      title: (activeTemplate == null ? void 0 : activeTemplate.subject) ?? "Announcement",
      content: (activeTemplate == null ? void 0 : activeTemplate.content) ?? ""
    });
  }
);
var AnnouncementResponseDtoSchema = createResponseSchema(
  AnnouncementDtoSchema
);
var AnnouncementWithRelationsResponseDtoSchema = createResponseSchema(
  AnnouncementWithRelationsDtoSchema
);
var AnnouncementArrayResponseDtoSchema = createResponseSchema(
  AnnouncementWithRelationsDtoSchema.array()
);
var PublicAnnouncementResponseDtoSchema = createResponseSchema(
  PublicAnnouncementDtoSchema
);
var PublicAnnouncementArrayResponseDtoSchema = createArrayResponseSchema(
  PublicAnnouncementDtoSchema
);
var CreateAnnouncementDtoSchema = z.object({
  priority: z.nativeEnum(AnnouncementPriority).default("NORMAL" /* NORMAL */),
  isPinned: z.boolean().default(false),
  targetType: z.nativeEnum(AudienceTargetType).default(AudienceTargetType.ALL),
  emailTemplateId: z.string().uuid().nullable().optional(),
  smsTemplateId: z.string().uuid().nullable().optional(),
  pushTemplateId: z.string().uuid().nullable().optional(),
  tierTargetIds: z.array(z.string().uuid()).optional(),
  groupTargetIds: z.array(z.string().uuid()).optional(),
  roleTargetIds: z.array(z.string().uuid()).optional(),
  memberTargetIds: z.array(z.string().uuid()).optional()
}).strict().refine(
  (data) => {
    var _a, _b, _c, _d;
    if (data.targetType === AudienceTargetType.TARGETED) {
      const hasTiers = (((_a = data.tierTargetIds) == null ? void 0 : _a.length) ?? 0) > 0;
      const hasGroups = (((_b = data.groupTargetIds) == null ? void 0 : _b.length) ?? 0) > 0;
      const hasRoles = (((_c = data.roleTargetIds) == null ? void 0 : _c.length) ?? 0) > 0;
      const hasMembers = (((_d = data.memberTargetIds) == null ? void 0 : _d.length) ?? 0) > 0;
      return hasTiers || hasGroups || hasRoles || hasMembers;
    }
    return true;
  },
  {
    message: "At least one target (tier, group, role, or member) is required when targetType is TARGETED",
    path: ["targetType"]
  }
);
var UpdateAnnouncementDtoSchema = z.object({
  priority: z.nativeEnum(AnnouncementPriority).optional(),
  isPinned: z.boolean().optional(),
  targetType: z.nativeEnum(AudienceTargetType).optional(),
  emailTemplateId: z.string().uuid().nullable().optional(),
  smsTemplateId: z.string().uuid().nullable().optional(),
  pushTemplateId: z.string().uuid().nullable().optional(),
  scheduledPublishAt: z.string().datetime().nullable().optional(),
  expiresAt: z.string().datetime().nullable().optional(),
  tierTargetIds: z.array(z.string().uuid()).optional(),
  groupTargetIds: z.array(z.string().uuid()).optional(),
  roleTargetIds: z.array(z.string().uuid()).optional(),
  memberTargetIds: z.array(z.string().uuid()).optional()
}).strict();

// src/announcements/query-announcement.dto.ts
var ALLOWED_ANNOUNCEMENT_FIELDS = [
  "uuid",
  "status",
  "priority",
  "isPinned",
  "targetType",
  "scheduledPublishAt",
  "publishedAt",
  "expiresAt",
  "createdAt",
  "updatedAt"
];
var QueryAnnouncementSchema = createValidatedQuerySchema([
  ...ALLOWED_ANNOUNCEMENT_FIELDS
]);
var ScheduleAnnouncementDtoSchema = z.object({
  scheduledPublishAt: z.string().datetime()
}).strict();
var PublishAnnouncementDtoSchema = z.object({
  sendNow: z.boolean().default(true)
}).strict().default({ sendNow: true });

// src/emails/announcement-published-email.dto.ts
var AnnouncementPublishedEmailDtoSchema = z.object({
  recipientName: z.string(),
  organizationName: z.string(),
  announcementSubject: z.string(),
  announcementHtmlContent: z.string(),
  priority: z.nativeEnum(AnnouncementPriority)
});
var RecurrenceConfigSchema = z.object({
  intervalLength: z.number().int().min(1),
  intervalType: z.nativeEnum(RecurrenceInterval),
  daysOfWeek: z.array(z.nativeEnum(DayOfWeek)).min(1).optional(),
  dayOfMonth: z.number().int().min(1).max(31).optional(),
  weekIndex: z.nativeEnum(WeekIndex).optional(),
  monthOfYear: z.number().int().min(1).max(12).optional(),
  endsAt: z.string().datetime().optional(),
  maxOccurrences: z.number().int().min(1).optional()
}).strict();
var UpsertScheduledActionDtoSchema = z.object({
  scheduledFor: z.string().datetime(),
  isRecurring: z.boolean().default(false),
  intervalLength: z.number().int().min(1).optional(),
  intervalType: z.nativeEnum(RecurrenceInterval).optional(),
  endsAt: z.string().datetime().optional(),
  maxOccurrences: z.number().int().min(1).optional(),
  // Calendar-grade recurrence fields
  daysOfWeek: z.array(z.nativeEnum(DayOfWeek)).min(1).optional(),
  dayOfMonth: z.number().int().min(1).max(31).optional(),
  weekIndex: z.nativeEnum(WeekIndex).optional(),
  monthOfYear: z.number().int().min(1).max(12).optional()
}).strict().refine((data) => !data.isRecurring || data.intervalLength !== void 0, {
  message: "Interval length is required when recurring",
  path: ["intervalLength"]
}).refine((data) => !data.isRecurring || data.intervalType !== void 0, {
  message: "Interval type is required when recurring",
  path: ["intervalType"]
}).transform((data) => ({
  ...data,
  // Strip recurrence-specific fields when isRecurring is false so callers
  // never receive stale recurrence data for a one-time schedule.
  intervalLength: data.isRecurring ? data.intervalLength : void 0,
  intervalType: data.isRecurring ? data.intervalType : void 0,
  endsAt: data.isRecurring ? data.endsAt : void 0,
  maxOccurrences: data.isRecurring ? data.maxOccurrences : void 0,
  daysOfWeek: data.isRecurring ? data.daysOfWeek : void 0,
  dayOfMonth: data.isRecurring ? data.dayOfMonth : void 0,
  weekIndex: data.isRecurring ? data.weekIndex : void 0,
  monthOfYear: data.isRecurring ? data.monthOfYear : void 0
}));

// src/events/create-event.dto.ts
var CreateEventDtoSchema = z.object({
  title: z.string().min(1).max(255),
  description: z.string().optional(),
  category: z.string().max(100).optional(),
  status: EventStatusSchema.optional().default("DRAFT"),
  visibility: EventVisibilitySchema.optional().default("MEMBERS_ONLY"),
  startDate: z.string().datetime(),
  endDate: z.string().datetime(),
  timezone: z.string().min(1).max(50),
  locationType: EventLocationTypeSchema,
  physicalAddress: z.preprocess(
    (val) => val === "" || val === null ? void 0 : val,
    z.string().optional()
  ),
  virtualLink: z.preprocess(
    (val) => val === "" || val === null ? void 0 : val,
    z.string().url().optional()
  ),
  targetType: z.nativeEnum(AudienceTargetType).optional().default(AudienceTargetType.ALL),
  tierTargetIds: z.array(z.string().uuid()).optional(),
  groupTargetIds: z.array(z.string().uuid()).optional(),
  roleTargetIds: z.array(z.string().uuid()).optional(),
  memberTargetIds: z.array(z.string().uuid()).optional(),
  registrationCap: z.number().int().positive().optional(),
  isPaidEvent: z.boolean().optional().default(false),
  recurrence: RecurrenceConfigSchema.optional()
}).strict().refine((data) => new Date(data.endDate) > new Date(data.startDate), {
  message: "End date must be after start date",
  path: ["endDate"]
}).refine(
  (data) => {
    if (data.locationType === "PHYSICAL" || data.locationType === "HYBRID") {
      return !!data.physicalAddress;
    }
    return true;
  },
  {
    message: "Physical address is required for physical or hybrid events",
    path: ["physicalAddress"]
  }
).refine(
  (data) => {
    if (data.locationType === "VIRTUAL" || data.locationType === "HYBRID") {
      return !!data.virtualLink;
    }
    return true;
  },
  {
    message: "Virtual link is required for virtual or hybrid events",
    path: ["virtualLink"]
  }
).refine(
  (data) => {
    var _a, _b, _c, _d;
    if (data.targetType === AudienceTargetType.TARGETED) {
      const hasTiers = (((_a = data.tierTargetIds) == null ? void 0 : _a.length) ?? 0) > 0;
      const hasGroups = (((_b = data.groupTargetIds) == null ? void 0 : _b.length) ?? 0) > 0;
      const hasRoles = (((_c = data.roleTargetIds) == null ? void 0 : _c.length) ?? 0) > 0;
      const hasMembers = (((_d = data.memberTargetIds) == null ? void 0 : _d.length) ?? 0) > 0;
      return hasTiers || hasGroups || hasRoles || hasMembers;
    }
    return true;
  },
  {
    message: "At least one target (tier, group, role, or member) is required when targetType is TARGETED",
    path: ["targetType"]
  }
);
var EventEditScopeSchema = z.enum([
  "THIS_EVENT",
  "THIS_AND_FUTURE",
  "ALL_EVENTS"
]);
var EventEditScope = {
  THIS_EVENT: "THIS_EVENT",
  THIS_AND_FUTURE: "THIS_AND_FUTURE",
  ALL_EVENTS: "ALL_EVENTS"
};
var EVENT_EDIT_SCOPE_DISPLAY = {
  THIS_EVENT: {
    label: "This event",
    description: "Only this occurrence will be affected."
  },
  THIS_AND_FUTURE: {
    label: "This and following events",
    description: "This occurrence and all future occurrences will be affected."
  },
  ALL_EVENTS: {
    label: "All events",
    description: "Every occurrence in this series will be affected."
  }
};

// src/events/update-event.dto.ts
var UpdateEventDtoSchema = z.object({
  title: z.string().min(1).max(255).optional(),
  description: z.string().nullable().optional(),
  category: z.string().max(100).nullable().optional(),
  status: EventStatusSchema.optional(),
  visibility: EventVisibilitySchema.optional(),
  startDate: z.string().datetime().optional(),
  endDate: z.string().datetime().optional(),
  timezone: z.string().min(1).max(50).optional(),
  locationType: EventLocationTypeSchema.optional(),
  physicalAddress: z.preprocess(
    (val) => val === "" ? null : val,
    z.string().nullable().optional()
  ),
  virtualLink: z.preprocess(
    (val) => val === "" ? null : val,
    z.string().url().nullable().optional()
  ),
  targetType: z.nativeEnum(AudienceTargetType).optional(),
  tierTargetIds: z.array(z.string().uuid()).optional(),
  groupTargetIds: z.array(z.string().uuid()).optional(),
  roleTargetIds: z.array(z.string().uuid()).optional(),
  memberTargetIds: z.array(z.string().uuid()).optional(),
  registrationCap: z.number().int().positive().nullable().optional(),
  isPaidEvent: z.boolean().optional(),
  editScope: EventEditScopeSchema.optional()
}).strict().refine(
  (data) => {
    if (data.startDate && data.endDate) {
      return new Date(data.endDate) > new Date(data.startDate);
    }
    return true;
  },
  { message: "End date must be after start date", path: ["endDate"] }
).refine(
  (data) => {
    if (data.locationType === "PHYSICAL" || data.locationType === "HYBRID") {
      return data.physicalAddress !== void 0 ? !!data.physicalAddress : true;
    }
    return true;
  },
  {
    message: "Physical address is required when changing to physical or hybrid",
    path: ["physicalAddress"]
  }
).refine(
  (data) => {
    if (data.locationType === "VIRTUAL" || data.locationType === "HYBRID") {
      return data.virtualLink !== void 0 ? !!data.virtualLink : true;
    }
    return true;
  },
  {
    message: "Virtual link is required when changing to virtual or hybrid",
    path: ["virtualLink"]
  }
).refine(
  (data) => {
    var _a, _b, _c, _d;
    if (data.targetType === AudienceTargetType.TARGETED) {
      const hasTiers = (((_a = data.tierTargetIds) == null ? void 0 : _a.length) ?? 0) > 0;
      const hasGroups = (((_b = data.groupTargetIds) == null ? void 0 : _b.length) ?? 0) > 0;
      const hasRoles = (((_c = data.roleTargetIds) == null ? void 0 : _c.length) ?? 0) > 0;
      const hasMembers = (((_d = data.memberTargetIds) == null ? void 0 : _d.length) ?? 0) > 0;
      return hasTiers || hasGroups || hasRoles || hasMembers;
    }
    return true;
  },
  {
    message: "At least one target (tier, group, role, or member) is required when targetType is TARGETED",
    path: ["targetType"]
  }
);

// src/events/query-event.dto.ts
var SEARCH_EVENT_FIELDS = [
  "title",
  "description",
  "category"
];
var ALLOWED_EVENT_FIELDS = [
  "uuid",
  "title",
  "description",
  "category",
  "status",
  "visibility",
  "startDate",
  "endDate",
  "locationType",
  "createdAt",
  "updatedAt"
];
var QueryEventSchema = createValidatedQuerySchema([
  ...ALLOWED_EVENT_FIELDS
]);
var EventRegistrationStatusSchema = z.enum(["REGISTERED", "CANCELLED"]);
var EVENT_REGISTRATION_STATUS_DISPLAY = {
  REGISTERED: { label: "Registered", color: "green" },
  CANCELLED: { label: "Cancelled", color: "red" }
};
var EventRegistrationDtoSchema = z.object({
  uuid: z.string().uuid(),
  eventId: bigintToString,
  membershipId: bigintToString,
  status: EventRegistrationStatusSchema,
  createdAt: dateToISOString,
  updatedAt: dateToISOString
});
var EventMembershipSnapshotDtoSchema = BaseMembershipDtoSchema.pick({
  uuid: true
}).extend({
  user: UserObjectDtoSchema.pick({
    firstName: true,
    lastName: true,
    email: true
  })
});
var EventRegistrationWithMemberDtoSchema = EventRegistrationDtoSchema.extend({
  membership: EventMembershipSnapshotDtoSchema
});
var EventRegistrationResponseDtoSchema = createResponseSchema(
  EventRegistrationDtoSchema
);
var EventRegistrationArrayResponseDtoSchema = createResponseSchema(
  EventRegistrationWithMemberDtoSchema.array()
);

// src/events/query-event-registration.dto.ts
var ALLOWED_EVENT_REGISTRATION_FIELDS = [
  "uuid",
  "status",
  "createdAt",
  "updatedAt",
  "membership.user.firstName",
  "membership.user.lastName",
  "membership.user.email"
];
var SEARCH_EVENT_REGISTRATION_FIELDS = [
  "membership.user.firstName",
  "membership.user.lastName",
  "membership.user.email"
];
var QueryEventRegistrationSchema = createValidatedQuerySchema([
  ...ALLOWED_EVENT_REGISTRATION_FIELDS
]);
var PublishEventDtoSchema = z.object({}).strict().default({});
var CreateEventRegistrationDtoSchema = z.object({
  membershipUuid: z.string().uuid({ message: "Please select a member" })
}).strict();
var CancelEventDtoSchema = z.object({
  reason: z.string().max(500).optional(),
  scope: EventEditScopeSchema.optional()
}).strict();
var DeleteEventDtoSchema = z.object({
  scope: EventEditScopeSchema.optional()
}).strict();
var AttendanceStatusSchema = z.enum(["CHECKED_IN", "NO_SHOW"]);
var ATTENDANCE_STATUS_DISPLAY = {
  CHECKED_IN: { label: "Checked In", color: "green" },
  NO_SHOW: { label: "No Show", color: "red" }
};
var CheckInMethodSchema = z.enum([
  "QR_REGISTRATION",
  "QR_EVENT",
  "QR_TICKET",
  "MANUAL"
]);
var CHECK_IN_METHOD_DISPLAY = {
  QR_REGISTRATION: { label: "QR (Registration)", color: "blue" },
  QR_EVENT: { label: "QR (Event)", color: "cyan" },
  QR_TICKET: { label: "QR (Ticket)", color: "purple" },
  MANUAL: { label: "Manual", color: "default" }
};

// src/events/attendance/event-attendance.dto.ts
var EventAttendanceDtoSchema = z.object({
  uuid: z.string().uuid(),
  eventId: bigintToString,
  membershipId: bigintToString,
  status: AttendanceStatusSchema,
  checkInMethod: CheckInMethodSchema.nullable(),
  checkInTime: nullableDateToISOString,
  isGuest: z.boolean(),
  guestName: z.string().nullable(),
  guestEmail: z.string().nullable(),
  checkedInById: nullableBigintToString,
  notes: z.string().nullable(),
  createdAt: dateToISOString,
  updatedAt: dateToISOString
});
var AttendanceMembershipSnapshotDtoSchema = BaseMembershipDtoSchema.pick({
  uuid: true
}).extend({
  user: UserObjectDtoSchema.pick({
    firstName: true,
    lastName: true,
    email: true
  })
});
var EventAttendanceWithMemberDtoSchema = EventAttendanceDtoSchema.extend({
  membership: AttendanceMembershipSnapshotDtoSchema
});
var EventAttendanceResponseDtoSchema = createResponseSchema(
  EventAttendanceDtoSchema
);
var EventAttendanceArrayResponseDtoSchema = createResponseSchema(
  EventAttendanceWithMemberDtoSchema.array()
);

// src/events/member-event-registration.dto.ts
var MemberEventSnapshotDtoSchema = EventDtoSchema.pick({
  uuid: true,
  title: true,
  status: true,
  startDate: true,
  endDate: true,
  locationType: true
});
var MemberEventAttendanceSnapshotDtoSchema = EventAttendanceDtoSchema.pick({
  uuid: true,
  status: true,
  checkInMethod: true,
  checkInTime: true
});
var MemberEventRegistrationWithAttendanceDtoSchema = EventRegistrationDtoSchema.pick({
  uuid: true,
  status: true,
  createdAt: true
}).extend({
  event: MemberEventSnapshotDtoSchema,
  attendance: MemberEventAttendanceSnapshotDtoSchema.nullable()
});
var MemberEventRegistrationWithAttendanceResponseDtoSchema = createResponseSchema(MemberEventRegistrationWithAttendanceDtoSchema);
var MemberEventRegistrationWithAttendanceArrayResponseDtoSchema = createResponseSchema(MemberEventRegistrationWithAttendanceDtoSchema.array());
var ALLOWED_MEMBER_EVENT_REGISTRATION_FIELDS = [
  "uuid",
  "status",
  "createdAt",
  "updatedAt",
  "event.title",
  "event.status",
  "event.startDate",
  "event.endDate",
  "event.locationType"
];
var SEARCH_MEMBER_EVENT_REGISTRATION_FIELDS = ["event.title"];
var QueryMemberEventRegistrationSchema = createValidatedQuerySchema([
  ...ALLOWED_MEMBER_EVENT_REGISTRATION_FIELDS
]);
var PublicEventDtoSchema = z.object({
  uuid: z.string().uuid(),
  organizationName: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  category: z.string().nullable(),
  startDate: dateToISOString,
  endDate: dateToISOString,
  timezone: z.string(),
  locationType: EventLocationTypeSchema,
  physicalAddress: z.string().nullable(),
  virtualLink: z.string().nullable(),
  registrationCap: z.number().int().positive().nullable(),
  remainingCapacity: z.number().int().nullable(),
  isPaidEvent: z.boolean(),
  image: PublicStorageItemDtoSchema.nullable(),
  ticketTiers: EventTicketTierDtoSchema.array()
});
var PublicEventResponseDtoSchema = createResponseSchema(PublicEventDtoSchema);
var GetPublicEventParamsSchema = z.object({
  eventUuid: z.string().uuid()
});

// src/events/public-event-list-item.dto.ts
var PublicEventListItemDtoSchema = EventDtoSchema.pick({
  uuid: true,
  title: true,
  category: true,
  startDate: true,
  endDate: true,
  timezone: true,
  locationType: true,
  physicalAddress: true,
  virtualLink: true,
  isPaidEvent: true
}).extend({
  image: PublicStorageItemDtoSchema.nullable()
});
var PublicEventListResponseDtoSchema = createArrayResponseSchema(
  PublicEventListItemDtoSchema
);
var GetPublicOrganizationEventsParamsSchema = z.object({
  slug: z.string().min(1, "Organization slug is required")
});
var RegisterForPublicEventDtoSchema = z.object({
  name: z.string().min(1).max(200),
  email: z.string().email()
}).strict();
var RegistrationQrTokenDtoSchema = z.object({
  qrCodeToken: z.string().uuid()
}).strict();
var RegistrationQrTokenResponseDtoSchema = createResponseSchema(
  RegistrationQrTokenDtoSchema
);
var CHECK_IN_PAYMENT_METHODS = [
  "CASH" /* CASH */,
  "CHECK" /* CHECK */,
  "VENMO" /* VENMO */,
  "ZELLE" /* ZELLE */,
  "PAYPAL" /* PAYPAL */,
  "CASH_APP" /* CASH_APP */,
  "COMP" /* COMP */,
  "OTHER" /* OTHER */
];
var CheckInPaymentMethodSchema = z.enum(CHECK_IN_PAYMENT_METHODS);
var ManualCheckInDtoSchema = z.object({
  membershipUuid: z.string().uuid(),
  notes: z.string().max(500).optional(),
  // Required when the event is paid — validated at the service layer
  // since isPaidEvent isn't known at parse time.
  tierUuid: z.string().uuid().optional(),
  paymentMethod: CheckInPaymentMethodSchema.optional()
}).strict();
var GuestCheckInDtoSchema = z.object({
  guestName: z.string().min(1).max(255),
  guestEmail: z.string().email().max(255),
  notes: z.string().max(500).optional(),
  // Required when the event is paid — validated at the service layer
  // since isPaidEvent isn't known at parse time.
  tierUuid: z.string().uuid().optional(),
  paymentMethod: CheckInPaymentMethodSchema.optional()
}).strict();
var CheckInEventQrDtoSchema = z.object({
  token: z.string().uuid()
}).strict();
var GuestCheckInEventQrDtoSchema = z.object({
  token: z.string().uuid(),
  email: z.string().email().max(255),
  name: z.string().min(1).max(255)
}).strict();
var CheckInRegistrationQrDtoSchema = z.object({
  token: z.string().uuid()
}).strict();
var CheckInTicketQrDtoSchema = z.object({
  token: z.string().uuid()
}).strict();
var EventAttendanceStatsDtoSchema = z.object({
  totalRegistered: z.number(),
  totalCheckedIn: z.number(),
  totalNoShows: z.number(),
  totalGuests: z.number(),
  attendanceRate: z.number(),
  // percentage (0-100)
  noShowRate: z.number()
  // percentage (0-100)
});
var EventAttendanceStatsResponseDtoSchema = createResponseSchema(
  EventAttendanceStatsDtoSchema
);
var EventAttendanceOverviewDtoSchema = EventDtoSchema.pick({
  uuid: true,
  title: true,
  status: true,
  startDate: true,
  endDate: true,
  locationType: true,
  registrationCap: true,
  eventSeriesId: true
}).extend({
  totalRegistered: z.number(),
  totalCheckedIn: z.number(),
  totalNoShows: z.number(),
  totalGuests: z.number(),
  attendanceRate: z.number(),
  noShowRate: z.number()
});
var EventAttendanceOverviewResponseDtoSchema = createResponseSchema(
  EventAttendanceOverviewDtoSchema
);
var EventAttendanceOverviewArrayResponseDtoSchema = createResponseSchema(EventAttendanceOverviewDtoSchema.array());
var ALLOWED_EVENT_ATTENDANCE_OVERVIEW_FIELDS = [
  "uuid",
  "title",
  "status",
  "startDate",
  "endDate",
  "locationType",
  "createdAt",
  "updatedAt"
];
var SEARCH_EVENT_ATTENDANCE_OVERVIEW_FIELDS = [
  "title",
  "description",
  "category"
];
var QueryEventAttendanceOverviewSchema = createValidatedQuerySchema([
  ...ALLOWED_EVENT_ATTENDANCE_OVERVIEW_FIELDS
]);

// src/events/attendance/query-event-attendance.dto.ts
var ALLOWED_EVENT_ATTENDANCE_FIELDS = [
  "uuid",
  "status",
  "checkInMethod",
  "checkInTime",
  "isGuest",
  "guestName",
  "guestEmail",
  "notes",
  "createdAt",
  "updatedAt",
  "membership.user.firstName",
  "membership.user.lastName",
  "membership.user.email"
];
var SEARCH_EVENT_ATTENDANCE_FIELDS = [
  "membership.user.firstName",
  "membership.user.lastName",
  "membership.user.email",
  "guestName",
  "guestEmail"
];
var QueryEventAttendanceSchema = createValidatedQuerySchema([
  ...ALLOWED_EVENT_ATTENDANCE_FIELDS
]);
var RemoveAttendanceDtoSchema = z.object({
  success: z.boolean()
});
var RemoveAttendanceResponseDtoSchema = createResponseSchema(
  RemoveAttendanceDtoSchema
);
var RegenerateEventQrDtoSchema = z.object({
  qrCheckInToken: z.string().uuid()
});
var RegenerateEventQrResponseDtoSchema = createResponseSchema(
  RegenerateEventQrDtoSchema
);
var EventTicketStatusSchema = z.enum([
  "ACTIVE",
  "USED",
  "REFUNDED",
  "CANCELLED"
]);
var EventTicketStatus = {
  ACTIVE: "ACTIVE",
  USED: "USED",
  REFUNDED: "REFUNDED",
  CANCELLED: "CANCELLED"
};
var EVENT_TICKET_STATUS_DISPLAY = {
  ACTIVE: { label: "Active", color: "green" },
  USED: { label: "Used", color: "default" },
  REFUNDED: { label: "Refunded", color: "orange" },
  CANCELLED: { label: "Cancelled", color: "red" }
};
var EventRefundStatusSchema = z.enum([
  "PENDING",
  "APPROVED",
  "DENIED",
  "PROCESSING",
  "PROCESSED"
]);
var EventRefundStatus = {
  PENDING: "PENDING",
  APPROVED: "APPROVED",
  DENIED: "DENIED",
  PROCESSING: "PROCESSING",
  PROCESSED: "PROCESSED"
};
var EVENT_REFUND_STATUS_DISPLAY = {
  PENDING: { label: "Pending Review", color: "blue" },
  APPROVED: { label: "Approved", color: "green" },
  DENIED: { label: "Denied", color: "red" },
  PROCESSING: { label: "Processing", color: "orange" },
  PROCESSED: { label: "Refunded", color: "default" }
};
var EventTicketDtoSchema = z.object({
  uuid: z.string().uuid(),
  qrCodeToken: z.string().uuid(),
  attendeeName: z.string().nullable(),
  attendeeEmail: z.string().nullable(),
  status: EventTicketStatusSchema,
  checkedInAt: dateToISOString.nullable(),
  createdAt: dateToISOString,
  updatedAt: dateToISOString
});
var EventTicketWithTierDtoSchema = EventTicketDtoSchema.extend({
  ticketTier: EventTicketTierDtoSchema.pick({
    uuid: true,
    name: true,
    price: true
  })
});
var EventTicketResponseDtoSchema = createResponseSchema(EventTicketDtoSchema);
var EventTicketArrayResponseDtoSchema = createResponseSchema(
  EventTicketWithTierDtoSchema.array()
);
var EventRefundRequestDtoSchema = z.object({
  uuid: z.string().uuid(),
  status: EventRefundStatusSchema,
  reason: z.string(),
  adminResponse: z.string().nullable(),
  processedAt: dateToISOString.nullable(),
  refundAmount: z.number().int().nullable(),
  createdAt: dateToISOString,
  updatedAt: dateToISOString
});
var EventRefundRequestWithMemberDtoSchema = EventRefundRequestDtoSchema.extend({
  membership: OrganizationMembershipDtoSchema.pick({
    uuid: true,
    user: true
  }),
  order: OrderDtoSchema.pick({
    uuid: true,
    orderNumber: true,
    totalAmount: true,
    memberPaysTotal: true
  })
});
var EventRefundRequestResponseDtoSchema = createResponseSchema(
  EventRefundRequestDtoSchema
);
var EventRefundRequestArrayResponseDtoSchema = createResponseSchema(
  EventRefundRequestWithMemberDtoSchema.array()
);
var CreateEventTicketTierDtoSchema = z.object({
  name: z.string().min(1).max(100),
  description: z.string().optional(),
  price: z.number().int().min(50),
  // Stripe minimum $0.50 = 50 cents
  capacity: z.number().int().positive().optional(),
  sortOrder: z.number().int().min(0).optional().default(0),
  salesStartAt: z.string().datetime().optional(),
  salesEndAt: z.string().datetime().optional(),
  isActive: z.boolean().optional().default(true)
});
var UpdateEventTicketTierDtoSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  description: z.string().nullable().optional(),
  price: z.number().int().min(50).optional(),
  capacity: z.number().int().positive().nullable().optional(),
  sortOrder: z.number().int().min(0).optional(),
  salesStartAt: z.string().datetime().nullable().optional(),
  salesEndAt: z.string().datetime().nullable().optional(),
  isActive: z.boolean().optional()
});
var ReorderEventTicketTiersDtoSchema = z.object({
  orderedUuids: z.array(z.string().uuid()).min(1)
});
var TicketLineItemSchema = z.object({
  tierUuid: z.string().uuid(),
  quantity: z.number().int().min(1).max(10)
});
var CreateEventTicketCheckoutDtoSchema = z.object({
  items: z.array(TicketLineItemSchema).min(1),
  returnUrl: z.string().url()
}).refine(
  ({ items }) => items.reduce((total, item) => total + item.quantity, 0) <= 10,
  {
    message: "A checkout can contain at most 10 tickets in total.",
    path: ["items"]
  }
);
var CreatePublicEventTicketCheckoutDtoSchema = z.object({
  name: z.string().min(1).max(200),
  email: z.string().email(),
  items: z.array(TicketLineItemSchema).min(1),
  returnUrl: z.string().url()
}).refine(
  ({ items }) => items.reduce((total, item) => total + item.quantity, 0) <= 10,
  {
    message: "A checkout can contain at most 10 tickets in total.",
    path: ["items"]
  }
);
var UpdateEventTicketDtoSchema = z.object({
  attendeeName: z.string().max(255).nullable().optional(),
  attendeeEmail: z.string().email().nullable().optional(),
  assignedMembershipUuid: z.string().uuid().nullable().optional()
});
var CreateEventRefundRequestDtoSchema = z.object({
  orderUuid: z.string().uuid(),
  reason: z.string().min(10).max(1e3)
});
var ApproveEventRefundRequestDtoSchema = z.object({
  adminResponse: z.string().max(1e3).optional()
});
var DenyEventRefundRequestDtoSchema = z.object({
  adminResponse: z.string().min(1).max(1e3)
});

// src/emails/event-published-email.dto.ts
var EventPublishedEmailDtoSchema = z.object({
  recipientName: z.string(),
  organizationName: z.string(),
  eventTitle: z.string(),
  eventDescription: z.string().nullable(),
  eventCategory: z.string().nullable(),
  eventStartDate: z.string(),
  eventEndDate: z.string(),
  eventTimezone: z.string(),
  eventLocationType: EventLocationTypeSchema,
  eventLocation: z.string().nullable(),
  eventVirtualLink: z.string().nullable(),
  registrationCap: z.number().int().positive().nullable()
});
var EventReminderEmailDtoSchema = z.object({
  recipientName: z.string(),
  organizationName: z.string(),
  eventTitle: z.string(),
  eventStartDate: z.string(),
  eventEndDate: z.string(),
  eventTimezone: z.string(),
  eventLocationType: EventLocationTypeSchema,
  eventLocation: z.string().nullable(),
  eventVirtualLink: z.string().nullable()
});
var EventCancelledEmailDtoSchema = z.object({
  recipientName: z.string(),
  organizationName: z.string(),
  eventTitle: z.string(),
  eventStartDate: z.string(),
  eventEndDate: z.string(),
  eventTimezone: z.string(),
  eventLocationType: EventLocationTypeSchema,
  eventLocation: z.string().nullable(),
  eventVirtualLink: z.string().nullable(),
  cancellationReason: z.string().nullable()
});
var EventChangeItemSchema = z.object({
  field: z.string(),
  oldValue: z.string(),
  newValue: z.string()
});
var EventUpdatedEmailDtoSchema = z.object({
  recipientName: z.string(),
  organizationName: z.string(),
  eventTitle: z.string(),
  eventStartDate: z.string(),
  eventEndDate: z.string(),
  eventTimezone: z.string(),
  eventLocationType: EventLocationTypeSchema,
  eventLocation: z.string().nullable(),
  eventVirtualLink: z.string().nullable(),
  changes: z.array(EventChangeItemSchema).min(1)
});
var EventDeletedEmailDtoSchema = z.object({
  recipientName: z.string(),
  organizationName: z.string(),
  eventTitle: z.string(),
  eventStartDate: z.string(),
  eventEndDate: z.string(),
  eventTimezone: z.string(),
  eventLocationType: EventLocationTypeSchema,
  eventLocation: z.string().nullable(),
  eventVirtualLink: z.string().nullable()
});
var EventRegistrationConfirmationEmailDtoSchema = z.object({
  recipientName: z.string(),
  organizationName: z.string(),
  eventTitle: z.string(),
  eventStartDate: z.string(),
  eventEndDate: z.string(),
  eventTimezone: z.string(),
  eventLocationType: EventLocationTypeSchema,
  eventLocation: z.string().nullable(),
  eventVirtualLink: z.string().nullable(),
  registrationUuid: z.string().uuid()
});
var EventRegistrationCancelledEmailDtoSchema = z.object({
  recipientName: z.string(),
  organizationName: z.string(),
  eventTitle: z.string(),
  eventStartDate: z.string(),
  eventEndDate: z.string(),
  eventTimezone: z.string(),
  eventLocationType: EventLocationTypeSchema,
  eventLocation: z.string().nullable(),
  eventVirtualLink: z.string().nullable(),
  cancelledBy: z.enum(["member", "admin"])
});
var EventTicketPurchaseConfirmationEmailDtoSchema = z.object({
  recipientName: z.string(),
  organizationName: z.string(),
  eventTitle: z.string(),
  eventStartDate: z.string(),
  eventEndDate: z.string(),
  eventTimezone: z.string(),
  eventLocation: z.string().nullable(),
  tickets: z.array(
    z.object({
      tierName: z.string(),
      quantity: z.number().int().positive(),
      unitPrice: z.number()
      // In dollars (not cents)
    })
  ),
  subtotalAmount: z.number(),
  // In dollars (not cents), before tax
  discountAmount: z.number().optional(),
  // In dollars (not cents); omitted from display when falsy
  taxAmount: z.number(),
  // In dollars (not cents); always shown, even $0.00
  platformFeeAmount: z.number().optional(),
  // Mimber platform fee in dollars (not cents)
  processorFeeAmount: z.number().optional(),
  // Payment processor (e.g. Stripe) fee in dollars (not cents)
  totalAmount: z.number(),
  // In dollars (not cents)
  invoiceNumber: z.string(),
  purchaseDate: z.string(),
  receiptPdfUrl: z.string().url()
});
var EventRefundRequestReceivedEmailDtoSchema = z.object({
  recipientName: z.string(),
  organizationName: z.string(),
  eventTitle: z.string(),
  invoiceNumber: z.string(),
  subtotalAmount: z.number(),
  // In dollars (not cents), before tax
  taxAmount: z.number(),
  // In dollars (not cents); always shown, even $0.00
  totalAmount: z.number(),
  // In dollars (not cents)
  reason: z.string(),
  submittedAt: z.string()
});
var EventRefundDeniedEmailDtoSchema = z.object({
  recipientName: z.string(),
  organizationName: z.string(),
  eventTitle: z.string(),
  invoiceNumber: z.string(),
  subtotalAmount: z.number(),
  // In dollars (not cents), before tax
  taxAmount: z.number(),
  // In dollars (not cents); always shown, even $0.00
  totalAmount: z.number(),
  // In dollars (not cents)
  adminResponse: z.string()
});
var EventRefundProcessedEmailDtoSchema = z.object({
  recipientName: z.string(),
  organizationName: z.string(),
  eventTitle: z.string(),
  invoiceNumber: z.string(),
  refundAmount: z.number(),
  // In dollars (not cents)
  refundedAt: z.string()
});
var ManagedMemberWelcomeEmailDtoSchema = z.object({
  recipientName: z.string(),
  recipientEmail: z.string().email(),
  organizationName: z.string(),
  tierName: z.string().nullable(),
  signupUrl: z.string().url()
});
var ImportType = /* @__PURE__ */ ((ImportType2) => {
  ImportType2["MANAGED_MEMBER_IMPORT"] = "MANAGED_MEMBER_IMPORT";
  ImportType2["MEMBER_INVITATION_IMPORT"] = "MEMBER_INVITATION_IMPORT";
  return ImportType2;
})(ImportType || {});
var ImportTypeSchema = z.enum(ImportType);
var IMPORT_TYPE_ROW_LIMITS = {
  ["MANAGED_MEMBER_IMPORT" /* MANAGED_MEMBER_IMPORT */]: 5e3,
  ["MEMBER_INVITATION_IMPORT" /* MEMBER_INVITATION_IMPORT */]: 1e3
};
var IMPORT_MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

// src/emails/import-job-completed-email.dto.ts
var ImportJobCompletedEmailDtoSchema = z.object({
  organizationName: z.string(),
  importType: ImportTypeSchema,
  successCount: z.number().int(),
  skippedCount: z.number().int(),
  errorCount: z.number().int(),
  hasErrorReport: z.boolean(),
  resultUrl: z.url()
});
var DonationThankYouEmailDtoSchema = z.object({
  recipientName: z.string(),
  organizationName: z.string(),
  amount: z.number(),
  // Amount in dollars (not cents)
  currency: z.string().default("USD"),
  donatedAt: z.coerce.date(),
  receiptNumber: z.string(),
  donationReference: z.string().uuid(),
  /** Link to the donor portal where the donor can review their giving history. Omitted until the donor portal is available (Phase 8). */
  donorPortalUrl: z.string().url().optional()
});
var DonationStatementDeliveredEmailDtoSchema = z.object({
  recipientName: z.string(),
  organizationName: z.string(),
  statementNumber: z.string(),
  year: z.number().int(),
  totalAmount: z.number(),
  // Amount in dollars (not cents)
  donationCount: z.number().int(),
  currency: z.string().default("USD"),
  periodStart: z.coerce.date(),
  periodEnd: z.coerce.date(),
  /** Link to view/download the statement in the donor portal. Omitted until the donor portal is available (Phase 8). */
  statementUrl: z.string().url().optional()
});
var DonationRefundProcessedEmailDtoSchema = z.object({
  recipientName: z.string(),
  organizationName: z.string(),
  refundAmount: z.number(),
  // Amount in dollars (not cents)
  originalAmount: z.number(),
  // Original donation amount in dollars
  currency: z.string().default("USD"),
  refundedAt: z.coerce.date(),
  donationReference: z.uuid()
});
var OwnershipTransferApprovalEmailDtoSchema = z.object({
  ownerName: z.string(),
  organizationName: z.string(),
  newOwnerName: z.string(),
  confirmUrl: z.string().url(),
  expiresAt: z.string().datetime()
});
var OwnershipTransferCompletedEmailDtoSchema = z.object({
  recipientName: z.string(),
  organizationName: z.string(),
  previousOwnerName: z.string(),
  newOwnerName: z.string()
});
var OrganizationDeletionApprovalEmailDtoSchema = z.object({
  ownerName: z.string(),
  organizationName: z.string(),
  confirmUrl: z.string(),
  expiresAt: z.string()
});
var OrganizationDeletionScheduledEmailDtoSchema = z.object({
  ownerName: z.string(),
  organizationName: z.string(),
  hardDeleteDate: z.string(),
  cancelUrl: z.string()
});
var OrganizationDeletionCanceledEmailDtoSchema = z.object({
  ownerName: z.string(),
  organizationName: z.string()
});
var OrganizationDeletionCompletedEmailDtoSchema = z.object({
  ownerName: z.string(),
  organizationName: z.string()
});
var CreateCommunicationTemplateDtoSchema = z.object({
  name: z.string().min(1).max(255),
  subject: z.string().min(1).max(255),
  content: z.string().min(1),
  channel: z.nativeEnum(CommunicationChannel).default("EMAIL" /* EMAIL */),
  status: z.nativeEnum(CommunicationTemplateStatus).default("DRAFT" /* DRAFT */)
}).strict();
var UpdateCommunicationTemplateDtoSchema = z.object({
  name: z.string().min(1).max(255).optional(),
  subject: z.string().min(1).max(255).optional(),
  content: z.string().min(1).optional(),
  status: z.nativeEnum(CommunicationTemplateStatus).optional()
}).strict();

// src/communication-templates/query-communication-template.dto.ts
var ALLOWED_COMMUNICATION_TEMPLATE_FIELDS = [
  "uuid",
  "name",
  "subject",
  "channel",
  "status",
  "createdAt",
  "updatedAt"
];
var QueryCommunicationTemplateSchema = createValidatedQuerySchema([
  ...ALLOWED_COMMUNICATION_TEMPLATE_FIELDS
]);

// src/communication-templates/template-variables.ts
var TEMPLATE_VARIABLES = [
  {
    key: "member_name",
    label: "Member Name",
    description: "The recipient's full name"
  },
  {
    key: "member_email",
    label: "Member Email",
    description: "The recipient's email address"
  },
  {
    key: "organization_name",
    label: "Organization Name",
    description: "The name of the organization"
  },
  {
    key: "announcement_title",
    label: "Announcement Title",
    description: "The subject line of the announcement email template"
  }
];
var TEMPLATE_VARIABLE_KEYS = new Set(
  TEMPLATE_VARIABLES.map((v) => v.key)
);
function escapeHtml(value) {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#x27;");
}
function renderTemplateVariables(content, context) {
  return content.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    if (!TEMPLATE_VARIABLE_KEYS.has(key)) {
      return match;
    }
    const value = context[key];
    return value !== void 0 ? escapeHtml(value) : match;
  });
}
var EventSeriesStatusSchema = z.enum([
  "ACTIVE",
  "PAUSED",
  "COMPLETED",
  "CANCELLED"
]);
var EventSeriesStatus = {
  ACTIVE: "ACTIVE",
  PAUSED: "PAUSED",
  COMPLETED: "COMPLETED",
  CANCELLED: "CANCELLED"
};
var EVENT_SERIES_STATUS_DISPLAY = {
  ACTIVE: { label: "Active", color: "green" },
  PAUSED: { label: "Paused", color: "orange" },
  COMPLETED: { label: "Completed", color: "blue" },
  CANCELLED: { label: "Cancelled", color: "red" }
};
var ScheduledActionType = /* @__PURE__ */ ((ScheduledActionType2) => {
  ScheduledActionType2["DUES_REMINDER"] = "DUES_REMINDER";
  ScheduledActionType2["DUES_INVOICE_GENERATE"] = "DUES_INVOICE_GENERATE";
  ScheduledActionType2["DUES_AUTO_PAY_CHARGE"] = "DUES_AUTO_PAY_CHARGE";
  ScheduledActionType2["EVENT_REMINDER"] = "EVENT_REMINDER";
  ScheduledActionType2["ANNOUNCEMENT_PUBLISH"] = "ANNOUNCEMENT_PUBLISH";
  ScheduledActionType2["EVENT_SERIES_GENERATE"] = "EVENT_SERIES_GENERATE";
  return ScheduledActionType2;
})(ScheduledActionType || {});
var ScheduledActionStatus = /* @__PURE__ */ ((ScheduledActionStatus2) => {
  ScheduledActionStatus2["PENDING"] = "PENDING";
  ScheduledActionStatus2["PROCESSING"] = "PROCESSING";
  ScheduledActionStatus2["COMPLETED"] = "COMPLETED";
  ScheduledActionStatus2["FAILED"] = "FAILED";
  ScheduledActionStatus2["CANCELED"] = "CANCELED";
  return ScheduledActionStatus2;
})(ScheduledActionStatus || {});
var ScheduledEntityType = /* @__PURE__ */ ((ScheduledEntityType2) => {
  ScheduledEntityType2["DUES_SETTINGS"] = "DUES_SETTINGS";
  ScheduledEntityType2["DUES_AUTO_PAY_ENROLLMENT"] = "DUES_AUTO_PAY_ENROLLMENT";
  ScheduledEntityType2["INVOICE"] = "INVOICE";
  ScheduledEntityType2["EVENT"] = "EVENT";
  ScheduledEntityType2["ANNOUNCEMENT"] = "ANNOUNCEMENT";
  ScheduledEntityType2["EVENT_SERIES"] = "EVENT_SERIES";
  return ScheduledEntityType2;
})(ScheduledEntityType || {});
var ScheduledActionDtoSchema = z.object({
  uuid: z.string().uuid(),
  organizationId: bigintToString,
  actionType: z.nativeEnum(ScheduledActionType),
  status: z.nativeEnum(ScheduledActionStatus),
  entityType: z.nativeEnum(ScheduledEntityType),
  entityId: z.string().max(100),
  scheduledFor: dateToISOString,
  isRecurring: z.boolean(),
  intervalLength: z.number().int().min(1).nullable(),
  intervalType: z.nativeEnum(RecurrenceInterval).nullable(),
  startsAt: dateToISOString.nullable(),
  endsAt: dateToISOString.nullable(),
  maxOccurrences: z.number().int().min(1).nullable(),
  executionCount: z.number().int().min(0),
  lastProcessedAt: dateToISOString.nullable(),
  failedAttemptCount: z.number().int().min(0),
  lastFailureReason: z.string().nullable(),
  metadata: z.record(z.string(), z.any()).nullable(),
  // Calendar-grade recurrence fields
  daysOfWeek: z.array(z.nativeEnum(DayOfWeek)).nullable().optional(),
  dayOfMonth: z.number().int().min(1).max(31).nullable().optional(),
  weekIndex: z.nativeEnum(WeekIndex).nullable().optional(),
  monthOfYear: z.number().int().min(1).max(12).nullable().optional(),
  createdAt: dateToISOString,
  updatedAt: dateToISOString
});
var CreateScheduledActionDtoSchema = z.object({
  actionType: z.nativeEnum(ScheduledActionType),
  entityType: z.nativeEnum(ScheduledEntityType),
  entityId: z.string().max(100),
  scheduledFor: z.string().datetime(),
  isRecurring: z.boolean().default(false),
  intervalLength: z.number().int().min(1).optional(),
  intervalType: z.nativeEnum(RecurrenceInterval).optional(),
  startsAt: z.string().datetime().optional(),
  endsAt: z.string().datetime().optional(),
  maxOccurrences: z.number().int().min(1).optional(),
  metadata: z.record(z.string(), z.any()).optional(),
  // Calendar-grade recurrence fields
  daysOfWeek: z.array(z.nativeEnum(DayOfWeek)).min(1).optional(),
  dayOfMonth: z.number().int().min(1).max(31).optional(),
  weekIndex: z.nativeEnum(WeekIndex).optional(),
  monthOfYear: z.number().int().min(1).max(12).optional()
});
var ScheduledActionResponseDtoSchema = createResponseSchema(
  ScheduledActionDtoSchema
);
var ScheduledActionListResponseDtoSchema = createArrayResponseSchema(
  ScheduledActionDtoSchema
);

// src/event-series/event-series.dto.ts
var EventSeriesDtoSchema = z.object({
  uuid: z.string().uuid(),
  organizationId: bigintToString,
  templateEventId: bigintToString,
  scheduledActionId: bigintToString,
  status: EventSeriesStatusSchema,
  lastGeneratedDate: dateToISOString.nullable(),
  generatedCount: z.number().int().min(0),
  createdAt: dateToISOString,
  updatedAt: dateToISOString
});
var EventSeriesWithRelationsDtoSchema = EventSeriesDtoSchema.extend({
  templateEvent: EventDtoSchema.optional(),
  scheduledAction: ScheduledActionDtoSchema.optional(),
  events: z.array(EventDtoSchema).optional()
});
var EventSeriesResponseDtoSchema = createResponseSchema(
  EventSeriesWithRelationsDtoSchema
);
var EventSeriesArrayResponseDtoSchema = createResponseSchema(
  EventSeriesDtoSchema.array()
);
var UpdateEventSeriesDtoSchema = z.object({
  status: EventSeriesStatusSchema.optional(),
  intervalLength: z.number().int().min(1).optional(),
  intervalType: z.nativeEnum(RecurrenceInterval).optional(),
  daysOfWeek: z.array(z.nativeEnum(DayOfWeek)).min(1).optional(),
  dayOfMonth: z.number().int().min(1).max(31).optional(),
  weekIndex: z.nativeEnum(WeekIndex).optional(),
  monthOfYear: z.number().int().min(1).max(12).optional(),
  endsAt: z.string().datetime().nullable().optional(),
  maxOccurrences: z.number().int().min(1).nullable().optional()
}).strict();
var DashboardTileDtoSchema = z.object({
  key: z.string(),
  title: z.string(),
  value: z.number(),
  resource: z.enum(Resource),
  destination: z.string()
}).strict();
var DashboardOverviewDtoSchema = z.object({
  tiles: z.array(DashboardTileDtoSchema)
}).strict();
var DashboardOverviewResponseDtoSchema = createResponseSchema(
  DashboardOverviewDtoSchema
);
var DashboardRecentActivityItemDtoSchema = z.object({
  uuid: z.string(),
  resource: z.enum(Resource),
  action: z.enum(Action),
  label: z.string(),
  resourceUuid: z.string().nullable(),
  destination: z.string().nullable(),
  performedBy: z.string().nullable(),
  createdAt: dateToISOString
}).strict();
var DashboardRecentActivityResponseDtoSchema = createArrayResponseSchema(DashboardRecentActivityItemDtoSchema);
var DashboardCalendarItemDtoSchema = z.object({
  uuid: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  start: z.iso.datetime(),
  end: z.iso.datetime().nullable(),
  resource: z.enum(Resource),
  resourceUuid: z.string(),
  destination: z.string()
}).strict();
var DashboardCalendarResponseDtoSchema = createArrayResponseSchema(
  DashboardCalendarItemDtoSchema
);
var DashboardCalendarQueryDtoSchema = z.object({
  from: z.string().datetime(),
  to: z.string().datetime()
}).strict();
var DuesAutoPayStatusSchema = z.enum([
  "ACTIVE",
  "PAUSED",
  "SUSPENDED",
  "CANCELED"
]);
var DuesAutoPayEnrollmentDtoSchema = z.object({
  uuid: z.string().uuid(),
  membershipId: bigintToString,
  userId: bigintToString,
  billingUserId: nullableBigintToString.optional(),
  organizationId: bigintToString,
  savedPaymentMethodId: nullableBigintToString,
  openInvoiceId: nullableBigintToString,
  billingScheduledActionId: nullableBigintToString,
  reminderScheduledActionId: nullableBigintToString,
  status: DuesAutoPayStatusSchema,
  nextBillingDate: nullableDateToISOString,
  enrolledAt: dateToISOString,
  pausedAt: nullableDateToISOString,
  suspendedAt: nullableDateToISOString,
  canceledAt: nullableDateToISOString,
  failedAttemptCount: z.number().int(),
  lastAttemptedAt: nullableDateToISOString,
  lastFailureReason: z.string().nullable(),
  // Included relations
  paymentMethodLast4: z.string().nullable().optional(),
  paymentMethodBrand: z.string().nullable().optional(),
  tierName: z.string().nullable().optional(),
  createdAt: dateToISOString,
  updatedAt: dateToISOString
});
var EnrollAutoPayDtoSchema = z.object({
  paymentMethodUuid: z.string().uuid()
});
var UpdateAutoPayPaymentMethodDtoSchema = z.object({
  paymentMethodUuid: z.string().uuid()
});
var DuesAutoPayEnrollmentResponseDtoSchema = createResponseSchema(
  DuesAutoPayEnrollmentDtoSchema
);
var DuesDashboardStatsDtoSchema = z.object({
  totalExpected: z.number(),
  totalCollected: z.number(),
  totalOutstanding: z.number(),
  totalOverdue: z.number(),
  overdueCount: z.number(),
  paidCount: z.number(),
  openCount: z.number(),
  autoPayActiveCount: z.number(),
  collectionRate: z.number()
});
var DuesDashboardResponseDtoSchema = createResponseSchema(
  DuesDashboardStatsDtoSchema
);
var DuesStatusDtoSchema = z.object({
  hasDues: z.boolean(),
  tierName: z.string().nullable(),
  duesAmount: z.number().nullable(),
  currency: z.string().default("USD"),
  billingInterval: z.string().nullable(),
  currentInvoiceUuid: z.string().uuid().nullable(),
  currentInvoiceStatus: z.string().nullable(),
  currentAmountDue: z.number().nullable(),
  currentTaxAmount: z.number().nullable(),
  dueAt: nullableDateToISOString,
  nextBillingDate: nullableDateToISOString,
  tierExpiresAt: nullableDateToISOString,
  autoPayStatus: z.string().nullable(),
  lastPaymentDate: nullableDateToISOString,
  lastPaymentAmount: z.number().nullable()
});
var DuesStatusResponseDtoSchema = createResponseSchema(DuesStatusDtoSchema);
var GenerateDuesInvoiceDtoSchema = z.preprocess(
  (value) => value ?? {},
  z.object({
    dueAt: z.iso.datetime().optional().describe(
      "Custom due date for the invoice, as an ISO datetime string. Defaults to now if omitted."
    )
  })
);
var FinanceReportPointDtoSchema = z.object({
  date: z.string(),
  revenue: z.number(),
  collectedInvoices: z.number()
}).strict();
var FinanceReportDtoSchema = z.object({
  totalRevenue: z.number(),
  duesCollectionRate: z.number(),
  outstandingBalance: z.number(),
  issuedInvoices: z.number(),
  paidInvoices: z.number(),
  trend: z.array(FinanceReportPointDtoSchema)
}).strict();
var FinanceReportResponseDtoSchema = createResponseSchema(
  FinanceReportDtoSchema
);
var QueryFinanceReportDtoSchema = z.object({
  from: z.string().datetime(),
  to: z.string().datetime()
}).strict().refine((data) => new Date(data.from) <= new Date(data.to), {
  message: "from must be before or equal to to",
  path: ["to"]
});
var TransactionMembershipSchema = BaseMembershipDtoSchema.pick({
  uuid: true
}).extend({
  // BaseMembershipDtoSchema uses z.string() without .uuid() — enforce the format here
  uuid: z.string().uuid(),
  user: UserObjectDtoSchema.pick({
    firstName: true,
    lastName: true
  })
});
var TransactionInvoiceSchema = z.object({
  uuid: z.string().uuid(),
  membership: TransactionMembershipSchema.nullable()
});
var TransactionOrderSchema = z.object({
  uuid: z.string().uuid(),
  membership: TransactionMembershipSchema.nullable()
});
var TransactionDtoSchema = z.object({
  uuid: z.string().uuid(),
  amount: nullableNumberCoerce,
  status: z.nativeEnum(PaymentStatus),
  method: z.nativeEnum(PaymentMethod).nullable(),
  stripePaymentIntentId: z.string().nullable(),
  createdAt: dateToISOString,
  invoice: TransactionInvoiceSchema.nullable(),
  order: TransactionOrderSchema.nullable()
}).transform((data) => {
  var _a, _b, _c, _d;
  const user = ((_b = (_a = data.invoice) == null ? void 0 : _a.membership) == null ? void 0 : _b.user) ?? ((_d = (_c = data.order) == null ? void 0 : _c.membership) == null ? void 0 : _d.user);
  const memberName = (user == null ? void 0 : user.firstName) || (user == null ? void 0 : user.lastName) ? [user.firstName, user.lastName].filter(Boolean).join(" ") : null;
  return { ...data, memberName };
});
var TransactionArrayResponseDtoSchema = createResponseSchema(
  TransactionDtoSchema.array()
);
var TransactionStripeDetailSchema = z.object({
  cardBrand: z.string().nullable(),
  cardLast4: z.string().nullable(),
  receiptUrl: z.string().nullable(),
  riskLevel: z.string().nullable(),
  riskScore: z.number().nullable(),
  paymentMethodType: z.string().nullable()
});
var TransactionDetailInvoiceSchema = z.object({
  uuid: z.string().uuid(),
  invoiceNumber: z.string(),
  status: z.nativeEnum(InvoiceStatus),
  totalAmount: nullableNumberCoerce,
  membership: TransactionMembershipSchema.nullable()
});
var TransactionDetailOrderSchema = z.object({
  uuid: z.string().uuid(),
  orderNumber: z.string(),
  status: z.nativeEnum(OrderStatus),
  sourceType: z.nativeEnum(OrderSourceType),
  totalAmount: nullableNumberCoerce,
  membership: TransactionMembershipSchema.nullable()
});
var TransactionDetailDtoSchema = z.object({
  uuid: z.string().uuid(),
  amount: nullableNumberCoerce,
  status: z.nativeEnum(PaymentStatus),
  method: z.nativeEnum(PaymentMethod).nullable(),
  provider: z.nativeEnum(PaymentProvider),
  stripePaymentIntentId: z.string().nullable(),
  stripeChargeId: z.string().nullable(),
  platformFeeAmount: nullableNumberCoerce,
  stripeFeeAmount: nullableNumberCoerce,
  netAmount: nullableNumberCoerce,
  paidAt: dateToISOString.nullable(),
  failureCode: z.string().nullable(),
  failureMessage: z.string().nullable(),
  note: z.string().nullable(),
  createdAt: dateToISOString,
  invoice: TransactionDetailInvoiceSchema.nullable(),
  order: TransactionDetailOrderSchema.nullable(),
  stripe: TransactionStripeDetailSchema.nullable()
}).transform((data) => {
  var _a, _b, _c, _d;
  const user = ((_b = (_a = data.invoice) == null ? void 0 : _a.membership) == null ? void 0 : _b.user) ?? ((_d = (_c = data.order) == null ? void 0 : _c.membership) == null ? void 0 : _d.user);
  const memberName = (user == null ? void 0 : user.firstName) || (user == null ? void 0 : user.lastName) ? [user.firstName, user.lastName].filter(Boolean).join(" ") : null;
  return { ...data, memberName };
});
var TransactionDetailResponseDtoSchema = createResponseSchema(
  TransactionDetailDtoSchema
);

// src/scheduled-actions/scheduled-action.utils.ts
var DAY_OF_WEEK_MAP = {
  ["SUNDAY" /* SUNDAY */]: 0,
  ["MONDAY" /* MONDAY */]: 1,
  ["TUESDAY" /* TUESDAY */]: 2,
  ["WEDNESDAY" /* WEDNESDAY */]: 3,
  ["THURSDAY" /* THURSDAY */]: 4,
  ["FRIDAY" /* FRIDAY */]: 5,
  ["SATURDAY" /* SATURDAY */]: 6
};
var WEEK_INDEX_MAP = {
  ["FIRST" /* FIRST */]: 1,
  ["SECOND" /* SECOND */]: 2,
  ["THIRD" /* THIRD */]: 3,
  ["FOURTH" /* FOURTH */]: 4,
  ["LAST" /* LAST */]: -1
};
function findNthDayOfWeekInMonth(year, month, dayOfWeek, weekIndex) {
  if (weekIndex === -1) {
    const lastDay = new Date(Date.UTC(year, month + 1, 0));
    for (let d = lastDay.getUTCDate(); d >= 1; d--) {
      const date = new Date(Date.UTC(year, month, d));
      if (date.getUTCDay() === dayOfWeek) return d;
    }
    return null;
  }
  let count = 0;
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(Date.UTC(year, month, d));
    if (date.getUTCDay() === dayOfWeek) {
      count++;
      if (count === weekIndex) return d;
    }
  }
  return null;
}
function computeNextScheduledDate(currentDate, intervalLength, intervalType, calendarFields) {
  var _a, _b, _c;
  const date = new Date(currentDate);
  if (intervalType === "WEEKLY" /* WEEKLY */ && ((_a = calendarFields == null ? void 0 : calendarFields.daysOfWeek) == null ? void 0 : _a.length)) {
    return computeNextWeeklyWithDays(
      date,
      intervalLength,
      calendarFields.daysOfWeek
    );
  }
  if (intervalType === "MONTHLY" /* MONTHLY */ && (calendarFields == null ? void 0 : calendarFields.weekIndex) && ((_b = calendarFields == null ? void 0 : calendarFields.daysOfWeek) == null ? void 0 : _b.length)) {
    return computeNextRelativeMonthly(
      date,
      intervalLength,
      calendarFields.daysOfWeek[0],
      calendarFields.weekIndex
    );
  }
  if (intervalType === "MONTHLY" /* MONTHLY */ && (calendarFields == null ? void 0 : calendarFields.dayOfMonth)) {
    return computeNextAbsoluteMonthly(
      date,
      intervalLength,
      calendarFields.dayOfMonth
    );
  }
  if (intervalType === "YEARLY" /* YEARLY */ && (calendarFields == null ? void 0 : calendarFields.monthOfYear) && (calendarFields == null ? void 0 : calendarFields.weekIndex) && ((_c = calendarFields == null ? void 0 : calendarFields.daysOfWeek) == null ? void 0 : _c.length)) {
    return computeNextRelativeYearly(
      date,
      intervalLength,
      calendarFields.monthOfYear,
      calendarFields.daysOfWeek[0],
      calendarFields.weekIndex
    );
  }
  if (intervalType === "YEARLY" /* YEARLY */ && (calendarFields == null ? void 0 : calendarFields.monthOfYear) && (calendarFields == null ? void 0 : calendarFields.dayOfMonth)) {
    return computeNextAbsoluteYearly(
      date,
      intervalLength,
      calendarFields.monthOfYear,
      calendarFields.dayOfMonth
    );
  }
  switch (intervalType) {
    case "DAILY" /* DAILY */:
      date.setUTCDate(date.getUTCDate() + intervalLength);
      break;
    case "WEEKLY" /* WEEKLY */:
      date.setUTCDate(date.getUTCDate() + intervalLength * 7);
      break;
    case "MONTHLY" /* MONTHLY */:
      date.setUTCMonth(date.getUTCMonth() + intervalLength);
      break;
    case "QUARTERLY" /* QUARTERLY */:
      date.setUTCMonth(date.getUTCMonth() + intervalLength * 3);
      break;
    case "YEARLY" /* YEARLY */:
      date.setUTCFullYear(date.getUTCFullYear() + intervalLength);
      break;
  }
  return date.toISOString();
}
function computeNextWeeklyWithDays(currentDate, intervalLength, daysOfWeek) {
  const targetDays = daysOfWeek.map((d) => DAY_OF_WEEK_MAP[d]).sort((a, b) => a - b);
  const currentDay = currentDate.getUTCDay();
  const nextDayThisWeek = targetDays.find((d) => d > currentDay);
  if (nextDayThisWeek !== void 0) {
    const diff = nextDayThisWeek - currentDay;
    const result2 = new Date(currentDate);
    result2.setUTCDate(result2.getUTCDate() + diff);
    return result2.toISOString();
  }
  const daysUntilNextSunday = 7 - currentDay;
  const daysToSkip = daysUntilNextSunday + (intervalLength - 1) * 7 + targetDays[0];
  const result = new Date(currentDate);
  result.setUTCDate(result.getUTCDate() + daysToSkip);
  return result.toISOString();
}
function computeNextAbsoluteMonthly(currentDate, intervalLength, dayOfMonth) {
  const result = new Date(currentDate);
  result.setUTCDate(1);
  result.setUTCMonth(result.getUTCMonth() + intervalLength);
  const daysInMonth = new Date(
    Date.UTC(result.getUTCFullYear(), result.getUTCMonth() + 1, 0)
  ).getUTCDate();
  result.setUTCDate(Math.min(dayOfMonth, daysInMonth));
  return result.toISOString();
}
function computeNextRelativeMonthly(currentDate, intervalLength, dayOfWeek, weekIndex) {
  const targetDay = DAY_OF_WEEK_MAP[dayOfWeek];
  const targetWeek = WEEK_INDEX_MAP[weekIndex];
  let year = currentDate.getUTCFullYear();
  let month = currentDate.getUTCMonth() + intervalLength;
  while (month > 11) {
    year += 1;
    month -= 12;
  }
  const day = findNthDayOfWeekInMonth(year, month, targetDay, targetWeek);
  if (day !== null) {
    return new Date(
      Date.UTC(
        year,
        month,
        day,
        currentDate.getUTCHours(),
        currentDate.getUTCMinutes(),
        currentDate.getUTCSeconds()
      )
    ).toISOString();
  }
  month += 1;
  if (month > 11) {
    year += 1;
    month -= 12;
  }
  const fallbackDay = findNthDayOfWeekInMonth(
    year,
    month,
    targetDay,
    targetWeek
  );
  return new Date(
    Date.UTC(
      year,
      month,
      fallbackDay ?? 1,
      currentDate.getUTCHours(),
      currentDate.getUTCMinutes(),
      currentDate.getUTCSeconds()
    )
  ).toISOString();
}
function computeNextAbsoluteYearly(currentDate, intervalLength, monthOfYear, dayOfMonth) {
  const year = currentDate.getUTCFullYear() + intervalLength;
  const month = monthOfYear - 1;
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return new Date(
    Date.UTC(
      year,
      month,
      Math.min(dayOfMonth, daysInMonth),
      currentDate.getUTCHours(),
      currentDate.getUTCMinutes(),
      currentDate.getUTCSeconds()
    )
  ).toISOString();
}
function computeNextRelativeYearly(currentDate, intervalLength, monthOfYear, dayOfWeek, weekIndex) {
  const targetDay = DAY_OF_WEEK_MAP[dayOfWeek];
  const targetWeek = WEEK_INDEX_MAP[weekIndex];
  const year = currentDate.getUTCFullYear() + intervalLength;
  const month = monthOfYear - 1;
  const day = findNthDayOfWeekInMonth(year, month, targetDay, targetWeek);
  return new Date(
    Date.UTC(
      year,
      month,
      day ?? 1,
      currentDate.getUTCHours(),
      currentDate.getUTCMinutes(),
      currentDate.getUTCSeconds()
    )
  ).toISOString();
}
function generateOccurrenceDates(startDate, intervalLength, intervalType, options = {}) {
  const {
    endsAt,
    maxOccurrences,
    executionCount = 0,
    maxLookahead = defaultMaxLookahead(),
    daysOfWeek,
    dayOfMonth,
    weekIndex,
    monthOfYear
  } = options;
  const calendarFields = {
    daysOfWeek,
    dayOfMonth,
    weekIndex,
    monthOfYear
  };
  const dates = [];
  let current = new Date(startDate);
  const endsAtDate = endsAt ? new Date(endsAt) : null;
  const remainingOccurrences = maxOccurrences ? maxOccurrences - executionCount : Infinity;
  for (let i = 0; i < remainingOccurrences; i++) {
    if (current > maxLookahead) break;
    if (endsAtDate && current > endsAtDate) break;
    dates.push(current.toISOString());
    current = new Date(
      computeNextScheduledDate(
        current,
        intervalLength,
        intervalType,
        calendarFields
      )
    );
  }
  return dates;
}
function buildLookahead(months) {
  const d = /* @__PURE__ */ new Date();
  d.setUTCMonth(d.getUTCMonth() + months);
  return d;
}
function defaultMaxLookahead() {
  return buildLookahead(6);
}
var WaitlistStatusSchema = z.enum(["PENDING", "ACCEPTED", "REJECTED"]);
var JoinWaitlistDtoSchema = z.object({
  email: z.string().email("Please enter a valid email address").max(255, "Email must be 255 characters or less"),
  name: z.string().min(1, "Name is required").max(255, "Name must be 255 characters or less"),
  organizationName: z.string().max(255, "Organization name must be 255 characters or less").optional(),
  source: z.string().max(500, "Source must be 500 characters or less").optional()
});
var WaitlistDtoSchema = z.object({
  uuid: z.string().uuid(),
  email: z.string().email(),
  name: z.string(),
  organizationName: z.string().nullable(),
  source: z.string().nullable(),
  status: WaitlistStatusSchema,
  inviteCode: z.string().nullable(),
  inviteCodeExpiresAt: nullableDateToISOString,
  acceptedAt: nullableDateToISOString,
  rejectedAt: nullableDateToISOString,
  createdAt: dateToISOString,
  updatedAt: dateToISOString
});
var WaitlistAcceptanceEmailDtoSchema = z.object({
  recipientName: z.string(),
  signupUrl: z.string().url(),
  inviteCode: z.string().uuid(),
  expiresAt: z.string().datetime()
  // ISO date string
});
var WaitlistNewEntryEmailDtoSchema = z.object({
  applicantName: z.string(),
  applicantEmail: z.string().email(),
  organizationName: z.string().nullable(),
  source: z.string().nullable(),
  waitlistEntryUrl: z.string().url(),
  submittedAt: z.string().datetime()
});
var CreateDonationPageDtoSchema = z.object({
  slug: z.string().min(1).max(255).regex(/^[a-z0-9-]+$/, {
    message: "Slug must be lowercase alphanumeric with hyphens only"
  }),
  title: z.string().min(1).max(255),
  description: z.string().optional(),
  content: z.string().optional(),
  currency: z.string().length(3).optional().default("USD"),
  minimumAmount: z.number().int().positive().optional().default(100),
  allowCustomAmount: z.boolean().optional().default(true),
  allowGuestCheckout: z.boolean().optional().default(true),
  isPublished: z.boolean().optional().default(false),
  isPrimary: z.boolean().optional().default(false),
  suggestedAmounts: z.array(
    z.object({
      amount: z.number().int().positive(),
      sortOrder: z.number().int().min(0)
    })
  ).optional()
}).strict();
var UpdateDonationPageDtoSchema = z.object({
  slug: z.string().min(1).max(255).regex(/^[a-z0-9-]+$/, {
    message: "Slug must be lowercase alphanumeric with hyphens only"
  }).optional(),
  title: z.string().min(1).max(255).optional(),
  description: z.string().nullable().optional(),
  content: z.string().nullable().optional(),
  currency: z.string().length(3).optional(),
  minimumAmount: z.number().int().positive().optional(),
  allowCustomAmount: z.boolean().optional(),
  allowGuestCheckout: z.boolean().optional(),
  isPublished: z.boolean().optional(),
  isPrimary: z.boolean().optional(),
  suggestedAmounts: z.array(
    z.object({
      amount: z.number().int().positive(),
      sortOrder: z.number().int().min(0)
    })
  ).optional()
}).strict();
var CreateDonationDtoSchema = z.object({
  donationPageUuid: z.string().uuid().optional(),
  amount: z.number().int().positive().max(1e7),
  // $100,000 cap in cents
  currency: z.string().length(3).optional().default("USD"),
  isRecurring: z.boolean().optional().default(false),
  intervalLength: z.number().int().positive().optional(),
  intervalType: z.nativeEnum(RecurrenceInterval).optional(),
  savedPaymentMethodUuid: z.string().uuid().optional(),
  note: z.preprocess(
    (val) => val === "" || val === null ? void 0 : val,
    z.string().max(1e3).optional()
  ),
  // Guest donor info — required when unauthenticated
  guestEmail: z.string().email().optional(),
  guestFirstName: z.string().max(255).optional(),
  guestLastName: z.string().max(255).optional()
}).strict().refine(
  (data) => {
    if (data.isRecurring) {
      return !!data.intervalType;
    }
    return true;
  },
  {
    message: "intervalType is required for recurring donations",
    path: ["intervalType"]
  }
);
var DonationCheckoutResponseDtoSchema = z.object({
  clientSecret: z.string().optional(),
  checkoutUrl: z.string().url().optional(),
  stripeCustomerId: z.string().optional(),
  customerSessionClientSecret: z.string().optional(),
  customerEphemeralKeySecret: z.string().optional(),
  donationUuid: z.string().uuid()
});
var UpdateRecurringDonationDtoSchema = z.object({
  amount: z.number().int().positive().optional(),
  savedPaymentMethodUuid: z.string().uuid().optional()
}).strict();
var DonationStatementDtoSchema = z.object({
  uuid: z.string().uuid(),
  year: z.number().int(),
  statementNumber: z.string(),
  currency: z.string(),
  totalAmount: z.number().int(),
  donationCount: z.number().int(),
  generatedAt: dateToISOString,
  sentAt: dateToISOString.nullable(),
  snapshot: z.record(z.string(), z.unknown()).nullable(),
  resendCount: z.number().int(),
  lastSentAt: dateToISOString.nullable(),
  createdAt: dateToISOString,
  updatedAt: dateToISOString
});
var DonationStatementResponseDtoSchema = createResponseSchema(
  DonationStatementDtoSchema
);
var DonationStatementArrayResponseDtoSchema = createResponseSchema(
  DonationStatementDtoSchema.array()
);
var GenerateDonationStatementsDtoSchema = z.object({
  year: z.number().int().min(2e3).max(2100),
  donorProfileUuids: z.array(z.string().uuid()).optional()
}).strict();

// src/donations/query-donation.dto.ts
var ALLOWED_DONATION_FIELDS = [
  "uuid",
  "amount",
  "currency",
  "isGuest",
  "donatedAt",
  "createdAt",
  "updatedAt"
];
var QueryDonationSchema = createValidatedQuerySchema([
  ...ALLOWED_DONATION_FIELDS
]);

// src/donations/query-donation-page.dto.ts
var ALLOWED_DONATION_PAGE_FIELDS = [
  "uuid",
  "slug",
  "title",
  "currency",
  "minimumAmount",
  "allowCustomAmount",
  "allowGuestCheckout",
  "isPublished",
  "isPrimary",
  "createdAt",
  "updatedAt"
];
var QueryDonationPageSchema = createValidatedQuerySchema([
  ...ALLOWED_DONATION_PAGE_FIELDS
]);

// src/donations/query-donor.dto.ts
var ALLOWED_DONOR_FIELDS = [
  "uuid",
  "email",
  "firstName",
  "lastName",
  "createdAt",
  "updatedAt"
];
var QueryDonorSchema = createValidatedQuerySchema([
  ...ALLOWED_DONOR_FIELDS
]);
var GetPublicDonationPageParamsSchema = z.object({
  orgSlug: z.string().min(1, "Organization slug is required"),
  pageSlug: z.string().min(1, "Donation page slug is required")
});
var GetPublicDonationPageQuerySchema = z.object({
  previewToken: z.uuid().optional()
});
var NotificationCategory = /* @__PURE__ */ ((NotificationCategory2) => {
  NotificationCategory2["MEMBERSHIP"] = "MEMBERSHIP";
  NotificationCategory2["MEMBERSHIP_DUES"] = "MEMBERSHIP_DUES";
  NotificationCategory2["EVENTS"] = "EVENTS";
  NotificationCategory2["ANNOUNCEMENTS"] = "ANNOUNCEMENTS";
  NotificationCategory2["GROUPS"] = "GROUPS";
  NotificationCategory2["DONATIONS"] = "DONATIONS";
  NotificationCategory2["ADMIN_MEMBERSHIP_ALERTS"] = "ADMIN_MEMBERSHIP_ALERTS";
  NotificationCategory2["ADMIN_FINANCE_ALERTS"] = "ADMIN_FINANCE_ALERTS";
  NotificationCategory2["ADMIN_EVENT_ALERTS"] = "ADMIN_EVENT_ALERTS";
  return NotificationCategory2;
})(NotificationCategory || {});
var NotificationEventType = /* @__PURE__ */ ((NotificationEventType2) => {
  NotificationEventType2["MEMBERSHIP_JOIN_REQUEST"] = "MEMBERSHIP_JOIN_REQUEST";
  NotificationEventType2["INVITATION_ACCEPTED"] = "INVITATION_ACCEPTED";
  NotificationEventType2["PURCHASE_COMPLETED"] = "PURCHASE_COMPLETED";
  NotificationEventType2["DONATION_RECEIVED"] = "DONATION_RECEIVED";
  NotificationEventType2["INVOICE_OVERDUE"] = "INVOICE_OVERDUE";
  NotificationEventType2["INVOICE_PAYMENT_FAILED"] = "INVOICE_PAYMENT_FAILED";
  NotificationEventType2["EVENT_REGISTRATION_CREATED"] = "EVENT_REGISTRATION_CREATED";
  NotificationEventType2["EVENT_WAITLIST_JOINED"] = "EVENT_WAITLIST_JOINED";
  NotificationEventType2["REFUND_REQUESTED"] = "REFUND_REQUESTED";
  NotificationEventType2["ANNOUNCEMENT_PUBLISHED"] = "ANNOUNCEMENT_PUBLISHED";
  NotificationEventType2["DUES_INVOICE_ISSUED"] = "DUES_INVOICE_ISSUED";
  NotificationEventType2["DUES_REMINDER"] = "DUES_REMINDER";
  NotificationEventType2["DUES_PAYMENT_CONFIRMATION"] = "DUES_PAYMENT_CONFIRMATION";
  NotificationEventType2["DUES_PAYMENT_FAILED"] = "DUES_PAYMENT_FAILED";
  NotificationEventType2["EVENT_INVITATION"] = "EVENT_INVITATION";
  NotificationEventType2["EVENT_REMINDER"] = "EVENT_REMINDER";
  NotificationEventType2["EVENT_CHANGED"] = "EVENT_CHANGED";
  NotificationEventType2["GROUP_ADDED"] = "GROUP_ADDED";
  NotificationEventType2["GROUP_MESSAGE"] = "GROUP_MESSAGE";
  NotificationEventType2["DONATION_RECEIPT"] = "DONATION_RECEIPT";
  return NotificationEventType2;
})(NotificationEventType || {});
var NotificationCategorySchema = z.nativeEnum(NotificationCategory);
var NotificationEventTypeSchema = z.nativeEnum(NotificationEventType);
var NotificationScope = /* @__PURE__ */ ((NotificationScope2) => {
  NotificationScope2["ADMIN"] = "ADMIN";
  NotificationScope2["MEMBER"] = "MEMBER";
  return NotificationScope2;
})(NotificationScope || {});
var NotificationScopeSchema = z.nativeEnum(NotificationScope);
var NOTIFICATION_EVENT_CONFIG = {
  // Admin-facing
  ["MEMBERSHIP_JOIN_REQUEST" /* MEMBERSHIP_JOIN_REQUEST */]: {
    category: "ADMIN_MEMBERSHIP_ALERTS" /* ADMIN_MEMBERSHIP_ALERTS */,
    scope: "ADMIN" /* ADMIN */,
    featureFlag: "notifications.membership-join-request",
    label: "New membership join requests",
    description: "A prospective member has requested to join the organization.",
    availableChannels: ["EMAIL" /* EMAIL */, "SMS" /* SMS */],
    defaultEnabled: {
      ["EMAIL" /* EMAIL */]: true,
      ["SMS" /* SMS */]: true
    },
    resource: "MEMBERSHIP" /* MEMBERSHIP */
  },
  ["INVITATION_ACCEPTED" /* INVITATION_ACCEPTED */]: {
    category: "ADMIN_MEMBERSHIP_ALERTS" /* ADMIN_MEMBERSHIP_ALERTS */,
    scope: "ADMIN" /* ADMIN */,
    featureFlag: "notifications.invitation-accepted",
    label: "Invitation accepted",
    description: "An invited member has accepted their invitation.",
    availableChannels: ["EMAIL" /* EMAIL */, "SMS" /* SMS */],
    defaultEnabled: {
      ["EMAIL" /* EMAIL */]: true,
      ["SMS" /* SMS */]: false
    },
    resource: "INVITATION" /* INVITATION */
  },
  ["PURCHASE_COMPLETED" /* PURCHASE_COMPLETED */]: {
    category: "ADMIN_FINANCE_ALERTS" /* ADMIN_FINANCE_ALERTS */,
    scope: "ADMIN" /* ADMIN */,
    featureFlag: "notifications.purchase-completed",
    label: "New purchases",
    description: "A member has completed a purchase or paid an invoice.",
    availableChannels: ["EMAIL" /* EMAIL */, "SMS" /* SMS */],
    defaultEnabled: {
      ["EMAIL" /* EMAIL */]: true,
      ["SMS" /* SMS */]: false
    },
    resource: "PAYMENT" /* PAYMENT */
  },
  ["DONATION_RECEIVED" /* DONATION_RECEIVED */]: {
    category: "ADMIN_FINANCE_ALERTS" /* ADMIN_FINANCE_ALERTS */,
    scope: "ADMIN" /* ADMIN */,
    featureFlag: "notifications.donation-received",
    label: "New donations",
    description: "A donation has been received.",
    availableChannels: ["EMAIL" /* EMAIL */, "SMS" /* SMS */],
    defaultEnabled: {
      ["EMAIL" /* EMAIL */]: true,
      ["SMS" /* SMS */]: false
    },
    resource: "DONATION" /* DONATION */
  },
  ["INVOICE_OVERDUE" /* INVOICE_OVERDUE */]: {
    category: "ADMIN_FINANCE_ALERTS" /* ADMIN_FINANCE_ALERTS */,
    scope: "ADMIN" /* ADMIN */,
    featureFlag: "notifications.invoice-overdue",
    label: "Invoice overdue",
    description: "A member invoice has become overdue.",
    availableChannels: ["EMAIL" /* EMAIL */, "SMS" /* SMS */],
    defaultEnabled: {
      ["EMAIL" /* EMAIL */]: true,
      ["SMS" /* SMS */]: false
    },
    resource: "INVOICE" /* INVOICE */
  },
  ["INVOICE_PAYMENT_FAILED" /* INVOICE_PAYMENT_FAILED */]: {
    category: "ADMIN_FINANCE_ALERTS" /* ADMIN_FINANCE_ALERTS */,
    scope: "ADMIN" /* ADMIN */,
    featureFlag: "notifications.invoice-payment-failed",
    label: "Invoice payment failed",
    description: "A payment attempt on a member invoice has failed.",
    availableChannels: ["EMAIL" /* EMAIL */, "SMS" /* SMS */],
    defaultEnabled: {
      ["EMAIL" /* EMAIL */]: true,
      ["SMS" /* SMS */]: false
    },
    resource: "INVOICE" /* INVOICE */
  },
  ["EVENT_REGISTRATION_CREATED" /* EVENT_REGISTRATION_CREATED */]: {
    category: "ADMIN_EVENT_ALERTS" /* ADMIN_EVENT_ALERTS */,
    scope: "ADMIN" /* ADMIN */,
    featureFlag: "notifications.event-registration-created",
    label: "New event registrations",
    description: "A member has registered for an event.",
    availableChannels: ["EMAIL" /* EMAIL */, "SMS" /* SMS */],
    defaultEnabled: {
      ["EMAIL" /* EMAIL */]: false,
      ["SMS" /* SMS */]: false
    },
    resource: "EVENT" /* EVENT */
  },
  ["EVENT_WAITLIST_JOINED" /* EVENT_WAITLIST_JOINED */]: {
    category: "ADMIN_EVENT_ALERTS" /* ADMIN_EVENT_ALERTS */,
    scope: "ADMIN" /* ADMIN */,
    featureFlag: "notifications.event-waitlist-joined",
    label: "Event waitlist joined",
    description: "A member has joined an event waitlist.",
    availableChannels: ["EMAIL" /* EMAIL */, "SMS" /* SMS */],
    defaultEnabled: {
      ["EMAIL" /* EMAIL */]: false,
      ["SMS" /* SMS */]: false
    },
    resource: "EVENT" /* EVENT */
  },
  ["REFUND_REQUESTED" /* REFUND_REQUESTED */]: {
    category: "ADMIN_FINANCE_ALERTS" /* ADMIN_FINANCE_ALERTS */,
    scope: "ADMIN" /* ADMIN */,
    featureFlag: "notifications.refund-requested",
    label: "Refund requested",
    description: "A member has requested a refund.",
    availableChannels: ["EMAIL" /* EMAIL */, "SMS" /* SMS */],
    defaultEnabled: {
      ["EMAIL" /* EMAIL */]: true,
      ["SMS" /* SMS */]: false
    },
    resource: "PAYMENT" /* PAYMENT */
  },
  // Member-facing
  ["ANNOUNCEMENT_PUBLISHED" /* ANNOUNCEMENT_PUBLISHED */]: {
    category: "ANNOUNCEMENTS" /* ANNOUNCEMENTS */,
    scope: "MEMBER" /* MEMBER */,
    featureFlag: "notifications.announcement-published",
    label: "New announcements",
    description: "An organization announcement has been published.",
    availableChannels: [
      "EMAIL" /* EMAIL */,
      "SMS" /* SMS */,
      "PUSH" /* PUSH */
    ],
    defaultEnabled: {
      ["EMAIL" /* EMAIL */]: true,
      ["SMS" /* SMS */]: false,
      ["PUSH" /* PUSH */]: true
    }
  },
  ["DUES_INVOICE_ISSUED" /* DUES_INVOICE_ISSUED */]: {
    category: "MEMBERSHIP_DUES" /* MEMBERSHIP_DUES */,
    scope: "MEMBER" /* MEMBER */,
    featureFlag: "notifications.dues-invoice-issued",
    label: "New due invoices",
    description: "A new membership dues invoice has been issued to you.",
    availableChannels: [
      "EMAIL" /* EMAIL */,
      "SMS" /* SMS */,
      "PUSH" /* PUSH */
    ],
    defaultEnabled: {
      ["EMAIL" /* EMAIL */]: true,
      ["SMS" /* SMS */]: false,
      ["PUSH" /* PUSH */]: true
    }
  },
  ["DUES_REMINDER" /* DUES_REMINDER */]: {
    category: "MEMBERSHIP_DUES" /* MEMBERSHIP_DUES */,
    scope: "MEMBER" /* MEMBER */,
    featureFlag: "notifications.dues-reminder",
    label: "Upcoming dues reminder",
    description: "Your membership dues payment is coming due soon.",
    availableChannels: [
      "EMAIL" /* EMAIL */,
      "SMS" /* SMS */,
      "PUSH" /* PUSH */
    ],
    defaultEnabled: {
      ["EMAIL" /* EMAIL */]: true,
      ["SMS" /* SMS */]: false,
      ["PUSH" /* PUSH */]: true
    }
  },
  ["DUES_PAYMENT_CONFIRMATION" /* DUES_PAYMENT_CONFIRMATION */]: {
    category: "MEMBERSHIP_DUES" /* MEMBERSHIP_DUES */,
    scope: "MEMBER" /* MEMBER */,
    featureFlag: "notifications.dues-payment-confirmation",
    label: "Dues payment confirmation",
    description: "Confirmation that your membership dues payment was received.",
    availableChannels: [
      "EMAIL" /* EMAIL */,
      "SMS" /* SMS */,
      "PUSH" /* PUSH */
    ],
    defaultEnabled: {
      ["EMAIL" /* EMAIL */]: true,
      ["SMS" /* SMS */]: false,
      ["PUSH" /* PUSH */]: false
    },
    locked: {
      ["EMAIL" /* EMAIL */]: true
    }
  },
  ["DUES_PAYMENT_FAILED" /* DUES_PAYMENT_FAILED */]: {
    category: "MEMBERSHIP_DUES" /* MEMBERSHIP_DUES */,
    scope: "MEMBER" /* MEMBER */,
    featureFlag: "notifications.dues-payment-failed",
    label: "Dues overdue notice",
    description: "Your membership dues payment attempt failed or is overdue.",
    availableChannels: [
      "EMAIL" /* EMAIL */,
      "SMS" /* SMS */,
      "PUSH" /* PUSH */
    ],
    defaultEnabled: {
      ["EMAIL" /* EMAIL */]: true,
      ["SMS" /* SMS */]: false,
      ["PUSH" /* PUSH */]: true
    }
  },
  ["EVENT_INVITATION" /* EVENT_INVITATION */]: {
    category: "EVENTS" /* EVENTS */,
    scope: "MEMBER" /* MEMBER */,
    featureFlag: "notifications.event-invitation",
    label: "Event invitations",
    description: "You've been invited to an event.",
    availableChannels: [
      "EMAIL" /* EMAIL */,
      "SMS" /* SMS */,
      "PUSH" /* PUSH */
    ],
    defaultEnabled: {
      ["EMAIL" /* EMAIL */]: true,
      ["SMS" /* SMS */]: false,
      ["PUSH" /* PUSH */]: true
    }
  },
  ["EVENT_REMINDER" /* EVENT_REMINDER */]: {
    category: "EVENTS" /* EVENTS */,
    scope: "MEMBER" /* MEMBER */,
    featureFlag: "notifications.event-reminder",
    label: "Event reminders",
    description: "An event you registered for is coming up.",
    availableChannels: [
      "EMAIL" /* EMAIL */,
      "SMS" /* SMS */,
      "PUSH" /* PUSH */
    ],
    defaultEnabled: {
      ["EMAIL" /* EMAIL */]: true,
      ["SMS" /* SMS */]: false,
      ["PUSH" /* PUSH */]: true
    }
  },
  ["EVENT_CHANGED" /* EVENT_CHANGED */]: {
    category: "EVENTS" /* EVENTS */,
    scope: "MEMBER" /* MEMBER */,
    featureFlag: "notifications.event-changed",
    label: "Event changes",
    description: "An event you registered for was changed or cancelled.",
    availableChannels: [
      "EMAIL" /* EMAIL */,
      "SMS" /* SMS */,
      "PUSH" /* PUSH */
    ],
    defaultEnabled: {
      ["EMAIL" /* EMAIL */]: true,
      ["SMS" /* SMS */]: false,
      ["PUSH" /* PUSH */]: true
    }
  },
  ["GROUP_ADDED" /* GROUP_ADDED */]: {
    category: "GROUPS" /* GROUPS */,
    scope: "MEMBER" /* MEMBER */,
    featureFlag: "notifications.group-added",
    label: "Added to a group",
    description: "You've been added to a group or committee.",
    availableChannels: [
      "EMAIL" /* EMAIL */,
      "SMS" /* SMS */,
      "PUSH" /* PUSH */
    ],
    defaultEnabled: {
      ["EMAIL" /* EMAIL */]: true,
      ["SMS" /* SMS */]: false,
      ["PUSH" /* PUSH */]: true
    }
  },
  ["GROUP_MESSAGE" /* GROUP_MESSAGE */]: {
    category: "GROUPS" /* GROUPS */,
    scope: "MEMBER" /* MEMBER */,
    featureFlag: "notifications.group-message",
    label: "Group messages",
    description: "New activity in a group or committee you belong to.",
    availableChannels: [
      "EMAIL" /* EMAIL */,
      "SMS" /* SMS */,
      "PUSH" /* PUSH */
    ],
    defaultEnabled: {
      ["EMAIL" /* EMAIL */]: false,
      ["SMS" /* SMS */]: false,
      ["PUSH" /* PUSH */]: true
    }
  },
  ["DONATION_RECEIPT" /* DONATION_RECEIPT */]: {
    category: "DONATIONS" /* DONATIONS */,
    scope: "MEMBER" /* MEMBER */,
    featureFlag: "notifications.donation-receipt",
    label: "Donation receipts",
    description: "Receipt confirmation for a donation you made.",
    availableChannels: [
      "EMAIL" /* EMAIL */,
      "SMS" /* SMS */,
      "PUSH" /* PUSH */
    ],
    defaultEnabled: {
      ["EMAIL" /* EMAIL */]: true,
      ["SMS" /* SMS */]: false,
      ["PUSH" /* PUSH */]: false
    },
    locked: {
      ["EMAIL" /* EMAIL */]: true
    }
  }
};
var NotificationPreferenceDtoSchema = z.object({
  eventType: NotificationEventTypeSchema,
  channel: z.nativeEnum(CommunicationChannel),
  enabled: z.boolean(),
  locked: z.boolean()
});
var NotificationPreferenceArrayResponseDtoSchema = createResponseSchema(NotificationPreferenceDtoSchema.array());
var UpdateNotificationPreferenceDtoSchema = z.object({
  eventType: NotificationEventTypeSchema,
  channel: z.nativeEnum(CommunicationChannel),
  enabled: z.boolean()
});
var UpdateNotificationPreferencesDtoSchema = z.object({
  preferences: UpdateNotificationPreferenceDtoSchema.array()
});
var DevicePlatform = /* @__PURE__ */ ((DevicePlatform2) => {
  DevicePlatform2["IOS"] = "IOS";
  DevicePlatform2["ANDROID"] = "ANDROID";
  return DevicePlatform2;
})(DevicePlatform || {});
var DevicePlatformSchema = z.nativeEnum(DevicePlatform);
var PushDeviceTokenDtoSchema = z.object({
  uuid: z.string().uuid(),
  platform: DevicePlatformSchema,
  deviceName: z.string().nullable(),
  createdAt: dateToISOString,
  updatedAt: dateToISOString
});
var PushDeviceTokenResponseDtoSchema = createResponseSchema(
  PushDeviceTokenDtoSchema
);
var PushDeviceTokenArrayResponseDtoSchema = createResponseSchema(
  PushDeviceTokenDtoSchema.array()
);
var RegisterPushDeviceTokenDtoSchema = z.object({
  token: z.string().min(1).max(255),
  platform: DevicePlatformSchema,
  deviceName: z.string().max(255).optional()
});
var UnregisterPushDeviceTokenDtoSchema = z.object({
  token: z.string().min(1).max(255)
});
var ContactSupportRequestDtoSchema = z.object({
  subject: z.string().min(1, "Subject is required").max(200, "Subject must be 200 characters or less"),
  message: z.string().min(1, "Message is required").max(5e3, "Message must be 5000 characters or less"),
  pageUrl: z.url("Page URL must be a valid URL").max(2048, "Page URL must be 2048 characters or less").optional()
});
var ContactSupportResponseDtoSchema = createResponseSchema(
  z.object({
    success: z.boolean()
  })
);
var ContactSupportRequestEmailDtoSchema = z.object({
  userName: z.string(),
  userEmail: z.email(),
  organizationName: z.string().nullable(),
  subject: z.string(),
  message: z.string(),
  pageUrl: z.url().nullable(),
  submittedAt: z.iso.datetime()
});
var ImportJobStatus = /* @__PURE__ */ ((ImportJobStatus2) => {
  ImportJobStatus2["PENDING"] = "PENDING";
  ImportJobStatus2["PROCESSING"] = "PROCESSING";
  ImportJobStatus2["COMPLETED"] = "COMPLETED";
  ImportJobStatus2["COMPLETED_WITH_ERRORS"] = "COMPLETED_WITH_ERRORS";
  ImportJobStatus2["FAILED"] = "FAILED";
  return ImportJobStatus2;
})(ImportJobStatus || {});
var ImportJobStatusSchema = z.enum(ImportJobStatus);
var ImportColumnMappingSchema = z.record(z.string(), z.string());
var jsonStringOrValue = (inner) => z.preprocess((val) => {
  if (typeof val !== "string") {
    return val;
  }
  try {
    return JSON.parse(val);
  } catch {
    return val;
  }
}, inner);
var CreateImportJobDtoSchema = z.object({
  type: ImportTypeSchema,
  columnMapping: jsonStringOrValue(ImportColumnMappingSchema),
  options: jsonStringOrValue(z.record(z.string(), z.unknown())).optional().default({})
});
var ImportJobDtoSchema = z.object({
  uuid: z.uuid(),
  type: ImportTypeSchema,
  status: ImportJobStatusSchema,
  originalFileName: z.string(),
  totalRows: z.number().int().nullable(),
  processedRows: z.number().int(),
  successCount: z.number().int(),
  skippedCount: z.number().int(),
  errorCount: z.number().int(),
  hasErrorReport: z.boolean(),
  startedAt: nullableDateToISOString,
  completedAt: nullableDateToISOString,
  createdAt: dateToISOString,
  updatedAt: dateToISOString
});
var ImportJobResponseDtoSchema = createResponseSchema(ImportJobDtoSchema);
var HouseholdMemberDtoSchema = z.object({
  uuid: z.uuid(),
  householdId: bigintToString,
  membershipId: bigintToString,
  membership: OrganizationMembershipDtoSchema,
  relationship: z.string().nullable(),
  isManaged: z.boolean(),
  linkedAt: nullableDateToISOString,
  createdAt: dateToISOString,
  updatedAt: dateToISOString
});

// src/households/household.dto.ts
var HouseholdDtoSchema = z.object({
  uuid: z.uuid(),
  organizationId: bigintToString,
  payerMembershipId: bigintToString,
  name: z.string().nullable(),
  createdAt: dateToISOString,
  updatedAt: dateToISOString
});
var HouseholdWithMembersDtoSchema = HouseholdDtoSchema.extend({
  payerMembership: OrganizationMembershipDtoSchema,
  members: HouseholdMemberDtoSchema.array()
});
var HouseholdResponseDtoSchema = createResponseSchema(
  HouseholdWithMembersDtoSchema
);
var HouseholdArrayResponseDtoSchema = createResponseSchema(
  HouseholdDtoSchema.array()
);
var CreateHouseholdDtoSchema = z.object({
  name: z.string().max(255).optional()
});
var UpdateHouseholdDtoSchema = z.object({
  name: z.string().max(255).nullable().optional(),
  payerMembershipUuid: z.uuid().optional()
});
var AddHouseholdMemberDtoSchema = z.discriminatedUnion("mode", [
  z.object({
    mode: z.literal("MANAGED"),
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    email: z.email().optional(),
    dateOfBirth: z.string().datetime().or(z.date()).optional(),
    relationship: z.string().optional(),
    membershipTierId: z.string().optional()
  }),
  z.object({
    mode: z.literal("LINKED"),
    membershipUuid: z.uuid(),
    relationship: z.string().optional()
  })
]);

// src/households/query-households.dto.ts
var ALLOWED_HOUSEHOLD_FIELDS = [
  "uuid",
  "name",
  "createdAt",
  "updatedAt"
];
var QueryHouseholdsDtoSchema = createValidatedQuerySchema([
  ...ALLOWED_HOUSEHOLD_FIELDS
]);

export { ADMIN_ONLY_RESOURCES, ALLOWED_ANNOUNCEMENT_FIELDS, ALLOWED_COMMUNICATION_TEMPLATE_FIELDS, ALLOWED_DONATION_FIELDS, ALLOWED_DONATION_PAGE_FIELDS, ALLOWED_DONOR_FIELDS, ALLOWED_EVENT_ATTENDANCE_FIELDS, ALLOWED_EVENT_ATTENDANCE_OVERVIEW_FIELDS, ALLOWED_EVENT_FIELDS, ALLOWED_EVENT_REGISTRATION_FIELDS, ALLOWED_GROUP_FIELDS, ALLOWED_GROUP_MEMBER_FIELDS, ALLOWED_HOUSEHOLD_FIELDS, ALLOWED_MEMBER_EVENT_REGISTRATION_FIELDS, ALLOWED_ORGANIZATION_ROLE_FIELDS, ASSIGNABLE_RESOURCES, ATTENDANCE_STATUS_DISPLAY, AcceptInvitationDtoSchema, AccountSecurityEventTypeSchema, AccountSecurityLogDtoSchema, Action, AddHouseholdMemberDtoSchema, AdministratorDtoSchema, AdministratorProfileDtoSchema, AggregatedPermissionDtoSchema, AnnouncementArrayResponseDtoSchema, AnnouncementDtoSchema, AnnouncementPriority, AnnouncementPublishedEmailDtoSchema, AnnouncementResponseDtoSchema, AnnouncementStatus, AnnouncementWithRelationsDtoSchema, AnnouncementWithRelationsResponseDtoSchema, ApiErrorSchema, ApproveEventRefundRequestDtoSchema, AssignTierDtoSchema, AttendanceStatusSchema, AudienceTargetDtoSchema, AudienceTargetResponseDtoSchema, AudienceTargetType, AudienceTargetTypeSchema, AudienceTargetWithRelationsDtoSchema, AuditLogDtoSchema, AuditLogUserDtoSchema, AuthCheckoutDtoSchema, AuthOrderCheckoutDtoSchema, BalanceTransactionDtoSchema, BaseMembershipDtoSchema, BaseQuerySchema, CHECK_IN_METHOD_DISPLAY, CHECK_IN_PAYMENT_METHODS, CancelEventDtoSchema, CartCheckoutItemDtoSchema, ChangeEmailDtoSchema, ChangeEmailResponseDtoSchema, ChangePasswordDtoSchema, ChangePasswordResponseDtoSchema, CheckInEventQrDtoSchema, CheckInMethodSchema, CheckInRegistrationQrDtoSchema, CheckInTicketQrDtoSchema, CheckoutResponseDtoSchema, CheckoutResultDtoSchema, CommunicationChannel, CommunicationTemplateArrayResponseDtoSchema, CommunicationTemplateDtoSchema, CommunicationTemplateResponseDtoSchema, CommunicationTemplateStatus, CompletePresignedUploadDtoSchema, ConfirmEmailChangeDtoSchema, ConfirmEmailChangeResponseDtoSchema, ConfirmOrganizationApprovalDtoSchema, ConfirmReactivationDtoSchema, ConfirmReactivationResponseDtoSchema, ContactSupportRequestDtoSchema, ContactSupportRequestEmailDtoSchema, ContactSupportResponseDtoSchema, CreateAdministratorDtoSchema, CreateAnnouncementDtoSchema, CreateAudienceTargetDtoSchema, CreateCommunicationTemplateDtoSchema, CreateDonationDtoSchema, CreateDonationPageDtoSchema, CreateEventDtoSchema, CreateEventRefundRequestDtoSchema, CreateEventRegistrationDtoSchema, CreateEventTicketCheckoutDtoSchema, CreateEventTicketTierDtoSchema, CreateFeatureFlagDtoSchema, CreateFeatureFlagOverrideDtoSchema, CreateGroupDtoSchema, CreateGroupMemberDtoSchema, CreateGroupRoleAssignmentDtoSchema, CreateGroupRoleDtoSchema, CreateHouseholdDtoSchema, CreateImportJobDtoSchema, CreateInvitationDtoSchema, CreateInvoiceDtoSchema, CreateInvoiceLineItemDtoSchema, CreateLineItemDtoSchema, CreateMemberConflictCode, CreateMemberConflictCodeSchema, CreateMemberConflictDtoSchema, CreateMemberDtoSchema, CreateMembershipTierDtoSchema, CreateOrganizationDtoSchema, CreateOrganizationRoleDtoSchema, CreateOrganizationTaxRateDtoSchema, CreatePaymentIntentDtoSchema, CreatePublicEventTicketCheckoutDtoSchema, CreateRoleAssignmentDtoSchema, CreateScheduledActionDtoSchema, CreateUserDtoSchema, CurrentOrganizationDtoSchema, DAY_OF_WEEK_DISPLAY, DashboardCalendarItemDtoSchema, DashboardCalendarQueryDtoSchema, DashboardCalendarResponseDtoSchema, DashboardOverviewDtoSchema, DashboardOverviewResponseDtoSchema, DashboardRecentActivityItemDtoSchema, DashboardRecentActivityResponseDtoSchema, DashboardTileDtoSchema, DayOfWeek, DeadLetterJobArrayResponseDtoSchema, DeadLetterJobDtoSchema, DeadLetterJobResponseDtoSchema, DeadLetterJobStatus, DeleteAccountResponseDtoSchema, DeleteEventDtoSchema, DeleteMembershipTierDtoSchema, DenyEventRefundRequestDtoSchema, DevicePlatform, DevicePlatformSchema, DocumentStatusDtoSchema, DonationArrayResponseDtoSchema, DonationCheckoutResponseDtoSchema, DonationDtoSchema, DonationPageArrayResponseDtoSchema, DonationPageDtoSchema, DonationPageResponseDtoSchema, DonationPageSummaryDtoSchema, DonationPageWithRelationsDtoSchema, DonationReceiptDtoSchema, DonationReceiptResponseDtoSchema, DonationRefundProcessedEmailDtoSchema, DonationResponseDtoSchema, DonationStatementArrayResponseDtoSchema, DonationStatementDeliveredEmailDtoSchema, DonationStatementDtoSchema, DonationStatementResponseDtoSchema, DonationThankYouEmailDtoSchema, DonationWithRelationsDtoSchema, DonorProfileArrayResponseDtoSchema, DonorProfileDtoSchema, DonorProfileResponseDtoSchema, DuesAutoPayEnrolledEmailDtoSchema, DuesAutoPayEnrollmentDtoSchema, DuesAutoPayEnrollmentResponseDtoSchema, DuesAutoPayStatusSchema, DuesDashboardResponseDtoSchema, DuesDashboardStatsDtoSchema, DuesMembershipSuspendedEmailDtoSchema, DuesSettingsDtoSchema, DuesSettingsResponseDtoSchema, DuesStatusDtoSchema, DuesStatusResponseDtoSchema, DuesUpcomingRenewalEmailDtoSchema, EVENT_EDIT_SCOPE_DISPLAY, EVENT_LOCATION_TYPE_DISPLAY, EVENT_REFUND_STATUS_DISPLAY, EVENT_REGISTRATION_STATUS_DISPLAY, EVENT_SERIES_STATUS_DISPLAY, EVENT_STATUS_DISPLAY, EVENT_TICKET_STATUS_DISPLAY, EVENT_VISIBILITY_DISPLAY, EligibleOwnerArrayResponseDtoSchema, EligibleOwnerDtoSchema, EmailLineItemDtoSchema, EnableSMSMFADtoSchema, EnableSMSMFAResponseDtoSchema, EnrollAutoPayDtoSchema, EventArrayResponseDtoSchema, EventAttendanceArrayResponseDtoSchema, EventAttendanceDtoSchema, EventAttendanceOverviewArrayResponseDtoSchema, EventAttendanceOverviewDtoSchema, EventAttendanceOverviewResponseDtoSchema, EventAttendanceResponseDtoSchema, EventAttendanceStatsDtoSchema, EventAttendanceStatsResponseDtoSchema, EventAttendanceWithMemberDtoSchema, EventCancelledEmailDtoSchema, EventChangeItemSchema, EventDeletedEmailDtoSchema, EventDtoSchema, EventEditScope, EventEditScopeSchema, EventLocationType, EventLocationTypeSchema, EventPublishedEmailDtoSchema, EventRefundDeniedEmailDtoSchema, EventRefundProcessedEmailDtoSchema, EventRefundRequestArrayResponseDtoSchema, EventRefundRequestDtoSchema, EventRefundRequestReceivedEmailDtoSchema, EventRefundRequestResponseDtoSchema, EventRefundRequestWithMemberDtoSchema, EventRefundStatus, EventRefundStatusSchema, EventRegistrationArrayResponseDtoSchema, EventRegistrationCancelledEmailDtoSchema, EventRegistrationConfirmationEmailDtoSchema, EventRegistrationDtoSchema, EventRegistrationResponseDtoSchema, EventRegistrationStatusSchema, EventRegistrationWithMemberDtoSchema, EventReminderEmailDtoSchema, EventResponseDtoSchema, EventSeriesArrayResponseDtoSchema, EventSeriesDtoSchema, EventSeriesResponseDtoSchema, EventSeriesStatus, EventSeriesStatusSchema, EventSeriesWithRelationsDtoSchema, EventStatus, EventStatusSchema, EventTicketArrayResponseDtoSchema, EventTicketDtoSchema, EventTicketPurchaseConfirmationEmailDtoSchema, EventTicketResponseDtoSchema, EventTicketStatus, EventTicketStatusSchema, EventTicketTierArrayResponseDtoSchema, EventTicketTierDtoSchema, EventTicketTierResponseDtoSchema, EventTicketWithTierDtoSchema, EventUpdatedEmailDtoSchema, EventVisibility, EventVisibilitySchema, EventWithRelationsDtoSchema, FeatureFlagDtoSchema, FeatureFlagOverrideDtoSchema, FileStatusEnum, FileTypeEnum, FileUploadDtoSchema, FilterConditionSchema, FilterOperator, FinanceReportDtoSchema, FinanceReportPointDtoSchema, FinanceReportResponseDtoSchema, FullConfigurationDtoSchema, GenerateDonationStatementsDtoSchema, GenerateDuesInvoiceDtoSchema, GenerateRecoveryCodesResponseDtoSchema, GetPayoutsQuerySchema, GetPublicDonationPageParamsSchema, GetPublicDonationPageQuerySchema, GetPublicEventParamsSchema, GetPublicOrganizationEventsParamsSchema, GetPublicOrganizationProfileParamsSchema, GetPublicOrganizationProfileQuerySchema, GrantUserPermissionDtoSchema, GroupArrayResponseDtoSchema, GroupDtoSchema, GroupDuesDtoSchema, GroupDuesInvoiceIssuedEmailDtoSchema, GroupDuesResponseDtoSchema, GroupMemberArrayResponseDtoSchema, GroupMemberDtoSchema, GroupMemberResponseDtoSchema, GroupMemberWithRelationsDtoSchema, GroupResponseDtoSchema, GroupRoleArrayResponseDtoSchema, GroupRoleAssignmentArrayResponseDtoSchema, GroupRoleAssignmentDtoSchema, GroupRoleAssignmentResponseDtoSchema, GroupRoleAssignmentWithRelationsArrayResponseDtoSchema, GroupRoleAssignmentWithRelationsDtoSchema, GroupRoleAssignmentWithRelationsResponseDtoSchema, GroupRoleDtoSchema, GroupRoleResponseDtoSchema, GroupTypeEnum, GroupTypeSchema, GroupVisibilityEnum, GroupVisibilitySchema, GroupWithRelationsDtoSchema, GuestCheckInDtoSchema, GuestCheckInEventQrDtoSchema, GuestCheckoutDtoSchema, GuestOrderCheckoutDtoSchema, HouseholdArrayResponseDtoSchema, HouseholdDtoSchema, HouseholdMemberDtoSchema, HouseholdResponseDtoSchema, HouseholdWithMembersDtoSchema, IMPORT_MAX_FILE_SIZE_BYTES, IMPORT_TYPE_ROW_LIMITS, ImportColumnMappingSchema, ImportJobCompletedEmailDtoSchema, ImportJobDtoSchema, ImportJobResponseDtoSchema, ImportJobStatus, ImportJobStatusSchema, ImportType, ImportTypeSchema, InitiateLinkResponseDtoSchema, InitiateTransferOwnershipDtoSchema, InvitationAcceptedAdminAlertEmailDtoSchema, InvitationDtoSchema, InvitationEmailDtoSchema, InvitationReminderEmailDtoSchema, InvitationStatus, InvitationStatusSchema, InvitationWithRelationsDtoSchema, InvoiceDtoSchema, InvoiceExpiringEmailDtoSchema, InvoiceIssuedEmailDtoSchema, InvoiceLineItemDtoSchema, InvoiceLineItemSourceTypeSchema, InvoiceOverdueEmailDtoSchema, InvoicePaidEmailDtoSchema, InvoicePdfReadyEmailDtoSchema, InvoiceSourceType, InvoiceStatus, InvoiceVoidedEmailDtoSchema, InvoiceWithRelationsDtoSchema, IssueInvoiceDtoSchema, IssuerType, JoinWaitlistDtoSchema, LinkedProvidersResponseDtoSchema, ListPasskeysResponseDtoSchema, ListResourceFilesQueryDtoSchema, LoginAdministratorDtoSchema, MEMBERSHIP_SOURCE_DISPLAY, MFADisableResponseDtoSchema, MFASetupResponseDtoSchema, MFAStatusResponseDtoSchema, MFAVerifyDtoSchema, MFAVerifyResponseDtoSchema, ManagedMemberWelcomeEmailDtoSchema, ManualCheckInDtoSchema, MemberDuesStatus, MemberDuesSummaryDtoSchema, MemberDuesSummaryResponseDtoSchema, MemberEventRegistrationWithAttendanceArrayResponseDtoSchema, MemberEventRegistrationWithAttendanceDtoSchema, MemberEventRegistrationWithAttendanceResponseDtoSchema, MemberGroupDuesDtoSchema, MemberRolesResponseDtoSchema, MemberRolesResponseResponseDtoSchema, MembershipDtoSchema, MembershipSource, MembershipSourceSchema, MembershipStatus, MembershipStatusSchema, MembershipTierDtoSchema, MfaPreferredMethodSchema, MimberAdminConfigDtoSchema, MimberConfigDtoSchema, MimberOrganizationConfigDtoSchema, NOTIFICATION_EVENT_CONFIG, NotificationCategory, NotificationCategorySchema, NotificationEventType, NotificationEventTypeSchema, NotificationPreferenceArrayResponseDtoSchema, NotificationPreferenceDtoSchema, NotificationScope, NotificationScopeSchema, OrderCheckoutItemDtoSchema, OrderCheckoutResponseDtoSchema, OrderCheckoutResultDtoSchema, OrderDtoSchema, OrderLineItemDtoSchema, OrderReceiptEmailDtoSchema, OrderSourceDonationDtoSchema, OrderSourceEventDtoSchema, OrderSourceType, OrderStatus, OrderWithRelationsDtoSchema, OrganizationApprovalActionType, OrganizationApprovalRequestDtoSchema, OrganizationApprovalRequestResponseDtoSchema, OrganizationApprovalRequestSummaryDtoSchema, OrganizationApprovalRequestSummaryResponseDtoSchema, OrganizationApprovalStatus, OrganizationCheckoutDtoSchema, OrganizationDeletionApprovalEmailDtoSchema, OrganizationDeletionCanceledEmailDtoSchema, OrganizationDeletionCompletedEmailDtoSchema, OrganizationDeletionScheduledEmailDtoSchema, OrganizationDtoSchema, OrganizationMembershipDtoSchema, OrganizationOwnershipDtoSchema, OrganizationOwnershipResponseDtoSchema, OrganizationProfileSettingsDtoSchema, OrganizationRoleArrayResponseDtoSchema, OrganizationRoleDeletedDtoSchema, OrganizationRoleDeletedResponseDtoSchema, OrganizationRoleDtoSchema, OrganizationRoleResponseDtoSchema, OrganizationRoleWithRelationsArrayResponseDtoSchema, OrganizationRoleWithRelationsDtoSchema, OrganizationRoleWithRelationsResponseDtoSchema, OrganizationTaxRateArrayResponseDtoSchema, OrganizationTaxRateAssignmentArrayResponseDtoSchema, OrganizationTaxRateAssignmentDtoSchema, OrganizationTaxRateDtoSchema, OrganizationTaxRateResponseDtoSchema, OverrideType, OwnershipTransferApprovalEmailDtoSchema, OwnershipTransferCompletedEmailDtoSchema, PAYMENT_METHOD_DISPLAY, PaginationMetaSchema, PaginationSchema, PaymentDtoSchema, PaymentFailedEmailDtoSchema, PaymentIntentResponseDtoSchema, PaymentIntentResultDtoSchema, PaymentMethod, PaymentProvider, PaymentReceivedEmailDtoSchema, PaymentStatus, PaymentTerms, PayoutDtoSchema, PayoutInterval, PayoutMethod, PayoutScheduleDtoSchema, PayoutStatus, PermissionDtoSchema, PresignedUploadRequestDtoSchema, PresignedUrlDtoSchema, PreviewTokenResponseSchema, ProfileStatus, PublicAnnouncementArrayResponseDtoSchema, PublicAnnouncementDtoSchema, PublicAnnouncementResponseDtoSchema, PublicAnnouncementSourceDtoSchema, PublicAnnouncementTransformDtoSchema, PublicDonationPageDtoSchema, PublicEventDtoSchema, PublicEventListItemDtoSchema, PublicEventListResponseDtoSchema, PublicEventResponseDtoSchema, PublicFeatureFlagDtoSchema, PublicInvitationDtoSchema, PublicOrganizationDtoSchema, PublicStorageItemDtoSchema, PublishAnnouncementDtoSchema, PublishEventDtoSchema, PushDeviceTokenArrayResponseDtoSchema, PushDeviceTokenDtoSchema, PushDeviceTokenResponseDtoSchema, QueryAnnouncementSchema, QueryCommunicationTemplateSchema, QueryDonationPageSchema, QueryDonationSchema, QueryDonorSchema, QueryEventAttendanceOverviewSchema, QueryEventAttendanceSchema, QueryEventRegistrationSchema, QueryEventSchema, QueryFinanceReportDtoSchema, QueryGroupMemberSchema, QueryGroupSchema, QueryHouseholdsDtoSchema, QueryMemberEventRegistrationSchema, QueryMembersDtoSchema, QueryMembershipsDtoSchema, QueryOrganizationRoleSchema, QueryParamsDtoSchema, QueryParamsFilterSchema, QuerySchema, RECURRENCE_INTERVAL_DISPLAY, RESOURCE_LABELS, RecordLoginEventResponseDtoSchema, RecordPaymentDtoSchema, RecurrenceConfigSchema, RecurrenceInterval, RecurringDonationArrayResponseDtoSchema, RecurringDonationDtoSchema, RecurringDonationResponseDtoSchema, RecurringDonationStatus, RecurringDonationStatusSchema, RefundProcessedEmailDtoSchema, RegenerateEventQrDtoSchema, RegenerateEventQrResponseDtoSchema, RegisterAdministratorDtoSchema, RegisterForPublicEventDtoSchema, RegisterPushDeviceTokenDtoSchema, RegisterUserDtoSchema, RegistrationQrTokenDtoSchema, RegistrationQrTokenResponseDtoSchema, RemainingRecoveryCodesResponseDtoSchema, RemoveAttendanceDtoSchema, RemoveAttendanceResponseDtoSchema, ReorderEventTicketTiersDtoSchema, RequestReactivationCodeResponseDtoSchema, Resource, ResourceAttachmentDtoSchema, ResourceEnum, ResourceReferenceDtoSchema, ResponseMetaSchema, RevokeAllSessionsResponseDtoSchema, RoleAssignmentArrayResponseDtoSchema, RoleAssignmentDtoSchema, RoleAssignmentResponseDtoSchema, RoleAssignmentWithRelationsArrayResponseDtoSchema, RoleAssignmentWithRelationsDtoSchema, RoleAssignmentWithRelationsResponseDtoSchema, RolePermissionDtoSchema, RolePermissionInputSchema, SEARCH_EVENT_ATTENDANCE_FIELDS, SEARCH_EVENT_ATTENDANCE_OVERVIEW_FIELDS, SEARCH_EVENT_FIELDS, SEARCH_EVENT_REGISTRATION_FIELDS, SEARCH_MEMBER_EVENT_REGISTRATION_FIELDS, SEND_RESOURCES, STANDARD_ACTIONS, SYSTEM_RESOURCES, SavedPaymentMethodDtoSchema, SavedPaymentMethodStatus, SavedPaymentMethodType, ScheduleAnnouncementDtoSchema, ScheduledActionDtoSchema, ScheduledActionListResponseDtoSchema, ScheduledActionResponseDtoSchema, ScheduledActionStatus, ScheduledActionType, ScheduledEntityType, SecurityAccountDeactivatedEmailDtoSchema, SecurityDeletionCancelledEmailDtoSchema, SecurityDeletionScheduledEmailDtoSchema, SecurityEmailChangedEmailDtoSchema, SecurityMfaDisabledEmailDtoSchema, SecurityMfaEnabledEmailDtoSchema, SecurityPasswordChangedEmailDtoSchema, SecuritySocialProviderChangedEmailDtoSchema, SetPasswordDtoSchema, SetPasswordResponseDtoSchema, SetPreferredMfaMethodDtoSchema, SetPreferredMfaMethodResponseDtoSchema, SetupIntentRequestSchema, SetupIntentResponseSchema, SocialProviderDtoSchema, SocialProviderSchema, SortConditionSchema, SortDirection, StartStripeConnectSchema, StepUpInitiateResponseDtoSchema, StepUpMethodSchema, StepUpStatusResponseDtoSchema, StepUpVerifyDtoSchema, StepUpVerifyResponseDtoSchema, StorageClassEnum, StorageItemDtoSchema, StripeAccountRequirementsSchema, StripeAccountStatus, StripeAccountStatusSchema, StripeOnboardingUrlSchema, StripeUrlSchema, SubmitInviteCodeDtoSchema, SuspendMemberDtoSchema, TEMPLATE_VARIABLES, TEMPLATE_VARIABLE_KEYS, TURNSTILE_HEADER, TURNSTILE_MAX_TOKEN_LENGTH, TURNSTILE_VALIDATION_KEY, TaxableSourceType, TicketLineItemSchema, TierChangePolicy, TierChangePreviewDtoSchema, TierChangePreviewQueryDtoSchema, TierChangePreviewResponseDtoSchema, TransactionArrayResponseDtoSchema, TransactionDetailDtoSchema, TransactionDetailInvoiceSchema, TransactionDetailOrderSchema, TransactionDetailResponseDtoSchema, TransactionDtoSchema, TransactionInvoiceSchema, TransactionMembershipSchema, TransactionOrderSchema, TransactionStripeDetailSchema, TurnstileAction, UnlinkSocialProviderDtoSchema, UnregisterPushDeviceTokenDtoSchema, UpdateAdministratorDtoSchema, UpdateAnnouncementDtoSchema, UpdateAudienceTargetDtoSchema, UpdateAutoPayPaymentMethodDtoSchema, UpdateCommunicationTemplateDtoSchema, UpdateConfigurationDtoSchema, UpdateDonationPageDtoSchema, UpdateDuesSettingsDtoSchema, UpdateEventDtoSchema, UpdateEventSeriesDtoSchema, UpdateEventTicketDtoSchema, UpdateEventTicketTierDtoSchema, UpdateFeatureFlagDtoSchema, UpdateFeatureFlagOverrideDtoSchema, UpdateGroupDtoSchema, UpdateGroupDuesDtoSchema, UpdateGroupMemberDtoSchema, UpdateGroupRoleDtoSchema, UpdateHouseholdDtoSchema, UpdateInvoiceDtoSchema, UpdateLineItemDtoSchema, UpdateMemberDtoSchema, UpdateMembershipTierDtoSchema, UpdateNotificationPreferenceDtoSchema, UpdateNotificationPreferencesDtoSchema, UpdateOrganizationDtoSchema, UpdateOrganizationProfileDtoSchema, UpdateOrganizationRoleDtoSchema, UpdateOrganizationTaxRateAssignmentsDtoSchema, UpdateOrganizationTaxRateDtoSchema, UpdatePhoneNumberDtoSchema, UpdatePhoneNumberResponseDtoSchema, UpdateProfileDtoSchema, UpdateRecurringDonationDtoSchema, UpdateUserDtoSchema, UploadAvatarDtoSchema, UpsertScheduledActionDtoSchema, UserDtoSchema, UserObjectDtoSchema, UserPermissionArrayResponseDtoSchema, UserPermissionDtoSchema, UserPermissionResponseDtoSchema, UserProfileDtoSchema, VerifyPhoneNumberDtoSchema, VerifyPhoneNumberResponseDtoSchema, VerifyRecoveryCodeDtoSchema, VerifyRecoveryCodeResponseDtoSchema, VoidInvoiceDtoSchema, WEEK_INDEX_DISPLAY, WaitlistAcceptanceEmailDtoSchema, WaitlistDtoSchema, WaitlistNewEntryEmailDtoSchema, WaitlistStatusSchema, WebAuthnCredentialDtoSchema, WeekIndex, bigintToString, buildLookahead, computeNextScheduledDate, createArrayResponseSchema, createResponseSchema, createValidatedQuerySchema, dateToISOString, findNthDayOfWeekInMonth, generateOccurrenceDates, isStripeUrl, nullableBigintToString, nullableDateToISOString, nullableNumberCoerce, numberCoerce, optionalBigintToString, optionalDateToISOString, optionalNumberCoerce, renderTemplateVariables };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map